/**
 * DigitalPrescriptionForm — Paper-Pad Style Doctor Workspace
 *
 * Designed to feel like writing on a real prescription pad:
 *   ┌───────────────────────────────────────────────────┐
 *   │  Vitals strip (inline)  │  BP: __ HR: __ T: __   │
 *   │  📦 My Rx Templates                              │
 *   ├───────────────────────────────────────────────────┤
 *   │  Symptoms  [chip] [chip] [+ add]                  │
 *   │                                                   │
 *   │  ℞  Type prescription... (Enter to add)           │
 *   │                                                   │
 *   │  1. Paracetamol 650mg Tab 1-0-1 AF 5d  [✎] [✕]  │
 *   │  2. Amoxicillin 500mg Cap 1-1-1 AF 7d  [✎] [✕]  │
 *   │                                                   │
 *   │  🧪 Tests: [CBC] [Blood Sugar] [✕]               │
 *   │  📝 Notes: _______________________________       │
 *   │  🔄 Follow-up: [3D] [1W] [2W] [1M]              │
 *   │                                                   │
 *   │  ┌─────────────────────────────────────────────┐  │
 *   │  │   💾 Save Prescription (3 meds + 2 tests)  │  │
 *   │  └─────────────────────────────────────────────┘  │
 *   │  📤 Upload file  │  📄 Attach report              │
 *   └───────────────────────────────────────────────────┘
 */

import React, { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { useScope } from '../../context/PermissionContext';
import {
  addDigitalPrescription,
  addVitals,
  addSymptoms,
  addNote,
  parsePrescription,
  fetchPrescriptionReference,
  fetchPrescriptionSuggestions,
  forgetPrescriptionMemory,
  deleteMetadata,
  fetchVisitMetadata,
  uploadPrescriptionImages,
  getFileDownloadUrl,
} from '../../services/api/visitMetadataApi';
import { getPrescriptionConfig } from '../../services/api/prescriptionBrandingApi';
import eventBus from '../../services/realtime/EventBus';
import { fetchLatestPrescription, updatePatientDemographics } from '../../services/api/visitApi';
import { openPrescriptionPdf } from '../../services/api/prescriptionPdfApi';
import {
  listPrescriptionTemplates,
  applyPrescriptionTemplate,
  deletePrescriptionTemplate,
} from '../../services/api/prescriptionTemplateApi';
import {
  VITAL_DEFINITIONS,
  DEFAULT_VITAL_KEYS,
  OPTIONAL_VITAL_KEYS,
  getSymptomSuggestions,
  COMMON_SYMPTOMS,
  PAD_COMMON_SYMPTOMS,
} from '../../constants/visitMetadata';
import AddPrescription from './AddPrescription';
import {
  SectionHeader, SectionDivider, SECTION_INFO, PadField, SECTION_LABEL_GUTTER, PrescriptionHoverStyles,
} from './PrescriptionHelpers';
import FollowUpPicker, { resolveFollowUpDate } from './FollowUpPicker';
import { LabTestsField, normalizeLabName, visitTestProjection } from './LabPanel';
import useProcedureStore from '../../stores/procedureStore';
import useLabEnabled from '../../hooks/useLabEnabled';
import useLabOrderStore from '../../stores/labOrderStore';
import { plusDaysISO } from '../common/ValidityControl';
import { STAFF_ROLES, specialisationName } from '../../constants/staff';
import {
  frequencyForSchedule, parseDoseSlot, formatDoseSlot, formatDoseSchedule, parseDoseSchedule,
  DOSE_STEP, DOSE_MIN, DOSE_MAX,
} from '../../utils/rxDosage';
import { formatDateIST } from '../../utils/formatIST';
import useFinePointer from '../../hooks/useFinePointer';
import TemplateStrip from './TemplateStrip';
import { NARROW_HOST_PX } from './TemplateChipRow';
import TemplatePickerList, { ALL_CATEGORIES } from './TemplatePickerList';
import TemplateSaveForm from './TemplateSaveForm';
import TemplateSuggestionRow, {
  TemplateGroupHeading, TemplateSeeAllRow,
} from './TemplateSuggestionRow';
import {
  TEMPLATE_CATEGORIES,
  WHOLE_PRESCRIPTION,
  TEMPLATE_DELETE_COPY,
  TEMPLATE_ROW_COPY,
  C_O_MORE_PILLS,
  IMAGES_SECTION_COPY,
  SYMPTOM_PICKER_COPY,
  selectByCategory,
  fieldTemplateRows,
  DOUBLE_CLICK_HINT,
  readTemplateSort,
  writeTemplateSort,
  templateSaveAffordance,
  mergeTemplates,
  buildAddToastMessage,
} from '../../utils/rxTemplates';
import {
  mergeMemoryDelta, memoryFieldRows, memoryGroupHeading, memoryCountLine, dedupeSystemRows, MEMORY_TYPES,
  TEST_TYPE_PLACEHOLDER, normaliseRowKey, normaliseMedicineRowKey,
  SURGERY_SECTION_LABEL, SURGERY_PLACEHOLDER, SURGERY_SECTION_TOOLTIP,
  removeMemoryRow, restoreMemoryRow,
  MEMORY_FORGET_ARIA, MEMORY_FORGET_TITLE, MEMORY_FORGET_BODY,
  MEMORY_FORGET_KEEP, MEMORY_FORGET_CONFIRM, MEMORY_FORGET_TOAST,
  coMemoryRows, CO_CHIP_LABEL, CO_CHIP_TITLE, recentCoValues,
  CO_DERIVED_INFO_TITLE, CO_DERIVED_INFO_BODY, CO_DERIVED_INFO_DISMISS, CO_DERIVED_INFO_ARIA,
} from '../../utils/rxMemory';
import MemorySuggestionRow from './MemorySuggestionRow';
import ConfirmationModal from '../common/ConfirmationModal/ConfirmationModal';

// P7 — container width at which the pad reflows into two columns.
export const TWO_COLUMN_PX = 1024;

// FE-6 — from this many advised surgeries the section scrolls inside its own
// box instead of growing until it pushes Follow-up off the screen. There is no
// cap on how many lines a doctor may add beyond the contract's 20.
export const SURGERY_SCROLL_AFTER = 6;
export const SURGERY_SCROLL_MAX_HEIGHT = '8rem';

// FIX-5 (owner) — the ONE medicine-row glyph: the org-list (master) medicine
// rows and the "★ Your pick" memory rows must show the same icon, so it is
// a single exported constant rather than a literal repeated in two JSX spots.
export const MEDICINE_ROW_ICON = '💊';

// FIX-7 (owner) — ONE shared floor for every on-focus dropdown's width, so a
// template/memory row never wraps onto multiple lines just because the field
// it is anchored to happens to be narrow (Surgery's own reported defect at
// ~330px). Reused verbatim by Diagnosis, Tests (lab-OFF) and Surgeries —
// never a bespoke per-field number.
export const RX_DROPDOWN_MIN_WIDTH = 320;

/**
 * dropdownRectWithMinWidth — widens a measured `{top, left, width}` rect up
 * to `RX_DROPDOWN_MIN_WIDTH`, then clamps both back inside the viewport so a
 * narrow host (<460px) never overflows past the right edge. `null`/absent
 * rect (not yet measured) rides through unchanged — every caller already
 * gates rendering on the rect being truthy.
 */
export function dropdownRectWithMinWidth(rect, minWidth = RX_DROPDOWN_MIN_WIDTH) {
  if (!rect) return rect;
  const vw = (typeof window !== 'undefined' && window.innerWidth) || 1024;
  const width = Math.min(Math.max(rect.width, minWidth), vw - 16);
  const left = Math.max(8, Math.min(rect.left, vw - width - 8));
  return { top: rect.top, left, width };
}

// ── Caches & helpers ────────────────────────────────────────────────
// SESSION-SCOPED, not app-scoped. The blob carries `top_medicines` /
// `top_complaints` / `top_diagnoses` (+ FE-2: the doctor's field `memory`) —
// the signed-in doctor's own prescribing history in the CURRENT org. The org
// is resolved server-side from the Cognito session, so the only in-app way to
// change org is sign out → sign in, which is a React Router transition, NOT a
// page reload: this module-level cache survives it. Left alone it renders
// org A's medicines as one-tap quick-add pills on org B's script, and starves
// org B of its own data because the cache short-circuits the fetch.
// `resetRxReferenceCache` below is called from signOut / signOutOptimistic,
// alongside the other session-scoped caches.
//
// FE-2: a `Map` of cache-key -> PROMISE (not the resolved value). One entry
// per memory owner (keyed on `visitId`, since the server resolves the owner
// FROM the visit's doctor per Q9 — practice mode has no visit, one shared
// key). Storing the promise (not the value) means four simultaneous mounts
// of the same visit all read the SAME in-flight request — no stampede — and
// a rejected read never poisons the cache for the next mount.
const PRACTICE_REFERENCE_KEY = '__practice__';
let _referenceCache = new Map();
export function resetRxReferenceCache() { _referenceCache = new Map(); }
const DRAFT_PREFIX = 'rx_draft_';
const getDraftKey = (id) => `${DRAFT_PREFIX}${id}`;
const DRAFT_MAX_AGE = 24 * 60 * 60 * 1000;

// RX-HALF-DOSE — every dosage STRING (chip, quick-add match, memory label)
// routes through the shared `formatDoseSchedule`/`parseDoseSchedule`
// (src/utils/rxDosage.js) — no hand-built `${m}-${a}-${n}` template
// literal anywhere in this file.
//
// Copy is PO-owned, verbatim from stories.md "## Copy":
// D7 (owner cut) — the static "Half doses allowed" helper line was dropped;
// the stepper's own ▲ showing ½ is the discoverability, no replacement text.
const DOSE_INVALID_COPY = 'Enter a dose in halves — 0, ½, 1, 1½ … up to 9.';
const DOSE_SAVE_BLOCKED_COPY = 'Fix the highlighted dose before saving.';

// Variant A stepper (owner-approved, build-plan.md) — a single M/A/N slot.
// Blurred: plain text showing the canonical glyph ("½", "1½"). Focused: a
// free-typed buffer (so "1/2" / "½" / "1.5" can be typed to completion
// before it is parsed) with ArrowUp/ArrowDown stepping by DOSE_STEP —
// same behaviour a native `step="0.5"` number input gives, without that
// input type's hard block on typing a fraction glyph.
//
// `onChange(parsedNumberOrNull)` — `null` means "reject this keystroke":
// the caller must keep the row's last valid value and flag only this slot
// (AC3) — this component never coerces an invalid entry to 0 itself.
function DoseSlotInput({ value, onChange, hasError, testId, ariaLabel, boxStyle, stepBtnStyle }) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => formatDoseSlot(value));

  useEffect(() => {
    if (!focused) setText(formatDoseSlot(value));
  }, [value, focused]);

  const step = (dir) => {
    const base = parseDoseSlot(text) ?? (typeof value === 'number' ? value : 0);
    const raw = base + dir * DOSE_STEP;
    const next = Math.min(DOSE_MAX, Math.max(DOSE_MIN, Math.round(raw / DOSE_STEP) * DOSE_STEP));
    setText(formatDoseSlot(next));
    onChange(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.15rem' }}>
      <button type="button" tabIndex={-1} onClick={() => step(1)} style={stepBtnStyle} aria-label={`${ariaLabel} increase by half`}>▲</button>
      <input
        type="text"
        inputMode="decimal"
        step={DOSE_STEP}
        min={DOSE_MIN}
        max={DOSE_MAX}
        data-testid={testId}
        aria-label={ariaLabel}
        value={text}
        onFocus={() => setFocused(true)}
        onChange={(e) => {
          const raw = e.target.value;
          setText(raw);
          // QA-D2: an empty/whitespace-only box is a CLEARED slot, not an
          // invalid one — coerce to 0 silently (the old number input's
          // behaviour, AC2/I9). parseDoseSlot's own `null` contract stays
          // strict for every other caller (parseDoseSchedule etc.); this is
          // the one UI entry point that must not flag "".
          onChange(raw.trim() === '' ? 0 : parseDoseSlot(raw));
        }}
        onBlur={() => {
          setFocused(false);
          setText(formatDoseSlot(value));
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') { e.preventDefault(); step(1); }
          else if (e.key === 'ArrowDown') { e.preventDefault(); step(-1); }
        }}
        style={boxStyle}
      />
      <button type="button" tabIndex={-1} onClick={() => step(-1)} style={stepBtnStyle} aria-label={`${ariaLabel} decrease by half`}>▼</button>
    </div>
  );
}

// ── C2 — curated medicine dropdown: ONE merge over a source list, source
// tagged per row. No fuzzy/alias data (C1) and no lab tests (C3) enter this
// list — only the doctor's own templates and the org medicine master.
// FE-10: the doctor's own past prescribing (★ Your pick) is NOT one of these
// source lists any more — it is `buildRxMemoryRows`'s memory rows, rendered
// as its own group ABOVE this merge (see `memRows` at both call sites),
// never mixed into it. Dedup here is by lowercase name, first occurrence
// wins — callers control priority via list ORDER. A later source (e.g. an
// admin-managed master) slots in by adding one more list, no re-cut.
function mergeCuratedMedicineRows(sourceLists) {
  const seen = new Set();
  const merged = [];
  for (const rows of sourceLists) {
    for (const row of rows) {
      const key = row.label.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(row);
    }
  }
  return merged;
}

// Local, synchronous match against the doctor's own saved Rx templates — no
// API round-trip. Template medicines already carry form/strength/dosage
// (dosage_schedule)/duration/timing (see handleApplyTemplate), so this needs
// no shape translation beyond turning M-A-N slots into the "1-0-1" string
// directAddMedicine already knows how to split back apart.
function buildTemplateMedicineMatches(templates, lastToken) {
  const seen = new Set();
  const out = [];
  for (const tpl of (templates || [])) {
    for (const m of (tpl.medicines || [])) {
      const name = m.medicine || m.medicine_input;
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key) || !key.includes(lastToken)) continue;
      seen.add(key);
      out.push({
        type: 'medicine', label: name, shorthand: name, source: 'template',
        form: m.form || null, strength: m.strength || null,
        common_dosage: m.dosage_schedule ? formatDoseSchedule(m.dosage_schedule) : null,
        frequency: m.frequency || null,
        common_duration: m.duration || null, common_timing: m.timing || null,
      });
    }
  }
  return out;
}

// D10/D25 — a CATEGORISED template offered inside its own field, now on FOCUS
// as well as while typing. One row stands for the WHOLE set ("Fever meds · 3
// medicines"), so it must never be mistaken for a single medicine: the renderer
// gives it its own filled card (`TemplateSuggestionRow`). Uncategorised
// (whole-prescription) templates are deliberately excluded — they live in the
// Templates strip and the All-templates list only.
//
// Which templates, in what order, how many and whether `See all n` is offered
// all come from the ONE `fieldTemplateRows` selector — the same
// `selectByCategory` ordering the strip's tab counts read. Nothing here
// re-derives a count or a cap.
function buildTemplateSetRows(selection, category, kind, query, narrow) {
  const group = fieldTemplateRows(selection, category, query, { narrow });
  const rows = group.rows.map((t, i) => ({
    type: 'template_set', source: 'template_set', kind, category,
    template: t, label: t.name,
    // The group heading renders above the FIRST row of each kind.
    startsGroup: i === 0,
    groupHeading: group.heading,
  }));
  // bk-989 — `See all n` is an ENTRY, not a decoration on the last row: it is
  // the last thing ↓ reaches in the group, so the keyboard can get to the
  // templates the cap is hiding. bk-988 — it is here while typing too.
  if (group.seeAll > 0) {
    rows.push({
      type: 'template_see_all', source: 'template_see_all',
      kind, category, count: group.seeAll,
    });
  }
  return rows;
}

// Medicine-master rows (server-sourced, source:"master") — FROZEN CONTRACT:
// { name, code, type, strength_value, strength_unit, dosage, frequency,
//   duration, timing, qty, brand_name, composition, deleted, source }.
// brand_name/composition are empty for the pilot — never displayed. `deleted`
// rows must never appear — filtered out defensively even though the server
// is expected to already exclude them. strength_unit may be null: join with
// a space only when present — a bare-digit strength must stay unit-less so
// the existing no-unit warning still fires (never invent "mg").
//
// `strength_value` arrives as a JSON NUMBER (40, 0.5), not a string. It must
// NOT be tested for truthiness: a strength of 0 is falsy, so `filter(Boolean)`
// silently drops it and "0 mg" renders as the bare unit "mg". Presence, not
// truthiness — dropping a doctor's strength digit is the same never-invent
// violation U1 exists to prevent.
const isPresent = (v) => v !== null && v !== undefined && v !== '';
function buildMasterMedicineRows(apiMedicines) {
  return (apiMedicines || [])
    .filter((m) => m.source === 'master' && !m.deleted)
    .map((m) => ({
      type: 'medicine', label: m.name, shorthand: m.name, source: 'master',
      // FE-14 — the master's own identity code, carried through to the save
      // payload so a master pick folds by code, not just by display name.
      code: m.code || null,
      form: m.type || null,
      strength: [m.strength_value, m.strength_unit].filter(isPresent).join(' ') || null,
      common_dosage: m.dosage || null, frequency: m.frequency || null,
      common_duration: m.duration || null, common_timing: m.timing || null,
    }));
}

// FE-3B/FE-10 — the doctor's own remembered medicines (G1 `memory.types.RX`)
// — THE ONLY source of "★ Your pick" / `Your medicines`, one suggestion row
// per remembered name, filled from its TOP-ranked variant (`variants[0]` —
// up to 3, ranked `count` desc by FE-1). Shaped so `medicineArgFromRow`
// (below) and `applySuggestion`'s existing template/master direct-add
// branch need no special case for this source.
//
// FE-10 — this used to sit ALONGSIDE a second, server-ranked "doctor_history"
// derivation from the debounced `/prescription/suggest` call (its own ★ Your
// pick badge, its own variant sub-pills, both able to disagree with this
// list). That second derivation is retired: `variants` (the full ranked
// array, capped at 3 by FE-1) and `subtitle` (the top variant's inline
// context, shown only when there is nothing to pick between) now ride the
// row itself, so the render below needs no second data source for either.
function buildRxMemoryRows(memGroup, heading, query) {
  return memGroup.rows.map((r, i) => {
    const variants = r.variants || [];
    const topVariant = variants[0] || {};
    const multiVariant = variants.length > 1;
    return {
      type: 'medicine', label: r.v, shorthand: r.k || r.v, source: 'doctor_memory', row: r,
      form: topVariant.form || null, strength: topVariant.strength || null,
      common_dosage: topVariant.dosage || null, common_duration: topVariant.duration || null,
      common_timing: topVariant.timing || null,
      variants: multiVariant ? variants.slice(0, 3) : null,
      subtitle: (!multiVariant && (topVariant.strength || topVariant.dosage || topVariant.form))
        ? [topVariant.strength, topVariant.form, topVariant.dosage, topVariant.duration].filter(Boolean).join(' · ')
        : null,
      startsGroup: i === 0, groupHeading: heading,
      countLine: i === 0 ? memoryCountLine(memGroup.total, memGroup.matched, query) : undefined,
    };
  });
}

// T2b — browse-all: request the WHOLE org master once per mount (additive
// `limit`, server-cached — see visitMetadataApi.fetchPrescriptionSuggestions).
// 500 is the backend's own le=500 ceiling.
const BROWSE_ALL_LIMIT = 500;

// Client-side filter over the cached raw master rows — name, code, brand,
// composition, case/space-insensitive. Runs on every keystroke but over an
// in-memory array (no request), so it costs nothing the browser can feel.
function filterCachedMasterRows(rawRows, token) {
  const t = String(token || '').toLowerCase().replace(/\s+/g, '');
  if (!t) return rawRows;
  return rawRows.filter((m) => {
    const hay = [m.name, m.code, m.brand_name, m.composition]
      .filter(Boolean).join(' ').toLowerCase().replace(/\s+/g, '');
    return hay.includes(t);
  });
}

// T2b — ONE derivation of the count line, browse and filtered alike, so the
// two states can't drift. `shown` excludes lab rows (Y is "medicines", not
// tests). Ratified: Y=1 → "medicine" singular; X=1 → "matches" (verb only,
// "medicines" itself still tracks Y). X===Y collapses to the plain total —
// "Showing 276 of 276" is noise. Null when there's nothing to say (0 total
// is the bk-887 empty state's job, not this line's).
function countLineText(shown, total, query) {
  if (total == null || total === 0) return null;
  // X==0 → the no-match sentence owns this, never both on screen at once.
  if (shown === 0) return null;
  const medWord = `medicine${total === 1 ? '' : 's'}`;
  if (shown === total) return `${total} ${medWord}`;
  if (query) {
    const verb = shown === 1 ? 'matches' : 'match';
    return `${shown} of ${total} ${medWord} ${verb} "${query}"`;
  }
  return `Showing ${shown} of ${total} ${medWord}`;
}

// ── Typed-name resolution against the org's medicine list (rx-r3 backend).
// A parse result may carry `medicine_source: "master"` + `master_medicine`
// (one exact hit — the backend has ALREADY filled the row's blank
// frequency/timing/duration/strength; typed always wins), or
// `medicine_candidates[]` + the duplicate warning (more than one row with that
// name — the backend fills nothing and the doctor picks).
// Both strings below are the ratified/backend copy, used VERBATIM.
const MASTER_SOURCE_LABEL = 'From your medicine list';
const MULTI_MASTER_MATCH_WARNING = 'More than one medicine has this name. Pick the one you meant.';

// ONE prescription-row builder, shared by every fill path (dropdown pick of a
// template / master / ★ Your pick row, and the duplicate-candidate pick). A
// second mapper would be a second set of rules.
function buildMedicineRow(med) {
  // RX-HALF-DOSE: parseDoseSchedule (not `.split('-').map(Number)`) —
  // the old split silently turned a half-dose string like "½-0-½" into
  // NaN -> 0-0-0 (a wrong-dose bug on every quick-add/template/memory fill).
  const doseSource = med.dosage || med.common_dosage || '';
  return {
    medicine: med.name || med.medicine || '',
    medicine_input: (med.raw_input || med.name || '').toLowerCase(),
    form: med.form || null,
    strength: med.strength || null,
    dosage_schedule: parseDoseSchedule(doseSource),
    frequency: med.frequency || null,
    timing: med.timing || med.common_timing || null,
    duration: med.duration || med.common_duration || null,
    route: null,
    notes: null,
    // A pick from the org's medicine list IS "from your medicine list" — the
    // SAME single rule the typed-resolution path and the duplicate-picker
    // already use for the chip, not a second one. Every other caller passes no
    // `source` and is unaffected.
    medicine_source: med.source === 'master' ? 'master' : null,
    // FE-14 — the master's identity code rides ONLY with a master pick (the
    // save payload's `code` mirrors `medicine_source`); a typed/parser/memory
    // row never carries one onto the wire.
    code: med.source === 'master' ? (med.code || null) : null,
    is_resolved: true,
    _source: 'quick_add',
    _needsReview: false,
    _warnings: [],
  };
}

// Suggestion row (as built by buildTemplateMedicineMatches /
// buildMasterMedicineRows) → buildMedicineRow's argument shape.
function medicineArgFromRow(s) {
  return {
    name: s.label,
    raw_input: s.shorthand || s.label,
    source: s.source || null,
    code: s.code || null,
    form: s.form || null,
    strength: s.strength || null,
    dosage: s.common_dosage || null,
    duration: s.common_duration || null,
    timing: s.common_timing || null,
    frequency: s.frequency || null,
  };
}

// The master fills only what the doctor left blank — a typed value is never
// overwritten. Keys the master can contribute to a prescription row:
const MASTER_FILLABLE = ['medicine', 'form', 'strength', 'dosage_schedule', 'frequency', 'duration', 'timing'];
function fillBlanks(row, filled) {
  const out = { ...row };
  for (const k of MASTER_FILLABLE) {
    const v = out[k];
    if (v === null || v === undefined || v === '') out[k] = filled[k];
  }
  return out;
}

// ── LC1 — lab-catalogue rows for the Rx dropdown. TEXT ONLY, by construction:
// a row carries a `label` and its source tag and NOTHING else — no
// procedure_id, no cost — so there is nothing here an order could be built
// from (advising needs a catalog_procedure_id). Matching reuses LabPanel's
// `normalizeLabName`, the same matcher the dedupe uses, so the dropdown and
// the Tests row can never disagree on what "the same test" is.
// Capped at 5 so the tests can never push the medicines (10) off the list;
// the loop breaks at the cap, so a 300-test catalogue costs 5 matches, not 300.
const LAB_SUGGESTION_CAP = 5;
function buildLabCatalogRows(labCatalog, lastToken) {
  const token = normalizeLabName(lastToken);
  if (token.length < 2) return [];
  const out = [];
  for (const t of labCatalog) {
    if (out.length >= LAB_SUGGESTION_CAP) break;
    if (t.name && normalizeLabName(t.name).includes(token)) {
      out.push({ label: t.name, source: 'lab_catalog' });
    }
  }
  return out;
}

// ── Never-invent parser (U1) — a bare digit strength ("650") carries no
// unit by design (D-1 Option B). Row-level warning AND the print-time
// confirmation both derive from this ONE check — never duplicated. A
// strength with a unit ("650 mg") or a blank strength never matches.
const STRENGTH_NO_UNIT_RE = /^\d+(\.\d+)?$/;
function strengthHasNoUnit(strength) {
  if (!strength) return false;
  return STRENGTH_NO_UNIT_RE.test(String(strength).trim());
}
const STRENGTH_NO_UNIT_WARNING = 'Strength has no unit — add mg, mcg or ml before printing.';

// ── Blank-field note (R3) ─────────────────────────────────────────────
// The org's medicine list is sparse — of the pilot's 148 rows, 132 carry no
// strength, 130 no duration, 83 no timing — so picking one fills the row only
// partly and nothing says so. This note names what is blank. It is a NOTE, not
// an error: a doctor may legitimately print with blanks, so it is muted, never
// red, carries no icon and never blocks.
//
// "dose is blank" == the M-A-N boxes carry no dose AT ALL — no schedule object,
// OR every slot is 0. The inline editor renders an empty box as `0` (`?? 0`),
// so on screen "empty" and "all-zero" are the same thing; treating 0-0-0 as
// filled would silently exempt every untouched row.
function doseIsBlank(schedule) {
  if (!schedule) return true;
  return !['morning', 'afternoon', 'night'].some((k) => Number(schedule[k]) > 0);
}

// Most of the pilot's uploaded medicines carry the strength inside the NAME —
// "OLOX 50 MG", "AMKEY 100 MG", "MOXIROA 400" (~44 of 148). Nagging "strength
// is not filled in" while "50 MG" sits on the same row reads as a bug, so a
// name containing ANY number suppresses the strength item.
//
// This ONLY decides whether to nag. It parses nothing out of the name, writes
// nothing, infers nothing: the strength field stays exactly as blank as it
// was, the saved payload and the printed script are untouched.
const NAME_HAS_NUMBER_RE = /\d/;
const nameCarriesStrength = (m) => NAME_HAS_NUMBER_RE.test(m.medicine || m.medicine_input || '');

// Fixed display order, regardless of which are missing (ratified).
const BLANK_FIELD_CHECKS = [
  ['strength', (m) => !m.strength && !nameCarriesStrength(m)],
  ['form', (m) => !m.form],
  ['dose', (m) => doseIsBlank(m.dosage_schedule)],
  ['duration', (m) => !m.duration],
  ['timing', (m) => !m.timing],
];

// Ratified joining: one item → the item · two → "a and b" · three or more →
// "a, b and c". No serial comma.
function joinBlankLabels(labels) {
  if (labels.length < 2) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

// `noUnitShown` — whether the row is ALREADY showing the no-unit line. The two
// must never both refer to strength: if the no-unit line owns strength, this
// note stays silent about it. A strength that is entirely blank produces no
// no-unit line (strengthHasNoUnit(null) is false by design), so `strength`
// lands here instead — the two directions of the same single rule.
function blankFieldNote(med, noUnitShown) {
  const labels = BLANK_FIELD_CHECKS
    .filter(([label, isBlank]) => !(label === 'strength' && noUnitShown) && isBlank(med))
    .map(([label]) => label);
  return labels.length > 0 ? `Not filled in: ${joinBlankLabels(labels)}.` : null;
}

// Row-warning precedence, top to bottom (ratified). Warnings the server sends
// that are not listed here (e.g. "Manually added — verify name") keep their
// arrival order above these.
const NOT_IN_MASTER_WARNING = 'Not in your medicine list — check the spelling, or print it as typed.';
const ROW_WARNING_PRECEDENCE = [MULTI_MASTER_MATCH_WARNING, NOT_IN_MASTER_WARNING, STRENGTH_NO_UNIT_WARNING];
function byRowWarningPrecedence(a, b) {
  const ra = ROW_WARNING_PRECEDENCE.indexOf(a);
  const rb = ROW_WARNING_PRECEDENCE.indexOf(b);
  return (ra === -1 ? -1 : ra) - (rb === -1 ? -1 : rb);
}

// ONE derivation of a row's warning lines + blank note, so the row strip and
// the print confirmation can never disagree about what a row is missing.
function rowNotices(med) {
  // The server sends the no-unit sentence in `warnings` too, but that copy is a
  // snapshot. Two cases:
  //  - a strength VALUE is present (bare digit "650", or later edited to
  //    "650 mg") → the LIVE derivation owns the warning entirely: drop the
  //    server's copy and re-add it only if the CURRENT value still has no
  //    unit. This is what clears the warning the instant the doctor types the
  //    unit in — D-1's "goes stale" half.
  //  - strength is null (the liquid/injectable guard — the parser explicitly
  //    refused to invent a per-volume number) → there is nothing to derive
  //    locally (strengthHasNoUnit(null) is false by design), so the server's
  //    own copy is the ONLY signal and must survive, or the row goes silent —
  //    D-1's "nothing renders at all" half.
  const hasStrengthValue = !!med.strength;
  const localNoUnit = strengthHasNoUnit(med.strength);
  const warnings = [
    ...(med._warnings || []).filter((w) => !(hasStrengthValue && w === STRENGTH_NO_UNIT_WARNING)),
    ...(localNoUnit ? [STRENGTH_NO_UNIT_WARNING] : []),
  ].sort(byRowWarningPrecedence);
  return { warnings, blankNote: blankFieldNote(med, warnings.includes(STRENGTH_NO_UNIT_WARNING)) };
}

// T-F1 — ONE failure message for every "could not add this line" case (parser
// call failed, or the parser returned something we couldn't classify). The
// card already shows the doctor's typed text, so the message never repeats it.
const PARSE_FALLBACK_MSG = "Couldn't add this automatically — your text is kept here. Fill in the details to finish.";

// Module-level constant for CC/DX smart suggestions (stable identity).
// Renamed from `CLAUSE_SPLIT_RE` — deliberately different from rxMemory's
// comma-only `splitMemoryClauses`: this answers "which segment am I typing
// NOW", shared by C/O and Diagnosis via `getActiveSegment`; do not unify
// with `splitMemoryClauses`.
const ACTIVE_SEGMENT_SPLIT_RE = /[,&+]|\band\b/i;

/**
 * replaceActiveSegment — RX-FORM-BUGS item 2. A memory-suggestion PICK
 * replaces the ACTIVE segment the query was computed from (`getActiveSegment`
 * above) instead of trailing along beside the fragment the doctor was
 * typing — the "IRR" + "Throat irritation" → "IRR, Throat irritation" bug.
 * `splitRe` MUST be the same regex the caller filtered its suggestions
 * with, so "which segment am I replacing" can never drift from "which
 * segment did I search on".
 *
 * No separator anywhere in `text` (including a blank field) → the whole
 * field IS the active segment: a full replace. Otherwise everything up to
 * and including the LAST separator match survives, followed by exactly one
 * space, then the pick — whether the trailing segment (what the doctor was
 * mid-typing) was non-blank ("irr" before the pick) or already blank (a
 * closed segment, e.g. a trailing "fever, ").
 */
export function replaceActiveSegment(text, insert, splitRe) {
  const raw = String(text || '');
  if (!raw.trim()) return insert;
  const flags = splitRe.flags.includes('g') ? splitRe.flags : `${splitRe.flags}g`;
  const globalRe = new RegExp(splitRe.source, flags);
  let match;
  let last = null;
  while ((match = globalRe.exec(raw)) !== null) {
    last = match;
    if (match.index === globalRe.lastIndex) globalRe.lastIndex += 1; // never loop on a zero-length match
  }
  if (!last) return insert;
  const base = raw.slice(0, last.index + last[0].length);
  return /\s$/.test(base) ? `${base}${insert}` : `${base} ${insert}`;
}

// FE-12 — Symptoms' "yours" chip carries its OWN small forget ×: Symptoms
// renders memory as small WRAPPING chips (`symptomOption`), not a scrollable
// row list like the other five fields (`MemorySuggestionRow`), so it cannot
// reuse that row's layout. Same rule (hover-revealed on a fine pointer,
// always visible on touch, keyboard-focus also reveals it, never a nested
// button) adapted to a chip: the label chip and the "×" sit as flex
// SIBLINGS inside one wrapper.
function SymptomYoursChip({
  sym, selected, highlighted, finePointer, onToggle, onForget, forgetAriaLabel, isDark, type, inkLight,
}) {
  const [chromeVisible, setChromeVisible] = useState(false);
  const forgetVisible = !finePointer || chromeVisible;
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setChromeVisible(true)}
      onMouseLeave={() => setChromeVisible(false)}
      onFocus={() => setChromeVisible(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setChromeVisible(false); }}
    >
      <button
        onClick={onToggle}
        style={{
          padding: '0.3rem 0.6rem', borderRadius: '2rem', fontSize: type.chip,
          border: selected || highlighted
            ? `1.5px solid ${isDark ? '#fbbf24' : '#d97706'}`
            : `1px solid ${isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.12)'}`,
          background: selected || highlighted
            ? (isDark ? 'rgba(234,179,8,0.18)' : '#fef3c7')
            : (isDark ? '#1e293b' : '#f9fafb'),
          color: selected || highlighted ? (isDark ? '#fbbf24' : '#92400e') : inkLight,
          cursor: 'pointer', fontWeight: selected ? 600 : 400, fontFamily: 'system-ui, sans-serif',
        }}
      >{selected ? '✓ ' : ''}{sym}</button>
      <button
        type="button"
        aria-label={forgetAriaLabel}
        onClick={(e) => { e.stopPropagation(); onForget(); }}
        style={{
          marginLeft: '-0.35rem', flexShrink: 0, width: '1.1rem', height: '1.1rem',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: isDark ? 'rgba(239,68,68,0.18)' : '#fee2e2', color: '#ef4444',
          fontSize: '0.65rem', fontWeight: 700, lineHeight: 1,
          opacity: forgetVisible ? 1 : 0, position: 'relative', zIndex: 1,
        }}
      >✕</button>
    </span>
  );
}

// R1-FE (rx-allergies) — Repeat Last Rx copy, Business-ratified verbatim
// (`state/sessions/rx-allergies/copy.md` §3-5, 2026-09-11). The toast is the
// Business EDIT of the PO draft, not the original wording. Every surface
// (chip, tooltip, confirm dialog, toast) reads the SAME `prevRxSummary`
// derivation built below — they can never disagree about what a previous
// prescription contained.
// D2 (rx-allergies QA): `visit_date` is `string | null` (C6) — when it's
// absent, drop the date segment (and its separator) instead of rendering a
// dangling "· ·" / "from : ".
const REPEAT_RX_CHIP = ({ date, summary }) =>
  date ? `🔄 Repeat Last Rx · ${date} · ${summary}` : `🔄 Repeat Last Rx · ${summary}`;
const REPEAT_RX_TOOLTIP = ({ date, summary }) =>
  date ? `Load prescription from ${date} — ${summary}` : `Load prescription — ${summary}`;
const REPEAT_RX_TITLE = 'Load previous prescription?';
const REPEAT_RX_CONFIRM_EMPTY = ({ date, summary }) => date
  ? `The pad is empty. This will fill it with this patient's prescription from ${date}: ${summary}.`
  : `The pad is empty. This will fill it with this patient's prescription: ${summary}.`;
const REPEAT_RX_CONFIRM_FILLED = ({ date, summary }) => date
  ? `This will replace what you have entered on the pad with this patient's prescription from ${date}: ${summary}.`
  : `This will replace what you have entered on the pad with this patient's prescription: ${summary}.`;
const REPEAT_RX_TOAST = ({ date, summary }) =>
  date ? `Loaded the prescription from ${date} — ${summary}` : `Loaded the prescription — ${summary}`;

// P-FE-1 (rx-allergies phase 2) — Business-ratified attribution shown beside
// the Allergies label while the box still holds the unedited patient-record
// value (copy.md "Phase 2 ratification" rule 4). Sentence case, no icon, no
// parentheses, no tooltip. NOT cuttable.
const ALLERGIES_FROM_RECORD_HINT = 'From patient record';

// ═════════════════════════════════════════════════════════════════════
export default function DigitalPrescriptionForm({
  visitId, onSuccess, onSaveStart, onClose, initialData, editMetaId,
  doctorSpecialization, patientId, visitStatus, visit,
  // True once the parent's visit-metadata fetch has resolved at least once
  // (defaults true for callers that don't pass it, e.g. practice mode,
  // which never has a metadata-driven editMetaId to wait for). Gates the
  // draft-restore prompt below: editMetaId is set ASYNCHRONOUSLY now (the
  // parent auto-resumes the visit's latest saved Rx from a metadata fetch,
  // not synchronously from a prop passed in on mount), so checking
  // `editMetaId` alone on the very first render always saw it still empty
  // and showed a stale "Restore unsaved work?" prompt even when a
  // perfectly good saved prescription (images included) was seconds away
  // from loading in.
  metadataLoaded = true,
  practiceMode = false,
}) {
  const { isDark } = useTheme();
  // `role` comes from the SAME PermissionContext `usePermissions()` reads;
  // `useScope()` is the null-safe reader (usePermissions throws outside a
  // provider) and this form is already mounted in trees without one.
  const { memberId: scopedDoctorId, role } = useScope();

  // D12 — ONE derived gate for every template affordance on this pad: the chip
  // rows, the All-templates list, "+ New template" and the field suggestions.
  // Hidden, never disabled; hidden on a completed / read-only visit; and the
  // list is not even FETCHED for a role that may not have it (the API refuses
  // independently, this just never asks).
  const canUseTemplates = useMemo(() => (
    (role === STAFF_ROLES.DOCTOR || role === STAFF_ROLES.ADMIN)
    && String(visitStatus || '').toUpperCase() !== 'COMPLETED'
  ), [role, visitStatus]);

  // Lab module: when ON (and not in practice mode), the "Tests" row becomes the
  // lab-advise surface (in-house orders + outside advice). `investigations[]`
  // holds the outside (print-only) strings; in-house tests are real LabOrders.
  const labEnabled = useLabEnabled();
  const labByVisit = useLabOrderStore((s) => s.byVisit);
  const labAdvise = useLabOrderStore((s) => s.advise);
  const labFetchForVisit = useLabOrderStore((s) => s.fetchForVisit);
  const labActive = labEnabled && !practiceMode;

  // Lab catalogue for the Rx dropdown (LC1) — READ-ONLY subscription. This form
  // must never drive `fetchProceduresForDept` (locked by
  // rxform_no_procedure_store_fetch): `LabTestsField`, rendered below whenever
  // the lab module is on, already fetches the dept-SCOPED slice, and we read
  // what it filled. No fetch can originate here, so no request count can grow.
  // Scoping is the SERVER's (dept-tagged ∪ org-wide) — never re-filter by
  // department here, or a test with NO department (org-wide) would be dropped.
  // Gated on labActive because the slice is shared with the procedure pickers,
  // which can fill it on this screen even when the lab module is off.
  const pickerProcedures = useProcedureStore((s) => s.pickerProcedures);
  const labCatalog = useMemo(
    () => (labActive ? pickerProcedures.filter((p) => p.category === 'lab' && p.active !== false) : []),
    [labActive, pickerProcedures]
  );

  // ── STATE ─────────────────────────────────────────────────────────
  const [vitalValues, setVitalValues] = useState({});
  const [activeVitalKeys, setActiveVitalKeys] = useState([...DEFAULT_VITAL_KEYS]);
  const [showOptionalVitals, setShowOptionalVitals] = useState(false);
  const [customVitals, setCustomVitals] = useState([]);
  const [showCustomVitalForm, setShowCustomVitalForm] = useState(false);
  const [customVitalLabel, setCustomVitalLabel] = useState('');
  const [customVitalUnit, setCustomVitalUnit] = useState('');

  const [selectedSymptoms, setSelectedSymptoms] = useState(new Set());
  const [customSymptomInput, setCustomSymptomInput] = useState('');
  const [symptomHighlight, setSymptomHighlight] = useState(-1);
  const [customSymptoms, setCustomSymptoms] = useState([]);
  const [showSymptomPicker, setShowSymptomPicker] = useState(false);
  const [symptomPickerRect, setSymptomPickerRect] = useState(null);
  // P4 — the C/O shortcut row shows 3 of the doctor's recent complaints; the
  // rest are one tap away.
  const [ccPillsExpanded, setCcPillsExpanded] = useState(false);
  // P6 — the medicine hint line shows while the box has focus or is empty.
  const [medBoxFocused, setMedBoxFocused] = useState(false);

  const [clinicalNotes, setClinicalNotes] = useState('');
  const [chiefComplaint, setChiefComplaint] = useState('');
  // Allergies — what THIS prescription entry carries and prints (like C/o).
  // Deliberately NOT prescription content, so it never gates the save guard
  // below. Phase 2 (P-FE-1) additionally seeds it from the PATIENT RECORD and
  // writes a doctor-edited value back there — see `patientAllergies` below.
  const [allergies, setAllergies] = useState('');
  // P-FE-1 — the PATIENT RECORD's allergies (contract P2:
  // `reference.patient_allergies`), i.e. the pad's default and the baseline the
  // write-back keeps in step. `allergiesDirty` is set by the textarea's
  // onChange ONLY — never by prefill, draft restore or edit-in-place
  // hydration — because it is what licenses the patient write (never-narrow
  // guard, Business condition 1).
  const [patientAllergies, setPatientAllergies] = useState('');
  const [allergiesDirty, setAllergiesDirty] = useState(false);
  // One-shot latch for the patient-record seed + a synchronous mirror of the
  // box, so the seed can read "is the box still empty?" from the async
  // reference callback without an effect that could re-fire and clobber typing.
  const allergiesSeededRef = useRef(false);
  const allergiesValueRef = useRef('');
  const allergiesDirtyRef = useRef(false);
  const patientAllergiesRef = useRef('');
  allergiesValueRef.current = allergies;
  allergiesDirtyRef.current = allergiesDirty;
  patientAllergiesRef.current = patientAllergies;
  const [diagnosis, setDiagnosis] = useState(initialData?.diagnosis || '');
  const [medicines, setMedicines] = useState(initialData?.medicines || []);
  const [doctorNotes, setDoctorNotes] = useState(initialData?.doctor_notes || '');
  const [followUpRequired, setFollowUpRequired] = useState(initialData?.follow_up_required ?? false);
  const [followUpDate, setFollowUpDate] = useState(resolveFollowUpDate(initialData?.follow_up_in) || null);
  const [investigations, setInvestigations] = useState(initialData?.investigations || []);
  // FE-6 (G2) — the doctor's advised surgeries, plain text: no procedure order,
  // no OT booking, no billing line (Q3). `advised_surgeries` is `string[] | null`
  // on the wire (null on legacy rows), which reads as [] here.
  const [advisedSurgeries, setAdvisedSurgeries] = useState(initialData?.advised_surgeries || []);
  // SINGLE SOURCE for what counts + prints — same projection the inline Tests row
  // uses, so the "(N tests)" count and the PDF match it exactly (live order names +
  // free-text strings with no order; a cancelled/done test's leftover string is
  // excluded). Lab OFF → just the raw investigation strings (unchanged behaviour).
  const printableInvestigations = useMemo(
    () => (labActive
      ? visitTestProjection(labByVisit[visitId] || [], investigations).printableNames
      : investigations),
    [labActive, labByVisit, visitId, investigations]
  );

  const [savedTemplates, setSavedTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  // ONE toast per Add (D7), carrying Undo while it is up. `canUndo` goes false
  // after the first Undo — one Undo, not a stack.
  const [templateToast, setTemplateToast] = useState(null); // { message, canUndo }
  const templateToastTimerRef = useRef(null);
  const [templatesError, setTemplatesError] = useState(null);
  const templatesFetchRef = useRef(null); // single-flight latch for the ONE list fetch
  // Which row asked for the list, and where to anchor it. Null = closed.
  const [templateListRequest, setTemplateListRequest] = useState(null); // { category, rect }
  const [showTemplateSaveForm, setShowTemplateSaveForm] = useState(false);
  // Category the save form opens on ('' = whole prescription) — set by the
  // section's `Save…` (D18).
  const [saveFormCategory, setSaveFormCategory] = useState('');
  // The template being EDITED (D15); null = the save form creates a new one.
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Previous prescription (repeat last Rx)
  const [prevRx, setPrevRx] = useState(null); // { medicines, investigations, doctor_notes, follow_up_required, follow_up_in, visit_date }
  const [prevRxLoading, setPrevRxLoading] = useState(false);

  const [rawInput, setRawInput] = useState('');
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [refData, setRefData] = useState(null);
  // FE-12 — forget a remembered entry (G5). `memoryForgetConfirm` is the
  // pending confirm (`ConfirmationModal`, never a native dialog): `{ type,
  // row }`. `memoryForgetToast` is the transient Undo banner: `{ message,
  // onUndo }`, `onUndo` absent once the window has passed (10s, matching the
  // pad's existing template-add toast).
  const [memoryForgetConfirm, setMemoryForgetConfirm] = useState(null);
  const [memoryForgetToast, setMemoryForgetToast] = useState(null);
  // r5 — the C/O "×" on a DERIVED individual value (no store row of its
  // own) opens the SAME `ConfirmationModal`, informational: `{ name, lines }`
  // — `lines` is the row's own `from` (rank order, set by `expandCoRows`),
  // never re-derived here.
  const [coDerivedInfo, setCoDerivedInfo] = useState(null);
  // Diagnosis/C-O/Medicines' own dropdown is an IMPERATIVE snapshot
  // (`dxSuggestions`/`ccSuggestions`/`suggestions`), built on focus/keystroke
  // — unlike every other field's memory group (a `useMemo` over `refData`),
  // it does not notice `refData` changing on its own. `{ type }` asks the
  // effect below to re-run that field's OWN builder once `refData` has
  // actually committed the forget/undo, so a CURRENTLY OPEN dropdown
  // reflects it immediately (no refetch — same mount payload, re-filtered).
  const [memoryForgetRequery, setMemoryForgetRequery] = useState(null);
  const memoryForgetToastTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(memoryForgetToastTimerRef.current), []);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggIdx, setSelectedSuggIdx] = useState(-1);
  const [suggPopoverRect, setSuggPopoverRect] = useState(null); // {top,left,width} — fixed-position anchor
  // D25/F8 — "is this host narrow?" for the template rows inside the four
  // dropdowns (2 rows, no sub-line, no badge). Same flag `TemplateChipRow` /
  // `TemplateStrip` already own — one breakpoint for the whole feature — and it
  // rides the pad's EXISTING width measure below (still one resize listener).
  const [narrowHost, setNarrowHost] = useState(false);

  const [editingIdx, setEditingIdx] = useState(null);
  const [editingInvIdx, setEditingInvIdx] = useState(null);
  const [showUpload, setShowUpload] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  // FE-4 — the lab-OFF free-text Tests strip: typing here shows the doctor's
  // own remembered tests (memory.types.INV), never a `/suggest` call.
  // FE-10 — closes the keyboard-walk gap FE-4/FE-5 flagged (lab-ON's own gap
  // was closed by FE-5 in `LabListPicker.jsx`; this is the lab-OFF half).
  // −1 = nothing highlighted, where it starts and returns to on every keystroke.
  const [testInput, setTestInput] = useState('');
  const [showTestSuggestions, setShowTestSuggestions] = useState(false);
  const [testPopoverRect, setTestPopoverRect] = useState(null);
  const [testHighlight, setTestHighlight] = useState(-1);
  // FE-6 — the Advised surgeries strip: same shape as the Tests strip (chips +
  // free typing), plus the ↑↓/Enter/Esc walk Diagnosis and C/O already have.
  // −1 = nothing highlighted, where it starts and returns to on every keystroke.
  const [surgeryInput, setSurgeryInput] = useState('');
  const [showSurgerySuggestions, setShowSurgerySuggestions] = useState(false);
  const [surgeryPopoverRect, setSurgeryPopoverRect] = useState(null);
  const [surgeryHighlight, setSurgeryHighlight] = useState(-1);

  // ── Autocomplete for Chief Complaint & Diagnosis ──────────────
  const [ccSuggestions, setCcSuggestions] = useState([]); // [{name, shorthand, source}]
  const [showCcSuggestions, setShowCcSuggestions] = useState(false);
  const [dxSuggestions, setDxSuggestions] = useState([]);
  const [showDxSuggestions, setShowDxSuggestions] = useState(false);
  // D25/F4 — the diagnosis dropdown gains a keyboard path (it had none) and is
  // rect-anchored like the medicine popover, so the pad's `overflow: hidden`
  // cannot clip it (bk-973). −1 = nothing highlighted, which is where it starts
  // and returns to on every keystroke.
  const [dxHighlight, setDxHighlight] = useState(-1);
  const [dxPopoverRect, setDxPopoverRect] = useState(null);
  // FE-3B — same keyboard path + rect-anchor for C/O, now that its dropdown
  // can be a full (up to 300-row) memory list instead of the old 8rem-capped
  // history strip; without the rect anchor a tall list would be clipped by
  // the pad's `overflow: hidden` exactly like bk-973 did for Diagnosis.
  const [ccHighlight, setCcHighlight] = useState(-1);
  const [ccPopoverRect, setCcPopoverRect] = useState(null);
  const dxInputRef = useRef(null);
  const ccInputRef = useRef(null);
  const ccTimerRef = useRef(null);
  const dxTimerRef = useRef(null);
  const ccSuggestionsRef = useRef(null);
  const dxSuggestionsRef = useRef(null);
  const ccUsedRef = useRef(new Set()); // tracks CC suggestions clicked this session
  const dxUsedRef = useRef(new Set()); // tracks DX suggestions clicked this session

  // ── Quick-add pills (loaded from refData.top_*) ──────────────
  // P3 — the learned-medicine pill band is gone (see the note at its old
  // place); the reference call still fills this so restoring the band is one
  // binding + one block.
  // RX-FORM-BUGS item 1/3 — the "recent" C/O and Instructions pills no
  // longer read `top_complaints`/`top_instructions` (the legacy
  // top-30-by-usage slice: crowded out by medicines, never updated
  // in-session). They derive from `refData.memory` instead — `recentCoValues`
  // (rxMemory.js) and `memoryFieldRows(..., MEMORY_TYPES.INSTR, ...)` below —
  // so `topComplaints`/`topInstructions` state is gone; `top_diagnoses` is
  // untouched (out of scope). The API response fields themselves are left
  // alone (no contract change).
  const [, setTopMedicines] = useState([]);
  const [topDiagnoses, setTopDiagnoses] = useState([]);

  // ── Instruction autocomplete (local-only from refData.instructions) ──
  const [instructionSuggestions, setInstructionSuggestions] = useState([]);
  const [showInstructionSugg, setShowInstructionSugg] = useState(false);
  // ── Per-medicine note suggestions (static INSTRUCTION_PHRASES) ────
  const [medNoteSugg, setMedNoteSugg] = useState([]);
  const [showMedNoteSugg, setShowMedNoteSugg] = useState(false);
  const [editingMedNoteIdx, setEditingMedNoteIdx] = useState(null);

  // Unrecognized items awaiting manual classification
  const [unrecognizedItems, setUnrecognizedItems] = useState([]); // [{ input, warning }]
  // Investigation candidates — parser knows it's an investigation, doctor picks which one
  const [investigationPicks, setInvestigationPicks] = useState([]); // [{ input, candidates: string[] }]
  // Investigation likely — parser thinks it's an investigation (indicator word detected)
  const [investigationLikely, setInvestigationLikely] = useState([]); // [{ input }]

  // In-app dialog state (replaces window.confirm)
  const [dialog, setDialog] = useState(null); // { title, message, onConfirm, onCancel }

  // Image Attachments State (Custom Theme)
  const [attachedImageFiles, setAttachedImageFiles] = useState([]); // File[]
  const [themeSupportsImages, setThemeSupportsImages] = useState(false);
  const [isDraggingImages, setIsDraggingImages] = useState(false);
  // Already-saved images for the prescription currently being edited
  // (editMetaId) — these are plain browser File objects for NEW,
  // not-yet-uploaded attachments above; a saved image has no local File
  // to show, only a meta_id + presigned URL fetched from the backend.
  const [existingImages, setExistingImages] = useState([]); // [{meta_id, file_name}]
  const [existingImageUrls, setExistingImageUrls] = useState({}); // meta_id -> url
  // Staged removal: clicking X on an already-saved thumbnail hides it locally
  // (existingImages) but the actual delete only fires on Generate Prescription,
  // so a refresh before saving restores it — same "undoable until save" contract
  // as newly-attached (not-yet-uploaded) files.
  const removedExistingImageIdsRef = useRef([]);
  const handleRemoveExistingImage = useCallback((metaId) => {
    setExistingImages((prev) => prev.filter((img) => img.meta_id !== metaId));
    removedExistingImageIdsRef.current.push(metaId);
  }, []);

  // Client-side mirror of the server's image rules (arogyakhata-backend
  // visit_metadata_service.upload_prescription_images: ALLOWED_EXTS
  // jpg/jpeg/png/webp, 5 MB cap) so a bad file is refused up front instead of
  // failing after the old image was already replaced.
  const takeValidImages = useCallback((files) => {
    const ok = [];
    for (const f of files) {
      if (!/\.(jpe?g|png|webp)$/i.test(f.name || '')) { setError(IMAGES_SECTION_COPY.badType(f.name)); continue; }
      if (f.size > 5 * 1024 * 1024) { setError(IMAGES_SECTION_COPY.tooBig(f.name)); continue; }
      ok.push(f);
    }
    return ok;
  }, []);

  // Replace = stage removal of the saved image + add the picked file (count
  // unchanged). A cancelled picker fires no change event → nothing changes.
  const handleReplaceExistingImage = useCallback((metaId, file) => {
    if (!file || takeValidImages([file]).length === 0) return;
    handleRemoveExistingImage(metaId);
    setAttachedImageFiles((prev) => [...prev, file]);
  }, [handleRemoveExistingImage, takeValidImages]);
  const handleReplaceAttachedImage = useCallback((idx, file) => {
    if (!file || takeValidImages([file]).length === 0) return;
    setAttachedImageFiles((prev) => prev.map((f, i) => (i === idx ? file : f)));
  }, [takeValidImages]);

  // Resolve a presigned URL per already-saved image for thumbnail display.
  const existingImageIds = existingImages.map((img) => img.meta_id).join(',');
  useEffect(() => {
    if (!visitId || existingImages.length === 0) { setExistingImageUrls({}); return; }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        existingImages.map(async (img) => {
          try {
            const { url } = await getFileDownloadUrl(visitId, 'PRESCRIPTION_IMAGE', img.meta_id);
            return [img.meta_id, url];
          } catch {
            return [img.meta_id, null];
          }
        })
      );
      if (!cancelled) setExistingImageUrls(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitId, existingImageIds]);

  useEffect(() => {
    let active = true;
    getPrescriptionConfig().then(res => {
      if (!active) return;
      const theme = res?.config?.theme || res?.theme;
      const caps = res?.capabilities?.[theme] || res?.capabilities;
      if (theme === 'custom' || caps?.supports_image_attachments) {
        setThemeSupportsImages(true);
      }
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  // Nurse-prefilled vitals tracking
  const [nurseVitalsSource, setNurseVitalsSource] = useState(null); // { name, timestamp } of who entered
  const doctorModifiedVitalsRef = useRef(false); // tracks if doctor has manually touched vitals

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  // Adjacent fix — a pick's `setRawInput('') + .focus()` used to fire the
  // focus SYNCHRONOUSLY, before React committed the cleared value, so the
  // input's own onFocus handler re-ran with the STALE (pre-pick) text and
  // re-opened the dropdown on the medicine that was JUST added. Deferring
  // the DOM focus one macrotask lets the clear commit first, so refocus
  // correctly reads the input as empty (browse) or freshly typed.
  const focusInputSoon = useCallback(() => {
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  // FE-28 (owner live run) — a WARNING WINS OVER THE DROPDOWN. Adding a row
  // that raises one ("Not in your medicine list…", "Strength has no unit…",
  // the "Not filled in:" note, an investigation card) used to leave the
  // field's panel open on top of it, so the doctor never saw it. When an add
  // produces a warning the panel closes and does NOT auto-reopen while the
  // input keeps focus — the next keystroke or a real re-focus (blur → focus)
  // brings it back. A clean add is untouched.
  const suppressAutoOpenRef = useRef(false);
  const [warningSeq, setWarningSeq] = useState(0);   // bumped when one appears
  const warningRowRef = useRef(null);                // the LAST row carrying one
  const flagRowWarning = useCallback(() => {
    suppressAutoOpenRef.current = true;
    setWarningSeq((n) => n + 1);
  }, []);
  // Bring the warning into view — `nearest`, so a row already on screen never
  // moves and the pad cannot jump under the doctor's hand.
  useEffect(() => {
    if (warningSeq === 0) return;
    warningRowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [warningSeq]);
  // FIX-6 (owner, live click-through) — a click on an input that is ALREADY
  // focused fires no `focus` event (the browser only fires one on a genuine
  // focus change), so any field whose dropdown closed WITHOUT blurring —
  // Medicines under D38's warning-suppress above, or an Enter-driven pick on
  // Diagnosis/C-O/Tests/Surgeries, none of which blur — stayed closed until
  // the doctor left the field and came back. ONE shared re-opener: a
  // `mousedown` on an ALREADY-focused input counts as a re-focus and runs
  // the SAME opener a real focus would; D38 itself is untouched (a warning
  // still closes the panel the moment it appears). Symptoms' picker has no
  // separate open/closed flag to get stuck — its dropdown content is
  // derived straight from the typed text — so it does not share this bug
  // class and is not wired here.
  const reopenIfAlreadyFocused = useCallback((ref, opener) => () => {
    if (document.activeElement === ref.current) opener();
  }, []);
  // T2a — the popover is positioned with `position: fixed` off the input's
  // own rect (no ReactDOM portal — stays inside this component's tree so the
  // existing pinned suites' `container.querySelectorAll(...)` keeps finding
  // it), which escapes the pad's `overflow:hidden` (:1663) without a second
  // DOM-mount mechanism. Re-measured on open + scroll/resize while open.
  const suggItemRefs = useRef({});          // idx -> row button, for scrollIntoView
  const lastSuggMouseRef = useRef({ x: null, y: null }); // real-movement gate for hover
  const aiTimerRef = useRef(null);
  const draftTimerRef = useRef(null);
  const symptomPickerRef = useRef(null);
  // D25/F5 — the picker panel is rect-anchored off its own "+ add" button.
  const symptomPickerAnchorRef = useRef(null);
  // bk-989 — the picker's `See all n`, so Enter on it can anchor the list.
  const symptomSeeAllRef = useRef(null);
  // FE-4 — the lab-OFF Tests strip's own add input, rect-anchored the same way.
  const testInputRef = useRef(null);
  // FE-6 — the Advised surgeries strip's own add input, rect-anchored the same way.
  const surgeryInputRef = useRef(null);
  // FE-8 — bk-989's pattern: the surgery dropdown's `See all n`, so Enter on it
  // can anchor the list the same way the other three template groups do.
  const surgerySeeAllRef = useRef(null);
  const skipDraftRef = useRef(false); // guards draft save during/after save
  const lastSavedMetaIdRef = useRef(null); // tracks meta_id returned by our own save to skip re-populate
  const undoStackRef = useRef([]); // undo history for Ctrl+Z
  const handleSaveRef = useRef(null); // stable ref for global shortcut

  // T3-LITE — per-row DOM handles, keyed by medicine index. Editor panel (for
  // scrollIntoView on open) · its first field (Medicine name, for autofocus) ·
  // the row's own Edit button (to return focus to on Done/close).
  const editorRefs = useRef({});
  const editorFirstFieldRefs = useRef({});
  const editButtonRefs = useRef({});

  // ── UNDO: snapshot form state before destructive operations ─────
  // Symptoms are part of the snapshot: a template Add can change them (D7), so
  // an Undo that left them behind would not be "the exact pre-add pad state".
  // (They were missing here before this build — Ctrl+Z silently kept them too.)
  const pushUndo = useCallback(() => {
    undoStackRef.current = [...undoStackRef.current.slice(-19), {
      medicines: [...medicines],
      investigations: [...investigations],
      // FE-6 — a template Add / Replace / copy-previous-Rx can change the
      // advised surgeries too, so an Undo that left them behind would not be
      // "the exact pre-add pad state" either.
      advisedSurgeries: [...advisedSurgeries],
      selectedSymptoms: [...selectedSymptoms],
      customSymptoms: [...customSymptoms],
      doctorNotes, diagnosis, chiefComplaint, allergies, clinicalNotes,
      followUpRequired, followUpDate,
    }];
  }, [medicines, investigations, advisedSurgeries, selectedSymptoms, customSymptoms, doctorNotes, diagnosis, chiefComplaint, allergies, clinicalNotes, followUpRequired, followUpDate]);

  const popUndo = useCallback(() => {
    const stack = undoStackRef.current;
    if (!stack.length) return;
    const snap = stack[stack.length - 1];
    undoStackRef.current = stack.slice(0, -1);
    setMedicines(snap.medicines);
    setInvestigations(snap.investigations);
    setAdvisedSurgeries(snap.advisedSurgeries || []);
    setSelectedSymptoms(new Set(snap.selectedSymptoms || []));
    setCustomSymptoms(snap.customSymptoms || []);
    setDoctorNotes(snap.doctorNotes);
    setDiagnosis(snap.diagnosis);
    setChiefComplaint(snap.chiefComplaint);
    setAllergies(snap.allergies || '');
    setClinicalNotes(snap.clinicalNotes);
    setFollowUpRequired(snap.followUpRequired);
    setFollowUpDate(snap.followUpDate);
  }, []);

  // ── COMPUTED ──────────────────────────────────────────────────────
  const symptomSuggestions = useMemo(
    () => getSymptomSuggestions(doctorSpecialization),
    [doctorSpecialization]
  );
  const availableOptionalVitals = useMemo(
    () => OPTIONAL_VITAL_KEYS.filter((k) => !activeVitalKeys.includes(k)),
    [activeVitalKeys]
  );

  // D26 — the doctor's chosen order, read once per mount from this device's
  // storage (absent / unreadable / poisoned → `Newest first`, never an error).
  // The PAD owns it because the same order drives THREE surfaces: the list, the
  // strip's chips and the on-focus dropdown rows.
  const [templateSort, setTemplateSort] = useState(() => readTemplateSort(scopedDoctorId));
  const changeTemplateSort = useCallback((sort) => {
    setTemplateSort(sort);
    writeTemplateSort(scopedDoctorId, sort);
  }, [scopedDoctorId]);

  // THE template selection — every chip row, every "See all n" count and the
  // All-templates list read this one derivation (never a second sort/count).
  const templateSelection = useMemo(
    () => selectByCategory(savedTemplates, templateSort),
    [savedTemplates, templateSort]
  );
  // A template symptom outside these lists is stored as CUSTOM, exactly as if
  // the doctor had typed it (D7).
  const knownSymptoms = useMemo(
    () => new Set([...symptomSuggestions.specialized, ...symptomSuggestions.common,
      ...COMMON_SYMPTOMS, ...PAD_COMMON_SYMPTOMS]),
    [symptomSuggestions]
  );
  // The only templates whose individual medicines may appear in the ℞ dropdown
  // (a symptom / diagnosis / lab template must never leak in there).
  const medicineSourceTemplates = useMemo(() => [
    ...templateSelection.byCategory[TEMPLATE_CATEGORIES.MEDICINES],
    ...templateSelection.byCategory[WHOLE_PRESCRIPTION],
  ], [templateSelection]);

  // ── Helper: populate vitals fields from a metadata entry ────────
  const applyNurseVitals = useCallback((entry) => {
    if (!entry?.vitals?.length) return;
    const newValues = {};
    const keysNeeded = new Set([...DEFAULT_VITAL_KEYS]);
    for (const v of entry.vitals) {
      if (v.key && v.value !== undefined && v.value !== '' && v.value != null) {
        newValues[v.key] = String(v.value);
        keysNeeded.add(v.key);
      }
    }
    if (Object.keys(newValues).length === 0) return;
    // Expand active vital keys to include any nurse-entered keys not in defaults
    setActiveVitalKeys(prev => {
      const merged = new Set(prev);
      for (const k of Object.keys(newValues)) merged.add(k);
      return [...merged];
    });
    setVitalValues(prev => ({ ...prev, ...newValues }));
    // Also handle custom vitals from nurse
    const customFromNurse = entry.vitals.filter(v => v.is_custom && v.value);
    if (customFromNurse.length > 0) {
      setCustomVitals(prev => {
        const existingKeys = new Set(prev.map(c => c.label));
        const newCustom = customFromNurse
          .filter(v => !existingKeys.has(v.label))
          .map(v => ({ key: v.key || `nurse_${Date.now()}_${Math.random().toString(36).slice(2,6)}`, label: v.label, value: String(v.value), unit: v.unit || '' }));
        return [...prev, ...newCustom];
      });
    }
    setNurseVitalsSource({
      name: entry.added_by_name || 'Nurse',
      timestamp: entry.created_at,
    });
  }, []);

  // ── P-FE-1 — seed Allergies from the PATIENT RECORD ────────────────
  // ONE-SHOT, ref-guarded, called from the reference read's `.then` — the same
  // shape as `applyNurseVitals` above and deliberately NOT an effect that could
  // re-fire and clobber typing. Precedence (highest wins):
  //   1. typed this session (`allergiesDirty`)   2. saved Rx entry (`initialData`)
  //   3. restored draft                          4. the patient record (here)
  //   5. empty
  // Levels 1-3 all show up as "the box already has a value" by the time this
  // runs, or as a later user/restore action that legitimately overwrites the
  // default — so an empty, untouched box is the only thing this fills.
  const seedAllergiesFromPatientRecord = useCallback((value) => {
    if (allergiesSeededRef.current) return;
    allergiesSeededRef.current = true;
    if (!value) return;
    if (allergiesDirtyRef.current) return;      // the doctor typed first — never overwrite
    if (allergiesValueRef.current) return;      // saved Rx entry / draft already won
    setAllergies(value);
  }, []);

  // ── EFFECTS ───────────────────────────────────────────────────────

  // Global keyboard shortcuts: Ctrl+Enter → Generate, Ctrl+Z → Undo
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); handleSaveRef.current?.(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        // Only intercept when no native text input is focused
        const tag = document.activeElement?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        e.preventDefault(); popUndo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [popUndo]);

  useEffect(() => {
    if (editMetaId && initialData) {
      // Skip re-populating when the editMetaId came from our own save —
      // the form already holds the correct state.
      if (lastSavedMetaIdRef.current === editMetaId) return;

      setMedicines(initialData.medicines || []);
      setDoctorNotes(initialData.doctor_notes || '');
      setChiefComplaint(initialData.chief_complaint || '');
      // P-FE-1 precedence — the saved entry's own allergies win (editing a
      // prescription must show what THAT prescription will re-print); an entry
      // that carries none falls through to the patient record, never to blank.
      setAllergies(initialData.allergies || patientAllergiesRef.current || '');
      setDiagnosis(initialData.diagnosis || '');
      setFollowUpRequired(initialData.follow_up_required ?? false);
      setFollowUpDate(resolveFollowUpDate(initialData.follow_up_in) || null);
      setInvestigations(initialData.investigations || []);
      // FE-6 — `null` on a legacy row (frozen G2: `string[] | null`) reads as [].
      setAdvisedSurgeries(initialData.advised_surgeries || []);
      setRawInput(''); setError(null); setSuccessMsg(null);
      // Restore vitals snapshot saved with the prescription
      if (initialData.vitals && initialData.vitals.length > 0) {
        const restoredValues = {};
        const restoredKeys = new Set([...DEFAULT_VITAL_KEYS]);
        const restoredCustom = [];
        for (const v of initialData.vitals) {
          if (v.is_custom) {
            restoredCustom.push({ key: v.key, label: v.label, value: String(v.value), unit: v.unit || '' });
          } else {
            restoredValues[v.key] = String(v.value);
            restoredKeys.add(v.key);
          }
        }
        setVitalValues(restoredValues);
        setActiveVitalKeys([...restoredKeys]);
        if (restoredCustom.length > 0) setCustomVitals(restoredCustom);
      }
    }
  }, [editMetaId]); // eslint-disable-line react-hooks/exhaustive-deps

  // existingImages stays in sync with the backend's CURRENT attached-image
  // set independent of the "skip re-populate on our own save" guard above:
  // right after a save, add_prescription has ALREADY relinked/added images
  // server-side, and that guard would otherwise hide them from view for
  // the rest of the session (found live: a doctor who couldn't see their
  // already-attached images re-attached the same photos a second time).
  // Keyed off a stable ids fingerprint, not the initialData object
  // reference (a new object every parent render). A resync here means
  // fresh server truth just landed, so any not-yet-saved staged removal
  // is dropped too -- it either already applied (handleSave clears the
  // ref on success) or is stale against this newer list.
  const initialAttachedImageIds = (initialData?.attached_images || []).map((img) => img.meta_id).join(',');
  useEffect(() => {
    setExistingImages(initialData?.attached_images || []);
    removedExistingImageIdsRef.current = [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMetaId, initialAttachedImageIds]);

  // The ONE mount read (G1, widened to also carry `memory`). `visit_id` is
  // sent so the server resolves the memory owner from the VISIT'S DOCTOR
  // (Q9=A) — never a `member_id`, the API doesn't accept one. Practice mode
  // has no real visit, so the param is omitted even though `visitId` may
  // carry a synthetic practice id. Cache key = the memory owner's scope
  // (`visitId`, or the shared practice key) so four simultaneous mounts of
  // the SAME visit share one in-flight promise (no request storm), and a
  // different visit/owner (referral) gets its own key.
  useEffect(() => {
    let cancelled = false;
    const cacheKey = practiceMode ? PRACTICE_REFERENCE_KEY : (visitId || PRACTICE_REFERENCE_KEY);
    // Never straddle two doctors' data while a new read is in flight — the
    // owner is decided by the RESPONSE, not a client guess, so clear first.
    setRefData(null);
    setTopMedicines([]); setTopDiagnoses([]);

    let promise = _referenceCache.get(cacheKey);
    if (!promise) {
      promise = fetchPrescriptionReference(practiceMode ? undefined : visitId);
      _referenceCache.set(cacheKey, promise);
      // Never cache a rejection — the next mount/remount gets a fresh try
      // instead of permanently inheriting a transient failure.
      promise.catch(() => {
        if (_referenceCache.get(cacheKey) === promise) _referenceCache.delete(cacheKey);
      });
    }
    promise.then((d) => {
      if (cancelled) return;
      setRefData(d);
      // P-FE-1 — the patient record's allergies ride on THIS existing mount
      // read (contract P2). No new request, no new effect: the seed is a
      // one-shot call, not a dependency.
      const recorded = d.patient_allergies || '';
      setPatientAllergies(recorded);
      seedAllergiesFromPatientRecord(recorded);
      setTopMedicines(d.top_medicines || []);
      setTopDiagnoses(d.top_diagnoses || []);
    }).catch(() => {
      // Memory-store / reference read failed — the pad stays usable with no
      // quick-add pills and no memory groups; never a blocking spinner
      // (S2 AC10 / G1 "memory-store failure degrades gracefully").
    });
    return () => { cancelled = true; };
  }, [visitId, practiceMode, seedAllergiesFromPatientRecord]);

  // ── FE-12 — forget a remembered entry (G5) ────────────────────────
  // The SAME "update this mount's state AND re-key the module promise
  // cache" move the save path's `memory_delta` merge already makes (above,
  // `rxResponse?.memory_delta`) — a remount later in the SAME session sees
  // the change, never a second `/reference` read.
  const applyMemoryChange = useCallback((updater) => {
    const cacheKey = practiceMode ? PRACTICE_REFERENCE_KEY : (visitId || PRACTICE_REFERENCE_KEY);
    setRefData((prev) => {
      if (!prev) return prev;
      const updatedMemory = updater(prev.memory);
      if (updatedMemory === prev.memory) return prev; // no-op, nothing matched
      const updated = { ...prev, memory: updatedMemory };
      if (_referenceCache.has(cacheKey)) _referenceCache.set(cacheKey, Promise.resolve(updated));
      return updated;
    });
  }, [practiceMode, visitId]);

  // Opens the `ConfirmationModal` — never a native `window.confirm`. `row`
  // is the EXACT memory row object (kept whole, not just its `v`) so Undo
  // can restore it (variants included) without a second lookup.
  const requestForgetMemoryRow = useCallback((type, row) => {
    setMemoryForgetConfirm({ type, row });
  }, []);

  const cancelForgetMemoryRow = useCallback(() => setMemoryForgetConfirm(null), []);

  // r5 — informational-only, no DELETE, no memory mutation: `row.from` is
  // `expandCoRows`'s own rank-ordered source-line list, read verbatim.
  const showCoDerivedInfo = useCallback((row) => {
    setCoDerivedInfo({ name: row.v, lines: row.from || [] });
  }, []);
  const dismissCoDerivedInfo = useCallback(() => setCoDerivedInfo(null), []);

  const confirmForgetMemoryRow = useCallback(async () => {
    const pending = memoryForgetConfirm;
    setMemoryForgetConfirm(null);
    if (!pending) return;
    const { type, row } = pending;
    try {
      await forgetPrescriptionMemory(type, row.v, practiceMode ? undefined : visitId);
    } catch (err) {
      // A 404 ("No such learning entry.") means the row is already gone
      // server-side either way — treat it exactly like a 200. Any OTHER
      // refusal is left AS-IS. Per the BUILT backend (INT-1 reconcile,
      // artifacts/contract-actual.md §3): 403 for a non-prescriber AND for a
      // `visit_id` outside the caller's org ("Visit not found." — never 404,
      // no cross-org existence leak); 400 only for a `type` outside the
      // seven. None of the three is reachable from this UI (the × only
      // renders on the caller's own rendered rows), and no retry/error copy
      // exists on the PO Copy list for them — so this stays a no-op rather
      // than a "try again" wrapper: the row simply stays offered, exactly as
      // if the click never happened.
      if (err?.type !== 'NOT_FOUND') return;
    }
    applyMemoryChange((memory) => removeMemoryRow(memory, type, row.v));
    setMemoryForgetRequery({ type });
    if (memoryForgetToastTimerRef.current) clearTimeout(memoryForgetToastTimerRef.current);
    setMemoryForgetToast({
      message: MEMORY_FORGET_TOAST,
      // "Forget ≠ blocklist" (G5) — there is no server-side undo; Undo
      // simply re-adds the exact row this session just removed, the same
      // way a re-typed value would re-learn at count 1 on the next save.
      onUndo: () => {
        applyMemoryChange((memory) => restoreMemoryRow(memory, type, row));
        setMemoryForgetRequery({ type });
        setMemoryForgetToast(null);
      },
    });
    memoryForgetToastTimerRef.current = setTimeout(() => setMemoryForgetToast(null), 10000);
  }, [memoryForgetConfirm, applyMemoryChange, practiceMode, visitId]);

  // ── Fetch existing vitals (nurse-entered) on mount ────────────────
  useEffect(() => {
    if (!visitId || practiceMode) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchVisitMetadata(visitId, 'VITAL');
        if (cancelled) return;
        // Pick the most recent vital entry
        const entries = data?.entries || data?.metadata || [];
        if (entries.length > 0) {
          // Sort by created_at descending and take the latest
          const sorted = [...entries].sort((a, b) => {
            const ta = new Date(a.created_at || 0).getTime();
            const tb = new Date(b.created_at || 0).getTime();
            return tb - ta;
          });
          applyNurseVitals(sorted[0]);
        }
      } catch { /* silent — vitals will just be empty */ }
    })();
    return () => { cancelled = true; };
  }, [visitId, practiceMode, applyNurseVitals]);

  // ── Real-time: listen for nurse adding vitals while doctor has form open ─
  useEffect(() => {
    if (!visitId || practiceMode) return;
    const unsub = eventBus.on('VISIT_METADATA_ADDED', (event) => {
      const p = event?.payload;
      if (!p || p.visitId !== visitId) return;
      if (p.metaType !== 'VITAL') return;
      // Only auto-fill if doctor hasn't manually touched vitals yet
      if (doctorModifiedVitalsRef.current) return;
      // p.entry contains the full metadata entry with vitals array
      if (p.entry) applyNurseVitals(p.entry);
    });
    return unsub;
  }, [visitId, practiceMode, applyNurseVitals]);

  useEffect(() => {
    // P1 (owner) — root cause: the Medicine field is the only one of the four
    // that exempts a mousedown on its OWN input from this outside-click
    // close (`inputRef` check below). Diagnosis and Chief Complaint were
    // missing that exemption: a mousedown ANYWHERE inside the field
    // (including the field itself, since it is not inside `*SuggestionsRef`
    // — that ref wraps only the dropdown, not the input) closed the panel
    // first; the panel only reopened if the browser then fired a FRESH
    // `focus` event, which it does not for a click on an ALREADY-focused
    // element (e.g. clicking again to move the caret while typing) — so a
    // click mid-typing closed the suggestions and never reopened them.
    // Symptoms' picker ref wraps its OWN trigger button too (already
    // self-exempt); Diagnosis/Chief-Complaint's dropdown refs do not wrap
    // their input, so they needed the same explicit exemption Medicine has.
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) setShowSuggestions(false);
      if (symptomPickerRef.current && !symptomPickerRef.current.contains(e.target)) setShowSymptomPicker(false);
      if (ccSuggestionsRef.current && !ccSuggestionsRef.current.contains(e.target) &&
          ccInputRef.current && !ccInputRef.current.contains(e.target)) setShowCcSuggestions(false);
      if (dxSuggestionsRef.current && !dxSuggestionsRef.current.contains(e.target) &&
          dxInputRef.current && !dxInputRef.current.contains(e.target)) setShowDxSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // T2a — anchor the fixed-position popover to the input's own rect, and
  // re-measure while it's open (scroll on any ancestor, or a resize) so it
  // tracks the input instead of drifting.
  useEffect(() => {
    if (!showSuggestions) return;
    const measure = () => {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setSuggPopoverRect({ top: r.bottom, left: r.left, width: r.width });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [showSuggestions]);

  // D25/F4 — same rect anchor for the diagnosis panel (and the symptom picker
  // below): both are `position: absolute` children of a pad that is
  // `overflow: hidden`, so a template group inside them could be clipped in the
  // narrow accordion — the exact "I can't see the templates" complaint this
  // round exists to fix.
  useEffect(() => {
    if (!showDxSuggestions) return undefined;
    const measure = () => {
      const el = dxInputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setDxPopoverRect({ top: r.bottom, left: r.left, width: r.width });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [showDxSuggestions]);

  // FE-3B — same rect anchor for the Chief Complaint panel, for the same
  // reason (its dropdown is now a full memory list, not the old 8rem strip).
  useEffect(() => {
    if (!showCcSuggestions) return undefined;
    const measure = () => {
      const el = ccInputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setCcPopoverRect({ top: r.bottom, left: r.left, width: r.width });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [showCcSuggestions]);

  useEffect(() => {
    if (!showSymptomPicker) return undefined;
    const measure = () => {
      const el = symptomPickerAnchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setSymptomPickerRect({ top: r.bottom, left: r.left });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [showSymptomPicker]);

  // FE-4 — same rect anchor for the lab-OFF Tests strip's memory dropdown.
  useEffect(() => {
    if (!showTestSuggestions) return undefined;
    const measure = () => {
      const el = testInputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setTestPopoverRect({ top: r.bottom, left: r.left, width: r.width });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [showTestSuggestions]);

  // FE-6 — same rect anchor for the Advised surgeries strip's memory dropdown.
  useEffect(() => {
    if (!showSurgerySuggestions) return undefined;
    const measure = () => {
      const el = surgeryInputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setSurgeryPopoverRect({ top: r.bottom, left: r.left, width: r.width });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [showSurgerySuggestions]);

  // A debounced suggestion fetch scheduled just before the pad closes used to
  // fire ANYWAY, ~300ms after unmount: one wasted request per abandoned field
  // and a setState on a dead component. Cleared with everything else.
  useEffect(() => () => {
    clearTimeout(aiTimerRef.current);
    clearTimeout(dxTimerRef.current);
    clearTimeout(ccTimerRef.current);
  }, []);

  // Keep the ArrowUp/ArrowDown-highlighted row on screen — missing before,
  // so keyboard selection could walk off the visible window on a long list.
  useEffect(() => {
    if (selectedSuggIdx < 0) return;
    suggItemRefs.current[selectedSuggIdx]?.scrollIntoView({ block: 'nearest' });
  }, [selectedSuggIdx]);

  // A resting mouse must never fight keyboard navigation: only move the
  // highlight when the pointer has actually MOVED (not on mouseenter fired
  // by the row sliding under a stationary cursor via scrollIntoView above).
  const handleSuggMouseMove = useCallback((idx) => (e) => {
    const { clientX, clientY } = e;
    if (lastSuggMouseRef.current.x === clientX && lastSuggMouseRef.current.y === clientY) return;
    lastSuggMouseRef.current = { x: clientX, y: clientY };
    setSelectedSuggIdx(idx);
  }, []);

  // ONE list fetch per mount. Single-flight AND idempotent: concurrent callers
  // share the in-flight promise, and a settled success is never re-fetched — so
  // re-renders, a late role hydration and repeated calls can't multiply
  // requests. A FAILURE clears the latch so the list's "Try again" works. The
  // error is held in state; it never blocks writing the prescription.
  const loadTemplates = useCallback(async () => {
    if (templatesFetchRef.current) return templatesFetchRef.current;
    const p = (async () => {
      try {
        setTemplatesLoading(true); setTemplatesError(null);
        const d = await listPrescriptionTemplates();
        setSavedTemplates(d.templates || []);
      } catch (err) {
        templatesFetchRef.current = null;
        setTemplatesError(err?.message || 'failed');
      } finally { setTemplatesLoading(false); }
    })();
    templatesFetchRef.current = p;
    return p;
  }, []);
  // Not fetched at all for a role that may not hold templates (the API refuses
  // independently — this simply never asks). `canUseTemplates` is a primitive,
  // so this fires once when it becomes true, not once per render.
  useEffect(() => { if (canUseTemplates) loadTemplates(); }, [canUseTemplates, loadTemplates]);

  // Load most recent digital prescription for this patient (single API call)
  // Scoped to current doctor — prevents cross-specialty Rx leakage
  useEffect(() => {
    if (!patientId || !visitId || practiceMode) return;
    let cancelled = false;
    (async () => {
      try {
        setPrevRxLoading(true);
        const data = await fetchLatestPrescription(patientId, visitId, scopedDoctorId || null);
        if (!cancelled && data.found) {
          setPrevRx({
            medicines: data.medicines || [],
            investigations: data.investigations || [],
            advised_surgeries: data.advised_surgeries || [],
            doctor_notes: data.doctor_notes || '',
            chief_complaint: data.chief_complaint || '',
            diagnosis: data.diagnosis || '',
            // `allergies` is deliberately NOT projected: nothing reads it any
            // more (phase-2 owner reversal — Repeat Last Rx never touches that
            // box, and it is not in the summary vocabulary either).
            follow_up_required: data.follow_up_required || false,
            follow_up_in: data.follow_up_in || null,
            visit_date: data.visit_date || null,
          });
        }
      } catch { /* silent */ }
      finally { if (!cancelled) setPrevRxLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [patientId, visitId, practiceMode, scopedDoctorId]);

  // Auto-draft (skip when save just cleared state or visit is completed)
  useEffect(() => {
    if (!visitId || practiceMode) return;
    draftTimerRef.current = setInterval(() => {
      if (skipDraftRef.current || visitStatus === 'COMPLETED') return;
      const any = medicines.length > 0 || investigations.length > 0 || advisedSurgeries.length > 0 ||
        Object.values(vitalValues).some(v => v !== '' && v != null) ||
        selectedSymptoms.size > 0 || clinicalNotes.trim() || doctorNotes.trim() || chiefComplaint.trim() || diagnosis.trim();
      if (any) {
        try {
          localStorage.setItem(getDraftKey(visitId), JSON.stringify({
            medicines, doctorNotes, followUpRequired, followUpDate, investigations,
            advisedSurgeries,
            vitalValues, activeVitalKeys, customVitals,
            selectedSymptoms: [...selectedSymptoms], customSymptoms, clinicalNotes,
            chiefComplaint, allergies, diagnosis,
            savedAt: Date.now(),
          }));
        } catch { /* ignore */ }
      }
    }, 10000);
    return () => clearInterval(draftTimerRef.current);
  }, [visitId, practiceMode, visitStatus, medicines, doctorNotes, followUpRequired, followUpDate, investigations,
      advisedSurgeries, vitalValues, activeVitalKeys, customVitals, selectedSymptoms, customSymptoms, clinicalNotes, chiefComplaint, allergies, diagnosis]);

  // Restore draft (skip for completed visits — prescription is already saved)
  // Waits for metadataLoaded: editMetaId is populated ASYNCHRONOUSLY (the
  // parent auto-resumes the visit's latest saved Rx from a metadata fetch),
  // so on the very first render editMetaId is ALWAYS still empty even when
  // a real saved prescription is about to load in a moment — checking
  // editMetaId alone let this effect fire (and show a stale-draft prompt)
  // before that fetch had a chance to settle. Once shown, a later re-run
  // with editMetaId now populated can't retract an already-open dialog.
  useEffect(() => {
    if (!visitId || editMetaId || practiceMode || !metadataLoaded) return;
    // Housekeeping: purge expired drafts
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k?.startsWith(DRAFT_PREFIX)) {
          try { const d = JSON.parse(localStorage.getItem(k)); if (!d?.savedAt || Date.now() - d.savedAt > DRAFT_MAX_AGE) localStorage.removeItem(k); }
          catch { localStorage.removeItem(k); }
        }
      }
    } catch { /* ignore */ }
    // If visit is completed, the prescription is already saved — discard any stale draft
    if (visitStatus === 'COMPLETED') {
      try { localStorage.removeItem(getDraftKey(visitId)); } catch { /* */ }
      return;
    }
    try {
      const raw = localStorage.getItem(getDraftKey(visitId));
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft?.savedAt || Date.now() - draft.savedAt > DRAFT_MAX_AGE) { localStorage.removeItem(getDraftKey(visitId)); return; }
      const hasMeds = draft.medicines?.length > 0;
      const hasVit = draft.vitalValues && Object.values(draft.vitalValues).some(v => v !== '' && v != null);
      const hasSym = draft.selectedSymptoms?.length > 0;
      const hasNt = draft.clinicalNotes?.trim() || draft.doctorNotes?.trim() || draft.diagnosis?.trim();
      const hasInv = draft.investigations?.length > 0;
      const hasSurg = draft.advisedSurgeries?.length > 0;
      if (hasMeds || hasVit || hasSym || hasNt || hasInv || hasSurg) {
        const mins = Math.round((Date.now() - draft.savedAt) / 60000);
        const tl = mins < 60 ? `${mins}m ago` : `${Math.round(mins / 60)}h ago`;
        // Build a summary for the dialog
        const summaryParts = [];
        if (draft.medicines?.length) summaryParts.push(`${draft.medicines.length} medicine${draft.medicines.length > 1 ? 's' : ''}`);
        if (hasVit) summaryParts.push('vitals');
        if (hasSym) summaryParts.push(`${draft.selectedSymptoms.length} symptom${draft.selectedSymptoms.length > 1 ? 's' : ''}`);
        if (hasNt) summaryParts.push('notes');
        if (hasInv) summaryParts.push(`${draft.investigations.length} test${draft.investigations.length > 1 ? 's' : ''}`);
        // FE-6 — same grammar as the counted parts above it ("2 medicines",
        // "3 tests"); this whole summary line is the pad's own long-standing
        // wording, not a new PO Copy-list string.
        if (hasSurg) summaryParts.push(`${draft.advisedSurgeries.length} surger${draft.advisedSurgeries.length > 1 ? 'ies' : 'y'}`);
        const summary = summaryParts.join(', ');
        setDialog({
          title: 'Restore unsaved work?',
          message: `Found unsaved data from ${tl}: ${summary}`,
          confirmLabel: 'Restore',
          cancelLabel: 'Discard',
          onConfirm: () => {
            if (draft.medicines) setMedicines(draft.medicines);
            if (draft.doctorNotes) setDoctorNotes(draft.doctorNotes);
            if (draft.followUpRequired) setFollowUpRequired(draft.followUpRequired);
            if (draft.followUpDate) setFollowUpDate(draft.followUpDate);
            if (draft.investigations) setInvestigations(draft.investigations);
            if (draft.advisedSurgeries) setAdvisedSurgeries(draft.advisedSurgeries);
            if (draft.vitalValues) setVitalValues(draft.vitalValues);
            if (draft.activeVitalKeys) setActiveVitalKeys(draft.activeVitalKeys);
            if (draft.customVitals) setCustomVitals(draft.customVitals);
            if (draft.selectedSymptoms) setSelectedSymptoms(new Set(draft.selectedSymptoms));
            if (draft.customSymptoms) setCustomSymptoms(draft.customSymptoms);
            if (draft.clinicalNotes) setClinicalNotes(draft.clinicalNotes);
            if (draft.chiefComplaint) setChiefComplaint(draft.chiefComplaint);
            if (draft.allergies) setAllergies(draft.allergies);
            if (draft.diagnosis) setDiagnosis(draft.diagnosis);
            setDraftRestored(true); setTimeout(() => setDraftRestored(false), 3000);
            setDialog(null);
          },
          onCancel: () => { localStorage.removeItem(getDraftKey(visitId)); setDialog(null); },
        });
      }
    } catch { /* ignore */ }
  }, [visitId, editMetaId, practiceMode, visitStatus, metadataLoaded]);

  const clearDraft = useCallback(() => {
    try { localStorage.removeItem(getDraftKey(visitId)); } catch { /* */ }
  }, [visitId]);

  // ── BR1 — browse the org's medicine list on an EMPTY box ──────────
  // The quick-add pills above the input are the doctor's own LEARNED history,
  // so a doctor who has not prescribed here yet sees nothing. This list is the
  // ORGANISATION'S UPLOADED medicine list instead — different population, so
  // the two surfaces do not repeat each other.
  //
  // Contract (feat/rx-r3-be-browse): the same GET /prescription/suggest with
  // NO `q` param returns the same envelope, `medicines[]` carrying the same
  // 14-key master rows, alphabetical. So the rows go through the SAME
  // buildMasterMedicineRows → medicineArgFromRow → buildMedicineRow path as a
  // typed search — no second fill rule, and no lab rows by construction.
  //
  // REQUEST DISCIPLINE: fetched at most ONCE per mount. The ref holds the
  // PROMISE, so two focuses racing before it resolves share one request
  // (single-flight), and every later focus reuses the resolved value —
  // focus/blur/focus costs one request, not one per focus.
  //
  // T2b — this SAME ref is now the browse-ALL master cache: it requests
  // `limit=BROWSE_ALL_LIMIT` (additive; an old server without `limit`
  // support just ignores the extra param and returns its usual 8) and reads
  // `master_total`. `updateSuggestions` (typed search, below) reads the
  // synchronous snapshot in `masterCacheResolvedRef` to filter the cached
  // rows client-side on every keystroke — no request per keystroke, no
  // second fetch. Absent `master_total` (old server) → `masterTotal` stays
  // null and every surface silently falls back to today's behaviour.
  const browseRowsRef = useRef(null);          // Promise<{raw, rows, masterTotal}> | null
  const masterCacheResolvedRef = useRef(null); // synchronous snapshot, set once the promise above settles
  const [browseError, setBrowseError] = useState(false);   // bk-927 — load failed
  const [masterTotal, setMasterTotal] = useState(null);    // org's non-deleted master count, when the server sends it

  const ensureMasterCache = useCallback(() => {
    if (!browseRowsRef.current) {
      // `raw: null` marks a load failure. Cached like a success — one
      // attempt per mount, never auto-retried on refocus/blur — only the
      // "Try again" button (bk-927) clears the ref to try again.
      browseRowsRef.current = fetchPrescriptionSuggestions(undefined, { limit: BROWSE_ALL_LIMIT })
        .then((data) => {
          const result = {
            raw: data?.medicines || [],
            rows: buildMasterMedicineRows(data?.medicines),
            masterTotal: typeof data?.master_total === 'number' ? data.master_total : null,
          };
          masterCacheResolvedRef.current = result;
          return result;
        })
        .catch(() => {
          const result = { raw: null, rows: null, masterTotal: null };
          masterCacheResolvedRef.current = result;
          return result;
        });
    }
    return browseRowsRef.current;
  }, []);

  const showBrowseList = useCallback(async () => {
    // FE-28 — a warning is on screen and the doctor has not touched the box
    // since: do not paint the list over it.
    if (suppressAutoOpenRef.current) return;
    // D25 — the doctor's MEDICINE templates open WITH the list, on plain focus:
    // built here (not only in `updateSuggestions`, which needs 2 typed
    // characters). No query, so this is the category's most-used ≤3 plus
    // `See all n`. Empty category → an empty array → no group, no heading.
    const setRows = buildTemplateSetRows(
      templateSelection, TEMPLATE_CATEGORIES.MEDICINES, 'medicine', '', narrowHost,
    );
    // FE-3B — the doctor's own remembered medicines, ABOVE the master list
    // (owner ruling), same D12 role gate as every other memory group.
    const memGroup = canUseTemplates
      ? memoryFieldRows(refData?.memory, MEMORY_TYPES.RX, '', {})
      : { rows: [], total: 0, matched: 0 };
    const memHeading = memGroup.rows.length > 0
      ? memoryGroupHeading(MEMORY_TYPES.RX, refData?.memory?.owner)
      : null;
    const memRows = memHeading ? buildRxMemoryRows(memGroup, memHeading, '') : [];
    const result = await ensureMasterCache();
    // The doctor may have started typing while this was in flight — never
    // stomp a filtered list with the unfiltered one.
    if (inputRef.current?.value.trim()) return;
    if (result.rows === null) {
      setBrowseError(true);
      setSuggestions([]);
      setMasterTotal(null);
      setShowSuggestions(true);
      setSelectedSuggIdx(-1);
      return;
    }
    setBrowseError(false);
    setMasterTotal(result.masterTotal);
    // Owner ruling — no duplicates anywhere: a master row that collides with
    // a memory row (same code, else same medicine-normalised name) is
    // dropped; memory wins. FE-14 — code checked first via `getSystemCode`;
    // `normaliseMedicineRowKey` folds "Dolo-650"/"Dolo 650mg" spellings.
    const dedupedMasterRows = dedupeSystemRows(result.rows, memGroup.rows, (r) => r.label, {
      getSystemCode: (r) => r.code, normaliseKey: normaliseMedicineRowKey,
    });
    setSuggestions([...setRows, ...memRows, ...dedupedMasterRows]);
    // Always open — an empty org's list now renders the ratified bk-887
    // sentence instead of a silent void.
    setShowSuggestions(true);
    // D25 — NOTHING is highlighted on open. In this box `Enter` with no
    // highlight means "parse what I typed" (the shorthand path), so
    // auto-highlighting any row — template or medicine — would silently
    // re-point Enter.
    setSelectedSuggIdx(-1);
  }, [ensureMasterCache, templateSelection, narrowHost, canUseTemplates, refData]);

  const retryBrowseList = useCallback(() => {
    browseRowsRef.current = null;
    masterCacheResolvedRef.current = null;
    setBrowseError(false);
    showBrowseList();
  }, [showBrowseList]);

  // ── AUTOCOMPLETE (C1/C2 — curated only: templates + medicine master +
  // ★ Your pick (`memory.types.RX`, FE-10 — the ONE derivation). No
  // fuzzy/alias data, no investigations.) An unmatched word can legitimately
  // yield nothing (C4) — that's rendered as no dropdown at all, not an empty
  // shell.
  // LC1 — lab-catalogue tests are merged in AFTER the medicines and always
  // sort last, so the top of the list stays exactly as stable as it is today.
  // The two lists are kept separate on purpose: medicines keep their own cap
  // of 10 and their own name dedupe, tests get their own cap of 5. A test that
  // shares a name with a medicine therefore still shows as both — they are
  // different things and pick differently (a medicine row, vs a Tests chip).
  //
  // T2b — the master portion is no longer a 300ms-debounced server round
  // trip: once `ensureMasterCache` has resolved (warmed on the FIRST focus,
  // same as browse), every keystroke filters the cached rows in memory and
  // shows them in the SAME synchronous pass as templates/labs — no request
  // per keystroke. FE-10 — the debounced `/prescription/suggest` call no
  // longer feeds "★ Your pick" (that is `memory.types.RX` now, the ONE
  // derivation); it keeps running ONLY as a master-row fallback for when the
  // cache never warmed (old server, or the browse-all fetch failed) — so
  // master matches never silently vanish. */
  const updateSuggestions = useCallback((text) => {
    // R3 — backspacing the box back to empty returns to the SAME browse-all
    // list shown on focus, not a closed picker (previously `setShowSuggestions(false)`
    // here, so the only recovery was blur + refocus). Reuses the single-flight
    // `browseRowsRef` cache — no new request. bk-937 — also cancel any
    // in-flight debounced search from before the box was cleared, else its
    // callback lands after us and re-opens the popover on the empty box with
    // a stale count line.
    if (!text.trim()) {
      if (aiTimerRef.current) { clearTimeout(aiTimerRef.current); aiTimerRef.current = null; }
      showBrowseList();
      return;
    }
    const textLower = text.trim().toLowerCase();
    const tokens = textLower.split(/\s+/);
    const lastToken = tokens[tokens.length - 1];
    if (lastToken.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }

    // Warm the cache the moment typing starts — harmless if already
    // warm/in-flight (single-flight, same ref browse-on-focus uses).
    ensureMasterCache();

    // Only MEDICINES + whole-prescription templates may contribute a single
    // medicine row — a symptom or lab template must never leak into the
    // medicine dropdown.
    const templateMatches = buildTemplateMedicineMatches(medicineSourceTemplates, lastToken);
    const labRows = buildLabCatalogRows(labCatalog, lastToken);
    // D10 whole-set rows: Medicines templates above the medicine matches, Lab
    // templates above the plain test matches.
    const medSetRows = buildTemplateSetRows(templateSelection, TEMPLATE_CATEGORIES.MEDICINES, 'medicine', lastToken, narrowHost);
    const labSetRows = buildTemplateSetRows(templateSelection, TEMPLATE_CATEGORIES.LAB_TESTS, 'lab', lastToken, narrowHost);
    const cache = masterCacheResolvedRef.current;
    // R20 contract reconcile — the cache is AUTHORITATIVE only when it holds
    // the WHOLE master. `master_total` is the org's TRUE non-deleted count and
    // can legitimately exceed the `limit=500` slice we cached (backend caps at
    // le=500, indexes up to 5000). Treating a PARTIAL cache as authoritative
    // would hide every master row past the 500th from the typed search — rows
    // the server search would still have found — so a partial cache degrades
    // to the server's own master rows, i.e. exactly pre-T2b behaviour.
    // `masterTotal` null (old server) is NOT partial: `null > n` is false.
    const cacheWarm = !!cache?.raw && !(cache.masterTotal > cache.raw.length);
    const cachedMasterRows = cacheWarm
      ? buildMasterMedicineRows(filterCachedMasterRows(cache.raw, lastToken))
      : [];
    // FE-3B — the doctor's own remembered medicines, ABOVE the master/parser
    // rows (owner ruling), filtered on the SAME `lastToken` the master cache
    // and templates already filter on. Same D12 role gate as every field's
    // memory group.
    const memGroup = canUseTemplates
      ? memoryFieldRows(refData?.memory, MEMORY_TYPES.RX, lastToken, {})
      : { rows: [], total: 0, matched: 0 };
    const memHeading = memGroup.rows.length > 0
      ? memoryGroupHeading(MEMORY_TYPES.RX, refData?.memory?.owner)
      : null;
    const memRows = memHeading ? buildRxMemoryRows(memGroup, memHeading, lastToken) : [];
    // Owner ruling — no duplicates anywhere: a parser-alias/master/★-pick row
    // that collides with a memory row (same code, else same medicine-normalised
    // name) is dropped here; memory wins. FE-14 — code first, then
    // `normaliseMedicineRowKey` (hyphens/dose-unit-insensitive).
    const curated = dedupeSystemRows(
      mergeCuratedMedicineRows([templateMatches, cachedMasterRows]), memGroup.rows, (r) => r.label,
      { getSystemCode: (r) => r.code, normaliseKey: normaliseMedicineRowKey },
    ).slice(0, 10);
    const instant = [
      ...medSetRows,
      ...memRows,
      ...curated,
      ...labSetRows,
      ...labRows,
    ];
    setSuggestions(instant);
    // A warm cache is AUTHORITATIVE for master matches — no need to wait on
    // the debounce to know there's nothing, so open immediately (even at
    // zero, to show the no-match sentence). Without a warm cache, stay
    // closed at zero until the debounced fetch below confirms — otherwise a
    // miss flashes before the master rows have even arrived.
    setShowSuggestions(instant.length > 0 || cacheWarm);
    setSelectedSuggIdx(-1);
    // Count line only when the cache it counts against is the whole master —
    // "8 of 2000 medicines match" off a 500-row slice would be a false total.
    setMasterTotal(cacheWarm ? (cache?.masterTotal ?? null) : null);

    // Debounced: FE-10 — this call no longer supplies "★ Your pick" (that
    // is `memGroup`/`memRows`, computed above, unaffected by this fetch); it
    // is kept ONLY as the master-row fallback for a cache that never warmed
    // (S10 AC2). Re-merge with the template matches so neither disappears
    // once the API result lands.
    if (aiTimerRef.current) clearTimeout(aiTimerRef.current);
    aiTimerRef.current = setTimeout(async () => {
      try {
        const data = await fetchPrescriptionSuggestions(lastToken);
        if (!data) return;
        // The cache (if warm) already supplied the authoritative master
        // matches above — this response's own master rows are only used as
        // a fallback when the cache never warmed.
        const masterRows = cacheWarm
          ? buildMasterMedicineRows(filterCachedMasterRows(masterCacheResolvedRef.current?.raw || [], lastToken))
          : buildMasterMedicineRows(data.medicines);
        // Priority: templates first, then the master — then memory-dedupe,
        // same rule as the instant pass above.
        const merged = dedupeSystemRows(
          mergeCuratedMedicineRows([templateMatches, masterRows]), memGroup.rows, (r) => r.label,
          { getSystemCode: (r) => r.code, normaliseKey: normaliseMedicineRowKey },
        );
        setSuggestions([...medSetRows, ...memRows, ...merged.slice(0, 10), ...labSetRows, ...labRows]);
        // T2a — open the popover even at zero results: this is the
        // AUTHORITATIVE (post-fetch) answer, so a true miss now renders the
        // ratified "No match" sentence instead of just staying closed.
        setShowSuggestions(true);
      } catch { /* keep whatever local matches are already shown */ }
    }, 300);
  }, [medicineSourceTemplates, templateSelection, labCatalog, ensureMasterCache, showBrowseList, narrowHost,
      canUseTemplates, refData]);

  // ── CALLBACKS ─────────────────────────────────────────────────────

  // ── Shared helpers for CC/DX smart suggestions ──────────────────
  /** Extract the active (last) segment from a multi-clause text field */
  const getActiveSegment = useCallback((text) => {
    const parts = text.split(ACTIVE_SEGMENT_SPLIT_RE).map(s => s.trim()).filter(Boolean);
    const active = parts.length > 0 ? parts[parts.length - 1] : text.trim();
    const existing = parts.slice(0, -1);
    return { activeText: active, existingClauses: existing };
  }, []);

  // FE-3B — `normalizeWords`/`clausesOverlap`/`trimSuggestion` (the C/O
  // history-suggestion overlap-trimming trio) are removed here: they had
  // exactly one caller, the retired `/suggest`-driven branch of
  // `updateCcSuggestions` above. `getActiveSegment` (still used by both C/O
  // and Diagnosis) and `ACTIVE_SEGMENT_SPLIT_RE` are unaffected.

  /** Direct-add a fully populated medicine (bypasses parse cycle). Every field
   * lands on an ordinary editable row — no auto-filled value is read-only or
   * styled as doctor-confirmed; that editability is the entire safety margin. */
  const directAddMedicine = useCallback((med) => {
    const row = buildMedicineRow(med);
    setMedicines(prev => [...prev, row]);
    // FE-28 — the row the doctor just added carries a warning or a blank note:
    // the panel must not cover it. ONE derivation (`rowNotices`), the same one
    // the row strip and the print confirmation read.
    const { warnings, blankNote } = rowNotices(row);
    if (warnings.length > 0 || blankNote) flagRowWarning();
  }, [flagRowWarning]);

  /** Fill a row from a 14-key medicine-master row the doctor PICKED (the
   * duplicate-name case). Runs the same master → suggestion → row pipeline the
   * dropdown pick uses (buildMasterMedicineRows → medicineArgFromRow →
   * buildMedicineRow), so a picked candidate can never fill differently from a
   * picked dropdown row. Only blanks are filled — typed always wins — and the
   * ambiguity (candidate list + its warning) clears once it's resolved.
   *
   * IDENTITY IS NOT A BLANK. On the ambiguous case the backend puts what the
   * doctor TYPED in `medicine` (`result["medicine"] = master_typed`) so his
   * characters are never dropped off screen — so `medicine` arrives non-blank
   * and `fillBlanks` would keep the typed "amoxil" after he picked
   * "Amoxil · 625 mg". A pick is an explicit identification of ONE master
   * row, exactly like a dropdown pick, and a dropdown pick always writes the
   * row's own `name` and marks it resolved. Both are set here, after the
   * blank-fill, so the script prints the master's name. `medicine_input`
   * deliberately stays as typed — it is the record of what he wrote. */
  const fillFromMasterCandidate = useCallback((idx, masterMedicine) => {
    const [suggestion] = buildMasterMedicineRows([masterMedicine]);
    if (!suggestion) return;
    const filled = buildMedicineRow(medicineArgFromRow(suggestion));
    setMedicines(prev => prev.map((m, i) => (i === idx ? {
      ...fillBlanks(m, filled),
      medicine: filled.medicine,
      is_resolved: true,
      medicine_source: 'master',
      code: filled.code,
      medicine_candidates: null,
      _warnings: (m._warnings || []).filter(w => w !== MULTI_MASTER_MATCH_WARNING),
    } : m)));
  }, []);

  // ── Templates: ADD (merge) — the default path everywhere (D6/D7/D14) ──
  // Chip click, "Add N templates" from the list and a field suggestion all land
  // here. Nothing is ever cleared; `Replace with selected` (handleApplyTemplate
  // below, from inside the list only) stays the one destructive path.
  const showTemplateToast = useCallback((message, canUndo) => {
    if (templateToastTimerRef.current) clearTimeout(templateToastTimerRef.current);
    setTemplateToast({ message, canUndo });
    templateToastTimerRef.current = setTimeout(() => setTemplateToast(null), 10000);
  }, []);
  useEffect(() => () => clearTimeout(templateToastTimerRef.current), []);

  const applyTemplates = useCallback((templates) => {
    const list = (templates || []).filter(Boolean);
    if (list.length === 0) return;
    pushUndo();
    const { next, summaries } = mergeTemplates({
      medicines, investigations,
      symptoms: [...selectedSymptoms], customSymptoms,
      advisedSurgeries: [...advisedSurgeries],
      diagnosis, chiefComplaint, doctorNotes,
      followUpRequired, followUpIn: null,
    }, list, { normalizeLabName, knownSymptoms });

    setMedicines(next.medicines);
    setInvestigations(next.investigations);
    setSelectedSymptoms(new Set(next.symptoms));
    setCustomSymptoms(next.customSymptoms);
    setAdvisedSurgeries(next.advisedSurgeries);
    setDiagnosis(next.diagnosis);
    setChiefComplaint(next.chiefComplaint);
    setDoctorNotes(next.doctorNotes);
    if (next.followUpRequired && !followUpRequired) {
      setFollowUpRequired(true);
      const d = resolveFollowUpDate(next.followUpIn);
      if (d) setFollowUpDate(d);
    }
    setEditingIdx(null);
    setError(null);
    showTemplateToast(buildAddToastMessage(summaries), true);

    // D11 — every apply counts, exactly ONE POST per template per click. The
    // response carries the incremented row, which is merged into the list state
    // so the most-used ordering stays honest with NO refetch. A usage failure
    // never surfaces to the doctor and never blocks the merge above.
    for (const t of list) {
      applyPrescriptionTemplate(t.template_id)
        .then((updated) => {
          if (!updated?.template_id) return;
          setSavedTemplates((prev) => prev.map((x) => (x.template_id === updated.template_id ? updated : x)));
        })
        .catch(() => {});
    }
  }, [pushUndo, medicines, investigations, selectedSymptoms, customSymptoms, advisedSurgeries, diagnosis,
      chiefComplaint, doctorNotes, followUpRequired, knownSymptoms, showTemplateToast]);

  // Every row's "All ▾ · n" opens the SAME list, pre-filtered to that row's
  // category and anchored to the opener that was clicked (the list is portalled,
  // so the anchor rect is how it stays next to its row).
  const handleOpenTemplateList = useCallback((category, anchorEl, query = '') => {
    const key = category || ALL_CATEGORIES;
    // The opener TOGGLES: clicking the row's "All ▾ · n" again closes the list
    // instead of re-opening it (the popover's own outside-click handler
    // deliberately ignores the opener, so this is the only thing deciding).
    setTemplateListRequest((prev) => (prev && prev.category === key ? null : {
      category: key,
      rect: anchorEl?.getBoundingClientRect ? anchorEl.getBoundingClientRect() : null,
      el: anchorEl || null,
      // bk-988 — opened from a TYPED state: the list starts on the same text,
      // so the doctor never retypes what he just typed.
      query: String(query || '').trim(),
    }));
  }, []);

  // D41/S17 — ONE double-click handler for all four field inputs. Opens the
  // All-templates list pre-filtered to that field's category, and closes the
  // on-focus panel the first click opened.
  //
  // Hard limits, all in this one place so no field can drift from them:
  //  - ONLY on an EMPTY input. With any text the handler does not run at all,
  //    so the browser's own word-selection is untouched (the D39 gate's
  //    failure mode).
  //  - fine pointer only — a double-tap is not a gesture this pad claims, so
  //    touch and tablet behaviour is unchanged. The keyboard/touch equivalent
  //    is `See all n` (FE-29), which is always there.
  //  - rows, chips, the strip and the list carry NO double-click behaviour at
  //    all, so a double-click can never add twice.
  const finePointer = useFinePointer();
  const openListOnDoubleClick = useCallback((category, closePanel) => (e) => {
    if (!finePointer) return;
    const el = e.currentTarget;
    if (String(el.value ?? '') !== '') return;      // typed text: hands off
    e.preventDefault();                              // …and nothing gets selected
    closePanel?.();
    handleOpenTemplateList(category, el, '');
  }, [finePointer, handleOpenTemplateList]);
  // The hint rides each field's EXISTING hint line, and only while the shortcut
  // is actually available.
  const showDoubleClickHint = useCallback(
    (value) => finePointer && String(value ?? '') === '',
    [finePointer],
  );

  // ── Templates: SAVE from a section (D18) ──────────────────────────────
  // ONE snapshot of what is on the pad: it prefills the save form AND decides
  // whether each row's `Save…` is offered (and with which title). Deriving the
  // button's promise from the same object the form is filled from is what stops
  // the two disagreeing.
  const padSnapshot = useMemo(() => ({
    medicines,
    symptoms: [...selectedSymptoms],
    investigations: printableInvestigations,
    advisedSurgeries: [...advisedSurgeries],
    diagnosis,
    chiefComplaint,
    doctorNotes,
    followUpRequired,
    followUpIn: followUpDate || null,
  }), [medicines, selectedSymptoms, printableInvestigations, advisedSurgeries, diagnosis,
       chiefComplaint, doctorNotes, followUpRequired, followUpDate]);

  // ── P1 — THE spacing scale. One value between sections, one inside a
  // section; no ad-hoc margin/gap literals in the pad's layout any more.
  const space = { section: '0.85rem', item: '0.3rem' };

  // ── P7 — two columns on a wide host. MEASURED (the pad is mounted in a
  // dashboard column, a modal and a narrow accordion — the viewport says
  // nothing useful), same pattern as the strip's narrow measure: one listener,
  // no observer loop, and a width of 0 (jsdom / first paint) is never "wide".
  // P8: a read-only prescription and print stay single column whatever the
  // screen does.
  const padRef = useRef(null);
  const [wideHost, setWideHost] = useState(false);
  useLayoutEffect(() => {
    const measure = () => {
      const w = padRef.current?.getBoundingClientRect().width || 0;
      setWideHost((prev) => {
        const next = w >= TWO_COLUMN_PX;
        return prev === next ? prev : next;
      });
      setNarrowHost((prev) => {
        // 0 means "not laid out" (jsdom, first paint) and is never narrow.
        const next = w > 0 && w < NARROW_HOST_PX;
        return prev === next ? prev : next;
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const padReadOnly = String(visitStatus || '').toUpperCase() === 'COMPLETED';
  const twoColumn = wideHost && !padReadOnly;

  // The section's existing (i) tooltip explains the template group now sitting
  // on its header line (D18) — but only for a role that actually has the
  // controls (D12: hidden, and never announced, for everyone else).
  const sectionTip = useCallback(
    (base) => (canUseTemplates ? `${base}. ${TEMPLATE_ROW_COPY.sectionTooltipSentence}` : base),
    [canUseTemplates]
  );

  // `Save…` on a section header opens the SAME save form with that section's
  // category preselected — the doctor never re-answers a question they already
  // answered by where they tapped. The top row saves the whole prescription.
  const openTemplateSaveForm = useCallback((category) => {
    setTemplateListRequest(null);
    setEditingTemplate(null);
    // Anything that is not one of the four real categories (the top row's
    // WHOLE_PRESCRIPTION, the list's "ALL" filter) saves the whole prescription.
    setSaveFormCategory(TEMPLATE_CATEGORIES[category] ? category : '');
    setShowTemplateSaveForm(true);
  }, []);

  // ── Templates: DELETE (D16) ───────────────────────────────────────────
  // Confirmed inside the list (shared ConfirmationModal). On success the row
  // just leaves list state — the chip rows, their counts, the list and the
  // field suggestions all re-derive from `savedTemplates`, with NO refetch.
  // Nothing written on the pad is read or touched here.
  const deleteTemplate = useCallback(async (template) => {
    if (!template?.template_id) return;
    try {
      await deletePrescriptionTemplate(template.template_id);
      setSavedTemplates((prev) => prev.filter((t) => t.template_id !== template.template_id));
      showTemplateToast(TEMPLATE_DELETE_COPY.successToast(template.name), false);
    } catch {
      // The row stays exactly where it was.
      showTemplateToast(TEMPLATE_DELETE_COPY.failureToast, false);
    }
  }, [showTemplateToast]);

  const undoTemplateAdd = useCallback(() => {
    popUndo();
    showTemplateToast('Reverted.', false);
  }, [popUndo, showTemplateToast]);

  const applySuggestion = useCallback((s, openerEl = null) => {
    // bk-988/989 — `See all n`, reached by click or by ↓ + Enter: the list
    // opens pre-filtered to the category AND pre-loaded with what is typed, so
    // the templates the 3-row cap hides are one keystroke away.
    if (s.source === 'template_see_all') {
      handleOpenTemplateList(s.category, openerEl, rawInput.trim());
      setShowSuggestions(false); setSelectedSuggIdx(-1);
      return;
    }
    // D10 — a whole-set row (Medicines / Lab tests template). Enter or click
    // ADDS every item in it by the merge rules; it never replaces, and a lab
    // template only writes test NAMES (no order is placed).
    if (s.source === 'template_set') {
      applyTemplates([s.template]);
      setRawInput(''); setShowSuggestions(false); focusInputSoon(); return;
    }
    // LC2 — lab-catalogue pick: writes the catalogue NAME as plain text onto the
    // Tests row and nothing else. It never creates a lab order — reception
    // raises those. `labAdvise` is deliberately not referenced in this branch,
    // and the row carries no procedure_id to advise with in the first place.
    // Dedupe uses `printableInvestigations` — the SINGLE projection of "the
    // tests on this visit" (live order names ∪ free-text strings), so a test
    // already typed by the doctor OR already ordered from the Tests row can't
    // come back as a second chip. `prev` is re-checked inside the updater so
    // two fast clicks on the same row still add one.
    if (s.source === 'lab_catalog') {
      const key = normalizeLabName(s.label);
      const alreadyOnVisit = printableInvestigations.some((n) => normalizeLabName(n) === key);
      setInvestigations((prev) => (
        alreadyOnVisit || prev.some((n) => normalizeLabName(n) === key) ? prev : [...prev, s.label]
      ));
      setRawInput(''); setShowSuggestions(false); focusInputSoon(); return;
    }
    // Template / medicine-master / FE-3B memory pick — AUTO-FILL: direct-add
    // straight onto the row with everything the source carries (memory's
    // TOP-ranked variant, per `buildRxMemoryRows`); the doctor edits from
    // there, exactly like every other auto-filled row (never locked).
    if (s.source === 'template' || s.source === 'master' || s.source === 'doctor_memory') {
      directAddMedicine(medicineArgFromRow(s));
      setRawInput(''); setShowSuggestions(false); focusInputSoon(); return;
    }
    const toks = rawInput.trim().split(/\s+/); toks[toks.length - 1] = s.shorthand || s.label;
    setRawInput(toks.join(' ') + ' '); setShowSuggestions(false); focusInputSoon();
  }, [rawInput, directAddMedicine, printableInvestigations, focusInputSoon, applyTemplates,
      handleOpenTemplateList]);

  // ── Smart Chief Complaint autocomplete ────────────────────────
  // FE-3B — same pattern as Diagnosis (FE-3): opens on focus, the ≥2-char and
  // ≥3-word gates are GONE, and the doctor's own MEMORY group is the whole
  // dropdown (C/O has no template category — `TEMPLATE_CATEGORIES` carries
  // none, build-plan §2 G6 — so there is no template group to sit under
  // here). The debounced `/suggest` history round trip (★ Your pick) this
  // field used per keystroke is RETIRED here, same as Diagnosis (S10 AC1) —
  // zero requests per keystroke.
  const updateCcSuggestions = useCallback((text) => {
    const raw = String(text || '');
    const { activeText } = getActiveSegment(raw);
    // QA D-2 — `getActiveSegment` (shared with Diagnosis, unchanged) drops
    // the trailing empty part after a comma, so "fever, " kept resolving to
    // activeText "fever" and the list stayed filtered instead of re-opening
    // (S1 AC4, architect-design.md §3's empty-query order). C/O splits on
    // COMMA ONLY (`splitMemoryClauses`'s own rule) — a trailing comma (plus
    // optional whitespace) means the doctor has closed the last clause and
    // is starting the next one, so the query goes empty. Diagnosis is not
    // touched: it calls `getActiveSegment` directly, never this override.
    const ccActiveText = /,\s*$/.test(raw) ? '' : activeText;
    // co-memory-backfill — individual values + whole lines, ranked per
    // architect-design.md §3 (`coMemoryRows`, the ONE place this ranking
    // happens). A row's `shape`/`src` marker is a SEPARATE render field,
    // never folded into `name` — `applyCcSuggestion` still inserts `s.name`
    // verbatim.
    const memGroup = canUseTemplates
      ? coMemoryRows(refData?.memory, ccActiveText, { alreadySelectedText: text })
      : { rows: [], total: 0, matched: 0 };
    const memHeading = memGroup.rows.length > 0
      ? memoryGroupHeading(MEMORY_TYPES.CO, refData?.memory?.owner)
      : null;
    const memRows = memHeading
      ? memGroup.rows.map((r, i) => ({
        name: r.v, source: 'doctor_memory', row: r,
        chip: r.shape === 'typed' ? { label: CO_CHIP_LABEL, title: CO_CHIP_TITLE } : null,
        startsGroup: i === 0, groupHeading: memHeading,
        countLine: i === 0 ? memoryCountLine(memGroup.total, memGroup.matched, ccActiveText) : undefined,
      }))
      : [];
    setCcHighlight(-1);
    setCcSuggestions(memRows);
    setShowCcSuggestions(memRows.length > 0);
  }, [getActiveSegment, canUseTemplates, refData]);

  // D25/F4's pattern, mirrored for C/O — one place picks a memory row,
  // whether clicked or reached with ↓ and Enter.
  const applyCcSuggestion = useCallback((s) => {
    if (!s) return;
    const insertText = s.name;
    ccUsedRef.current.add(String(s.name || '').toLowerCase());
    // Item 2 fix — REPLACE the active segment the query was computed from
    // (same `ACTIVE_SEGMENT_SPLIT_RE` `updateCcSuggestions` filters on),
    // never append the pick alongside the typed fragment.
    setChiefComplaint(prev => replaceActiveSegment(prev, insertText, ACTIVE_SEGMENT_SPLIT_RE));
    setShowCcSuggestions(false);
    setCcHighlight(-1);
  }, []);

  // ── Smart Diagnosis autocomplete ──────────────────────────────
  // D25/F4/FE-3 — this dropdown OPENS ON FOCUS (the 2-character gate is gone)
  // and has a keyboard path. FE-3 (S10 AC1) retires the debounced
  // `/suggest` history fetch entirely for this field — zero network requests
  // on keystroke — and replaces it with the doctor's own MEMORY group (FE-1's
  // `memoryFieldRows`), rendered UNDER the template group (mockup variant A):
  // template rows → memory rows → (nothing else for Diagnosis). The dropdown
  // IS the full memory list (owner ruling) — no cap, no `See all` for it.
  const updateDxSuggestions = useCallback((text) => {
    const { activeText } = getActiveSegment(String(text || ''));
    // The doctor's own DIAGNOSIS templates, tagged "Saved", above the memory
    // rows — from THE one per-field selector (cap, ordering and `See all n` all
    // come from there). Empty category → no rows → no group, no heading.
    const tplGroup = fieldTemplateRows(
      templateSelection, TEMPLATE_CATEGORIES.DIAGNOSIS, activeText, { narrow: narrowHost },
    );
    const tplRows = tplGroup.rows.map((t, i) => ({
      name: t.diagnosis || t.name, shorthand: '', source: 'template_set',
      template: t, _trimmedName: t.diagnosis || t.name,
      startsGroup: i === 0,
      groupHeading: tplGroup.heading,
    }));
    // bk-988/989 — the opener is the group's LAST entry, present while typing.
    if (tplGroup.seeAll > 0) {
      tplRows.push({
        name: '', shorthand: '', source: 'template_see_all',
        category: TEMPLATE_CATEGORIES.DIAGNOSIS, count: tplGroup.seeAll,
      });
    }
    // D12 role/completed gate, reused for memory chrome (never computed, let
    // alone rendered, for a role that can't see it — hidden, not disabled).
    const dxTemplateTexts = (templateSelection?.byCategory?.[TEMPLATE_CATEGORIES.DIAGNOSIS] || [])
      .map((t) => t.diagnosis || t.name).filter(Boolean);
    const memGroup = canUseTemplates
      ? memoryFieldRows(refData?.memory, MEMORY_TYPES.DX, activeText, {
        templateRows: dxTemplateTexts,
        // (c) owner ruling — never offer a row already present in what the
        // doctor has typed.
        alreadySelectedText: text,
      })
      : { rows: [], total: 0, matched: 0 };
    // Empty memory (for this field, or after template-wins dedupe leaves
    // nothing) ⇒ no heading, no separator, no chrome at all (S2 AC9).
    const memHeading = memGroup.rows.length > 0
      ? memoryGroupHeading(MEMORY_TYPES.DX, refData?.memory?.owner)
      : null;
    const memRows = memHeading
      ? memGroup.rows.map((r, i) => ({
        name: r.v, source: 'doctor_memory', row: r,
        startsGroup: i === 0, groupHeading: memHeading,
        countLine: i === 0 ? memoryCountLine(memGroup.total, memGroup.matched, activeText) : undefined,
      }))
      : [];
    // Nothing is highlighted until the doctor presses ↓ — in every field.
    setDxHighlight(-1);
    setDxSuggestions([...tplRows, ...memRows]);
    setShowDxSuggestions(tplRows.length > 0 || memRows.length > 0);
  }, [getActiveSegment, templateSelection, narrowHost, canUseTemplates, refData]);

  // FE-12 — see `memoryForgetRequery`'s own comment above. Runs AFTER
  // `refData` has committed the forget/undo (both state updates are set in
  // the SAME batch, so this render already carries the fresh closures), so
  // it always re-derives against the CURRENT `memory`, never a stale one.
  useEffect(() => {
    if (!memoryForgetRequery) return;
    if (memoryForgetRequery.type === MEMORY_TYPES.DX) updateDxSuggestions(diagnosis);
    else if (memoryForgetRequery.type === MEMORY_TYPES.CO) updateCcSuggestions(chiefComplaint);
    else if (memoryForgetRequery.type === MEMORY_TYPES.RX) {
      if (rawInput.trim()) updateSuggestions(rawInput); else showBrowseList();
    }
    setMemoryForgetRequery(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoryForgetRequery]);

  // D25/F4 — one place picks a diagnosis row, whether it was clicked or reached
  // with ↓ and Enter. A saved DIAGNOSIS template merges by the D7 rules (set if
  // empty, else append) and counts as an apply — never a silent overwrite of
  // what the doctor typed.
  const applyDxSuggestion = useCallback((s, openerEl = null) => {
    if (!s) return;
    if (s.source === 'template_see_all') {
      handleOpenTemplateList(s.category, openerEl, diagnosis.trim());
      setShowDxSuggestions(false);
      setDxHighlight(-1);
      return;
    }
    if (s.source === 'template_set') {
      applyTemplates([s.template]);
      setShowDxSuggestions(false);
      setDxHighlight(-1);
      return;
    }
    const insertText = s._trimmedName || s.name;
    dxUsedRef.current.add(String(s.name || '').toLowerCase());
    // Item 2 fix — same `replaceActiveSegment` C/O uses (sibling defect,
    // same `ACTIVE_SEGMENT_SPLIT_RE` `updateDxSuggestions` filters on):
    // replace the active segment, never append the pick alongside it.
    setDiagnosis(prev => replaceActiveSegment(prev, insertText, ACTIVE_SEGMENT_SPLIT_RE));
    setShowDxSuggestions(false);
    setDxHighlight(-1);
  }, [applyTemplates, handleOpenTemplateList, diagnosis]);

  // ── Instruction autocomplete (RX-FORM-BUGS item 1 — reads the memory
  // store, `memory.types.INSTR.rows`, kept current by the `memory_delta`
  // merge and forget ×; the legacy `topInstructions` top-30-by-usage slice
  // is retired — see the removed state and its comment at the old
  // declaration site). Same D12 gate every other memory group uses.
  const updateInstructionSuggestions = useCallback((text) => {
    // Extract the current line being typed (last line of the textarea)
    const lines = text.split('\n');
    const currentLine = (lines[lines.length - 1] || '').trim();
    if (!currentLine || currentLine.length < 2 || !canUseTemplates) {
      setInstructionSuggestions([]); setShowInstructionSugg(false); return;
    }
    const memGroup = memoryFieldRows(refData?.memory, MEMORY_TYPES.INSTR, currentLine, {});
    const currentKey = normaliseRowKey(currentLine);
    const matches = memGroup.rows
      .filter((r) => normaliseRowKey(r.v) !== currentKey)
      .map((r) => r.v)
      .slice(0, 6);
    if (matches.length > 0) {
      setInstructionSuggestions(matches);
      setShowInstructionSugg(true);
    } else {
      setInstructionSuggestions([]); setShowInstructionSugg(false);
    }
  }, [refData, canUseTemplates]);

  // ── Per-medicine note autocomplete (static INSTRUCTION_PHRASES) ───
  const updateMedNoteSuggestions = useCallback((text, medIdx) => {
    if (!text || text.trim().length < 2 || !refData?.instructions) {
      setMedNoteSugg([]); setShowMedNoteSugg(false); setEditingMedNoteIdx(null); return;
    }
    const lower = text.toLowerCase().trim();
    const matches = [];
    const seen = new Set();
    for (const [phrase, fullName] of Object.entries(refData.instructions)) {
      if (phrase.includes(lower) || fullName.toLowerCase().includes(lower)) {
        const key = fullName.toLowerCase();
        if (!seen.has(key)) { seen.add(key); matches.push(fullName); }
      }
    }
    if (matches.length > 0) {
      setMedNoteSugg(matches.slice(0, 5));
      setShowMedNoteSugg(true);
      setEditingMedNoteIdx(medIdx);
    } else {
      setMedNoteSugg([]); setShowMedNoteSugg(false); setEditingMedNoteIdx(null);
    }
  }, [refData]);

  const handleParse = useCallback(async () => {
    const text = rawInput.trim(); if (!text) return;
    if (parsing) return; // single-flight: a disabled input already blocks this, belt-and-braces against double-submit
    pushUndo();
    setParsing(true); setError(null);
    let results = [];
    try {
      const data = await parsePrescription(text);
      results = data?.results || [];
    } catch {
      // Swallowed deliberately — an empty `results` array below already
      // synthesizes one unrecognized card carrying the typed text, the
      // same as a wholly-empty (but successful) response.
    }
    try {
      // ── SAFETY: Only add medicines that are type="medicine" (resolved OR
      //    unresolved-with-context).  NEVER add type="unrecognized" as medicine.
      const medResults = [];
      let newInv = [], detFU = false, detFUIn = null;
      // Gather unrecognized items for actionable classification
      const newUnrecognized = [];
      const newInvPicks = [];
      const newInvLikely = [];
      for (const r of results) {
        const isMedicine = r.type === 'medicine' && r.medicine && r.medicine !== 'Unknown';
        const isUnrecognizedWithInput = r.type === 'unrecognized' && r.medicine_input;
        const isInvCandidates = r.type === 'investigation_candidates' && r.investigation_candidates?.length > 0;
        const isInvLikely = r.type === 'investigation_likely' && (r.investigation_input || r.medicine_input);
        const hasInvestigations = r.investigations?.length > 0;
        if (hasInvestigations) newInv.push(...r.investigations);
        if (r.follow_up_required && !detFU) { detFU = true; detFUIn = r.follow_up_in || null; }

        if (isMedicine) {
          medResults.push(r);
        } else if (isInvCandidates) {
          newInvPicks.push({ input: r.medicine_input || r.original_input, candidates: r.investigation_candidates });
        } else if (isInvLikely) {
          newInvLikely.push({ input: r.investigation_input || r.medicine_input });
        } else if (isUnrecognizedWithInput) {
          newUnrecognized.push({ input: r.medicine_input, warning: r.warnings?.[0] || PARSE_FALLBACK_MSG });
        } else if (!hasInvestigations && !r.follow_up_required) {
          // NEW (T-F1): a result the parser could not classify at all — never
          // let the doctor's line vanish. Surface it from the raw typed text.
          const raw = r.original_input || text;
          newUnrecognized.push({ input: raw, warning: r.warnings?.[0] || PARSE_FALLBACK_MSG });
        }
      }
      // A wholly-empty results array (or a failed call) must still not lose
      // the line — surface exactly what was typed as one unrecognized card.
      if (results.length === 0) {
        newUnrecognized.push({ input: text, warning: PARSE_FALLBACK_MSG });
      }
      // Flag results needing doctor review (AI-corrected or unresolved name)
      if (medResults.length > 0) {
        const prevLen = medicines.length; // track where new entries start
        setMedicines(prev => [...prev, ...medResults.map(r => ({
          ...r,
          _id: Date.now() + Math.random(),
          _needsReview: r.needs_review || r.ai_corrected || false,
          _warnings: (r.warnings && r.warnings.length > 0) ? r.warnings : null,
          // ── New parser learning fields ──
          _autoSuggested: r.auto_suggested || false,
          _suggestions: r.suggestions || [],       // [{name, source, priority}]
          _topSuggestion: r.top_suggestion || null,
          _rawInput: text,                          // original typed text for log-edit
          _parsedSnapshot: { ...r },                // snapshot of parsed result for log-edit
        }))]);
        // Auto-open edit panel for the first needs-review medicine so doctor sees it
        const firstNeedsReview = medResults.findIndex(r => r.needs_review || r.ai_corrected);
        if (firstNeedsReview >= 0) setEditingIdx(prevLen + firstNeedsReview);
      }
      if (newInv.length > 0) setInvestigations(prev => { const s = new Set(prev); const m = [...prev]; for (const inv of newInv) { if (!s.has(inv)) { s.add(inv); m.push(inv); } } return m; });
      if (detFU && !followUpRequired) { setFollowUpRequired(true); const d = resolveFollowUpDate(detFUIn); if (d) setFollowUpDate(d); }
      // Show actionable cards for each type
      if (newInvPicks.length > 0) setInvestigationPicks(prev => [...prev, ...newInvPicks]);
      if (newInvLikely.length > 0) setInvestigationLikely(prev => [...prev, ...newInvLikely]);
      if (newUnrecognized.length > 0) setUnrecognizedItems(prev => [...prev, ...newUnrecognized]);
      // FE-28 — anything the doctor has to LOOK at keeps the panel shut: a new
      // row carrying a warning or a blank note, an unrecognised line, or an
      // investigation card. `rowNotices` is the same single derivation the row
      // strip renders from.
      const addedARowWarning = medResults.some((r) => {
        const { warnings, blankNote } = rowNotices({
          ...r, _warnings: (r.warnings && r.warnings.length > 0) ? r.warnings : null,
        });
        return warnings.length > 0 || !!blankNote;
      });
      if (addedARowWarning || newUnrecognized.length > 0
        || newInvPicks.length > 0 || newInvLikely.length > 0) flagRowWarning();
      // Box ALWAYS clears — the unrecognized card above already carries the
      // doctor's typed text AND the failure copy (rendered on the card
      // itself, see item.warning below); no separate toast needed, and a
      // second copy of the same sentence would just duplicate it on screen.
      setRawInput(''); setShowSuggestions(false);
    } finally {
      setParsing(false);
    }
  }, [rawInput, parsing, followUpRequired, medicines.length, pushUndo, flagRowWarning]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleSave(); return; }
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedSuggIdx(p => Math.min(p + 1, suggestions.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedSuggIdx(p => Math.max(p - 1, -1)); return; }
      if (e.key === 'Enter' && selectedSuggIdx >= 0) { e.preventDefault(); applySuggestion(suggestions[selectedSuggIdx]); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleParse(); }
    if (e.key === 'Escape') setShowSuggestions(false);
  };

  // T3-LITE owner addition — no confirmation step: doctors add/remove rows
  // constantly on this pad, and undo (Ctrl+Z, `pushUndo` below) is already the
  // safety margin. Never a native confirm either way (hard rule).
  const removeMedicine = (idx) => {
    pushUndo();
    setMedicines(p => p.filter((_, i) => i !== idx));
    if (editingIdx === idx) setEditingIdx(null);
    else if (editingIdx !== null && editingIdx > idx) setEditingIdx(editingIdx - 1);
  };
  // T3-LITE — toggle the row's editor; the effect below scrolls it into view
  // and focuses its first field the moment it opens.
  const openEditor = useCallback((idx) => {
    setEditingIdx((cur) => (cur === idx ? null : idx));
  }, []);

  // Placement/affordance fix (S4 AC3 minimal answer): the editor can open
  // below the fold on a long list — this puts it on screen and drops the
  // doctor straight into the Medicine field, every time.
  useEffect(() => {
    if (editingIdx === null) return;
    editorRefs.current[editingIdx]?.scrollIntoView({ block: 'nearest' });
    editorFirstFieldRefs.current[editingIdx]?.focus();
  }, [editingIdx]);

  const updateMedicine = (idx, field, value) => setMedicines(p => p.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  // `parsedValue` is `null` when `DoseSlotInput` could not parse the typed
  // text (AC3) — the row's dosage_schedule is left exactly as it was, only
  // that ONE slot's error flag is set; the other two slots and the rest of
  // the row are untouched (I8).
  const updateDosageSlot = (idx, slot, parsedValue) => setMedicines(p => p.map((m, i) => {
    if (i !== idx) return m;
    if (parsedValue === null) {
      return { ...m, _doseErrors: { ...(m._doseErrors || {}), [slot]: true } };
    }
    const sc = m.dosage_schedule || { morning: 0, afternoon: 0, night: 0 };
    const next = { ...sc, [slot]: parsedValue };
    const nextErrors = { ...(m._doseErrors || {}) };
    delete nextErrors[slot];
    // The parser fills `frequency` from the slots ONCE, at parse time; editing
    // the slots afterwards used to leave the old label printing on the script
    // (1-0-1 "Twice Daily", edited to 1-0-0, still said Twice Daily). Same rule,
    // same labels, one place — and a hand-typed frequency is never overwritten.
    return { ...m, dosage_schedule: next, frequency: frequencyForSchedule(next, m.frequency), _doseErrors: nextErrors };
  }));

  // ── Templates: REPLACE — the ONE destructive path, reachable only from
  // "Replace with selected" inside the list, behind the ConfirmationModal.
  // Everything currently written is cleared and the sheet is filled fresh from
  // the ticked templates combined (the merge rules then apply between them, so
  // two templates sharing a medicine still land one row).
  const replaceWithTemplates = useCallback(async (templates) => {
    const list = (templates || []).filter(Boolean);
    if (list.length === 0) return;
    try {
      pushUndo();
      for (const t of list) {
        applyPrescriptionTemplate(t.template_id)
          .then((updated) => {
            if (!updated?.template_id) return;
            setSavedTemplates((prev) => prev.map((x) => (x.template_id === updated.template_id ? updated : x)));
          })
          .catch(() => {});
      }
      const { next } = mergeTemplates({
        medicines: [], investigations: [], symptoms: [], customSymptoms: [], advisedSurgeries: [],
        diagnosis: '', chiefComplaint: '', doctorNotes: '',
        followUpRequired: false, followUpIn: null,
      }, list, { normalizeLabName, knownSymptoms });

      setMedicines(next.medicines);
      setInvestigations(next.investigations);
      setSelectedSymptoms(new Set(next.symptoms));
      setCustomSymptoms(next.customSymptoms);
      setAdvisedSurgeries(next.advisedSurgeries);
      setDoctorNotes(next.doctorNotes);
      setChiefComplaint(next.chiefComplaint);
      setDiagnosis(next.diagnosis);
      setEditingIdx(null);
      if (next.followUpRequired) { setFollowUpRequired(true); const d = resolveFollowUpDate(next.followUpIn); if (d) setFollowUpDate(d); }
      else setFollowUpRequired(false);
      // ── STOP condition (rx-r2, owner ruling "nothing on the Rx screen ever
      // creates a lab order" — reception raises all of them): this specific
      // path is INTENTIONALLY NOT converted to investigation text like the
      // rest of the prescription screen. `template.lab_catalog_ids` is a bare
      // array of catalog procedure_ids (confirmed against how
      // PrescriptionTemplateManager.jsx writes AND reads it — no names are
      // stored on the template). Turning an id into investigation text needs
      // a name lookup, and the only FE mechanism for that (`useProcedureStore`
      // dept-scoped picker) was deliberately removed from THIS file in C3 —
      // re-adding it here would undo that cleanup for one narrow path. Rather
      // than silently dropping a doctor's attached tests (worse than creating
      // the order) or re-introducing the store, this is left AS-IS pending an
      // owner/tech-lead decision: either (a) the backend enriches the
      // template list/apply response with lab test NAMES so this can resolve
      // to investigation text with no FE store dependency, or (b)
      // PrescriptionTemplateManager.jsx (which already holds the procedure
      // catalog for its own UI) is changed to persist a parallel
      // `lab_catalog_names`/similar on save. Flagged in the dev report — do
      // not resolve unilaterally either direction.
      //
      // Lab-aware template: recreate its in-house lab orders (best-effort — a
      // deleted/inactive catalog test just fails its advise; backend dedupe keeps
      // it idempotent). Skipped when the lab module is off / practice mode.
      // ADD never reaches this code (D7) — only Replace does, unchanged.
      const catalogIds = list.flatMap((t) => t.lab_catalog_ids || []);
      if (labActive && catalogIds.length) {
        await Promise.all(catalogIds.map((pid) =>
          labAdvise({
            visit_id: visitId, patient_id: patientId, catalog_procedure_id: pid,
            patient_name: visit?.patient_name || visit?.name || undefined,
            queue_number: visit?.queue_number, expires_at: plusDaysISO(30), standing: false,
          }).catch(() => null)
        ));
        if (visitId) labFetchForVisit(visitId);
      }
      setRawInput(''); setError(null);
    } catch { setError('Failed to apply template'); }
  }, [pushUndo, knownSymptoms, labActive, labAdvise, labFetchForVisit, visitId, patientId, visit]);

  // R1-FE — ONE summary derivation for the chip, tooltip, confirm dialog AND
  // the toast: fixed vocabulary and order (copy.md §3.6, re-ratified for
  // phase 2) — counted medicines/tests/surgeries first, then named Chief
  // complaint / Diagnosis / Notes. Every part here is something
  // `handleApplyPrevRx` actually copies (same truthy checks), so the toast
  // never names a part the previous Rx lacked (guarded copy).
  //
  // OWNER REVERSAL (phase 2): `Allergies` is NOT in this vocabulary any more.
  // The patient record is the store for allergies, so Repeat Last Rx no longer
  // touches that box — and copy that named a part the load does not carry
  // would lie (Business phase-2 ratification rule 3).
  const prevRxSummary = useMemo(() => {
    if (!prevRx) return null;
    const counted = [];
    const nMeds = prevRx.medicines?.length || 0;
    if (nMeds > 0) counted.push(`${nMeds} medicine${nMeds > 1 ? 's' : ''}`);
    const nTests = prevRx.investigations?.length || 0;
    if (nTests > 0) counted.push(`${nTests} test${nTests > 1 ? 's' : ''}`);
    const nSurg = prevRx.advised_surgeries?.length || 0;
    if (nSurg > 0) counted.push(`${nSurg} surger${nSurg > 1 ? 'ies' : 'y'} advised`);
    const named = [];
    if (prevRx.chief_complaint) named.push('Chief complaint');
    if (prevRx.diagnosis) named.push('Diagnosis');
    if (prevRx.doctor_notes) named.push('Notes');
    const parts = [...counted, ...named];
    // date-led everywhere (copy.md — "12 Aug 2026"), IST-safe: never
    // `toISOString().slice(0,10)` (day-boundary trap across the IST/UTC gap).
    const date = formatDateIST(prevRx.visit_date);
    // Chip: counted parts first, capped at 2; a single part reads "X only"
    // (never a bare count, never an empty "()").
    const chipSummary = parts.length === 1 ? `${parts[0]} only` : parts.slice(0, 2).join(', ');
    // Tooltip / dialog / toast: the full list, same order, every part.
    const fullSummary = parts.join(', ');
    return { parts, date, chipSummary, fullSummary };
  }, [prevRx]);

  // Apply previous prescription from an earlier visit
  const handleApplyPrevRx = useCallback(() => {
    if (!prevRx) return;
    pushUndo();
    const ts = Date.now();
    setMedicines((prevRx.medicines || []).map((m, i) => ({
      _id: `prev_${ts}_${i}_${Math.random().toString(36).slice(2, 6)}`,
      medicine: m.medicine || '', medicine_input: m.medicine_input || m.medicine || '',
      strength: m.strength || null, form: m.form || null, dosage_schedule: m.dosage_schedule || null,
      frequency: m.frequency || null, timing: m.timing || null, duration: m.duration || null,
      route: m.route || null, notes: m.notes || null, is_resolved: true,
    })));
    setEditingIdx(null);
    if (prevRx.doctor_notes) setDoctorNotes(prevRx.doctor_notes);
    if (prevRx.chief_complaint) setChiefComplaint(prevRx.chief_complaint);
    if (prevRx.diagnosis) setDiagnosis(prevRx.diagnosis);
    // OWNER REVERSAL (phase 2) — Repeat Last Rx deliberately does NOT touch the
    // Allergies box. An allergy is a patient fact, not a therapeutic
    // instruction: it now lives on the patient record (seeded above), and
    // copying an old prescription's copy of it forward was only ever a
    // workaround for having no patient store.
    if (prevRx.investigations?.length) setInvestigations(prevRx.investigations);
    if (prevRx.advised_surgeries?.length) setAdvisedSurgeries(prevRx.advised_surgeries);
    if (prevRx.follow_up_required) { setFollowUpRequired(true); const d = resolveFollowUpDate(prevRx.follow_up_in); if (d) setFollowUpDate(d); }
    setRawInput(''); setError(null);
    if (prevRxSummary) {
      setSuccessMsg(REPEAT_RX_TOAST({ date: prevRxSummary.date, summary: prevRxSummary.fullSummary }));
      setTimeout(() => setSuccessMsg(null), 5000);
    }
  }, [prevRx, prevRxSummary, pushUndo]);

  // D23 — what the picker offers, in ONE derivation: the doctor's own
  // MEMORY (FE-4 — `memory.types.SYMPTOM`, replacing the old client-learning
  // `symptoms` field) first, then their specialisation's list, then the two
  // commons. De-duplicated ACROSS groups (never offered twice) and narrowed
  // live by whatever is typed in the box at the top.
  //
  // D25 — the doctor's SYMPTOMS templates ARE the symptom sets, so they are the
  // picker's FIRST group (heading from `TEMPLATE_ROW_LABELS`, not a second
  // "My symptom sets" heading), they narrow with the same typed text, and they
  // join `flat` so ↑↓ reach them instead of walking straight past.
  const symptomGroups = useMemo(() => {
    const q = customSymptomInput.trim().toLowerCase();
    const seen = new Set();
    const take = (list) => (list || []).filter((n) => {
      const key = String(n || '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      if (q && !key.includes(q)) return false;
      seen.add(key);
      return true;
    });
    const templates = fieldTemplateRows(
      templateSelection, TEMPLATE_CATEGORIES.SYMPTOMS, customSymptomInput, { narrow: narrowHost },
    );
    // FE-4 — hidden (never disabled) for a role that may not see memory (D12,
    // matching every other field's memory group), unfiltered here: `take()`
    // below does the SAME any-word narrowing every other group gets, and
    // records every name it renders in `seen` so specialisation/common never
    // repeat a name already offered as "Your symptoms" (owner ruling — no
    // duplicates).
    const memGroup = canUseTemplates
      ? memoryFieldRows(refData?.memory, MEMORY_TYPES.SYMPTOM)
      : { rows: [], total: 0, matched: 0 };
    const memNames = memGroup.rows.map((r) => r.v);
    const yours = take(memNames);
    const specialised = take(symptomSuggestions.specialized);
    const common = take(PAD_COMMON_SYMPTOMS);
    return {
      templates, yours, specialised, common,
      // FE-12 — the "yours" chip's own forget × needs the FULL row (`c`/`t`),
      // not just the name `take()` narrowed to; looked up once here rather
      // than a second `memoryFieldRows` call at click time.
      memGroup,
      // The TOTAL memory count (before the typed-query narrowing `take()`
      // applies) — drives the empty-state message and the count line exactly
      // the way `learnedSymptoms.length` used to.
      memTotal: memNames.length,
      // ONE flat sequence for the keyboard: templates first (they are the first
      // group), then every offered name in the order the groups render.
      // bk-989 — …and `See all n` is the LAST entry of that first group, so ↓
      // past the last template row lands on it instead of skipping it.
      flat: [
        ...templates.rows.map((template) => ({ template })),
        ...(templates.seeAll > 0 ? [{ seeAll: templates.seeAll }] : []),
        ...[...yours, ...specialised, ...common].map((name) => ({ name })),
      ],
    };
  }, [customSymptomInput, canUseTemplates, refData, symptomSuggestions, templateSelection, narrowHost]);

  const toggleSymptom = useCallback((sym) => {
    setSelectedSymptoms(prev => { const n = new Set(prev); if (n.has(sym)) n.delete(sym); else n.add(sym); return n; });
  }, []);
  // D23 — ONE entry point: the box at the top of the picker. `name` comes from
  // a highlighted suggestion, or from what the doctor typed. A name that is not
  // one of the known lists is stored as CUSTOM, exactly as before.
  const addTypedSymptom = useCallback((name) => {
    const t = String(name ?? customSymptomInput).trim(); if (!t) return;
    // Already un-selected (removed via its chip's ✕) is re-selected, not
    // refused — only a symptom that's currently selected is a true no-op.
    if (selectedSymptoms.has(t)) { setCustomSymptomInput(''); setSymptomHighlight(-1); return; }
    if (!knownSymptoms.has(t)) setCustomSymptoms(p => (p.includes(t) ? p : [...p, t]));
    setSelectedSymptoms(p => new Set([...p, t]));
    setCustomSymptomInput('');
    setSymptomHighlight(-1);
  }, [customSymptomInput, selectedSymptoms, knownSymptoms]);
  // FE-4 — the lab-OFF Tests strip's ONE entry point: `name` comes from a
  // clicked memory row, or from what the doctor typed. No lab module, no
  // catalogue — just the plain `investigations[]` array every save already
  // used, identical in shape to what the lab-ON picker's `pickFromLabList`
  // appends (parity across the module toggle).
  const addTypedTest = useCallback((name) => {
    const t = String(name ?? testInput).trim();
    if (!t) return;
    setInvestigations((prev) => (
      prev.some((x) => normalizeLabName(x) === normalizeLabName(t)) ? prev : [...prev, t]
    ));
    setTestInput('');
    setShowTestSuggestions(false);
  }, [testInput]);
  // FE-6 — the Advised surgeries strip's ONE entry point: `name` comes from a
  // picked memory row, or from what the doctor typed. Plain text only (Q3) —
  // no procedure order, no OT booking, no billing line. Duplicates are matched
  // on `normaliseRowKey`, the SAME row identity every memory dropdown on this
  // pad uses, so "Appendectomy" and "appendectomy." are never both added.
  const addSurgery = useCallback((name) => {
    const t = String(name ?? surgeryInput).trim();
    if (!t) return;
    setAdvisedSurgeries((prev) => (
      prev.some((x) => normaliseRowKey(x) === normaliseRowKey(t)) ? prev : [...prev, t]
    ));
    setSurgeryInput('');
    setShowSurgerySuggestions(false);
    setSurgeryHighlight(-1);
  }, [surgeryInput]);
  const removeSurgery = useCallback((idx) => {
    setAdvisedSurgeries((prev) => prev.filter((_, i) => i !== idx));
  }, []);
  const addCustomVital = useCallback(() => {
    if (!customVitalLabel.trim()) return;
    const key = `custom_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setCustomVitals(p => [...p, { key, label: customVitalLabel.trim(), value: '', unit: customVitalUnit.trim() || '' }]);
    setCustomVitalLabel(''); setCustomVitalUnit(''); setShowCustomVitalForm(false);
  }, [customVitalLabel, customVitalUnit]);

  const handleInputChange = (e) => {
    // the next keystroke is the doctor moving on — the list may open again
    suppressAutoOpenRef.current = false;
    setRawInput(e.target.value); updateSuggestions(e.target.value); setError(null); setSuccessMsg(null);
  };

  const hasMeds = medicines.length > 0;
  // Render-scope: a lab-only Rx (in-house orders, no outside text) still counts as "has tests".
  const hasInv = printableInvestigations.length > 0;
  // FE-6 — surgeries are content: they keep Follow-up on screen and let the
  // custom theme's image attachments be added to a surgeries-only Rx (the same
  // rule handleSave applies server-side-bound above).
  const hasSurg = advisedSurgeries.length > 0;
  // FE-dx-only — a typed diagnosis is prescription content too (owner
  // 2026-09-13: image upload must not be blocked on a diagnosis-only pad).
  // `hasRxContent` is the ONE predicate for "this pad carries a prescription
  // entry" — handleSave's save gate below and the image drop-zone render gate
  // (further down) both read this exact value, so they can never drift apart
  // again (bk report: dx-only widened the save gate but not the drop-zone).
  const hasDx = diagnosis.trim().length > 0;
  const hasRxContent = hasMeds || hasInv || hasSurg || hasDx;

  // ── UNIFIED SAVE ──────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    const hasVitals = Object.entries(vitalValues).some(([, v]) => v !== '' && v != null) || customVitals.some(v => v.value);
    const hasSym = selectedSymptoms.size > 0;
    const hasNotes = clinicalNotes.trim().length > 0;
    const hasCc = chiefComplaint.trim().length > 0;
    // Printed investigations = the single-source projection (live order names +
    // free-text strings with no order). Excludes a cancelled/done test's leftover
    // string so the PDF prints exactly what the Tests row shows.
    const allInvestigations = printableInvestigations;
    // FE-6 — advised surgeries are prescription content in their own right: a
    // surgeries-only Rx saves, composes and prints (BE-6 widened the backend's
    // own content guard to match). FE-dx-only — a typed diagnosis is content
    // too, same reasoning. `hasRxContent` is the render-scope predicate
    // declared once above (with hasMeds/hasInv/hasSurg/hasDx) and reused here
    // AND by the image drop-zone's render gate — one derivation for "this pad
    // carries prescription content", never a second copy to drift apart.
    if (!hasVitals && !hasSym && !hasNotes && !hasCc && !hasRxContent) {
      if (attachedImageFiles.length > 0) {
        setError('Add at least one prescription item (medicine, test, symptoms, or notes) to attach images to');
      } else {
        setError('Add at least one item');
      }
      return;
    }
    // I8 — Save stays clickable; an invalid dose slot blocks the actual
    // submit and states why (the inline messages under the slots say
    // which — Save just repeats the reason once, near the button).
    if (medicines.some((m) => m._doseErrors && Object.values(m._doseErrors).some(Boolean))) {
      setError(DOSE_SAVE_BLOCKED_COPY);
      return;
    }
    setSaving(true); setError(null); const errors = [];
    if (onSaveStart) onSaveStart();
    try {
      const promises = [];
      let imageDeletesDone = null;
      // Apply any staged existing-image removals (X clicked on an
      // already-saved thumbnail). Each PRESCRIPTION_IMAGE row deletes its
      // own S3 object and releases its image_slot on the parent
      // prescription — see visit_metadata_service.delete_metadata.
      if (removedExistingImageIdsRef.current.length > 0) {
        const idsToDelete = [...removedExistingImageIdsRef.current];
        removedExistingImageIdsRef.current = [];
        // The replacement upload below awaits this, so it never races the
        // delete that frees its image slot.
        imageDeletesDone = Promise.all(idsToDelete.map((id) => deleteMetadata(visitId, 'PRESCRIPTION_IMAGE', id)))
          .catch(e => { errors.push('Remove image: ' + (e.message || 'failed')); });
        promises.push(imageDeletesDone);
      }
      // Only save vitals as a separate VITAL entry if the doctor actually
      // modified them.  Nurse-prefilled vitals already exist — re-saving
      // them would create duplicates in the history.
      if (hasVitals && doctorModifiedVitalsRef.current) {
        const vp = [];
        for (const key of activeVitalKeys) { const val = vitalValues[key]; if (val !== undefined && val !== '' && val != null) { const def = VITAL_DEFINITIONS[key]; vp.push({ key, label: def?.label || key, value: String(val), unit: def?.unit || '', is_custom: false }); } }
        for (const cv of customVitals) { if (cv.value) vp.push({ key: cv.key, label: cv.label, value: String(cv.value), unit: cv.unit || '', is_custom: true }); }
        if (vp.length > 0) promises.push(addVitals(visitId, vp).catch(e => { errors.push('Vitals: ' + (e.message || 'failed')); }));
      }
      if (hasSym) {
        // ONE known-symptom set (see `knownSymptoms`) decides is_custom here AND
        // classifies a template-applied symptom on the way in — a second copy
        // would let the two paths disagree about the same word.
        promises.push(addSymptoms(visitId, [...selectedSymptoms].map(n => ({ name: n, is_custom: !knownSymptoms.has(n) }))).catch(e => { errors.push('Symptoms: ' + (e.message || 'failed')); }));
      }
      if (hasNotes) promises.push(addNote(visitId, clinicalNotes.trim()).catch(e => { errors.push('Notes: ' + (e.message || 'failed')); }));
      // ── P-FE-1 — write the allergy back to the PATIENT RECORD ────────
      // The pad is the SINGLE writer of this field (build-plan Q3). It fires on
      // EVERY save path, deliberately OUTSIDE the `hasRxContent` block: a
      // vitals+allergies pad creates no prescription entry, and before this the
      // allergy was silently gone on refresh (bk-1155 class).
      // Two conditions, both required:
      //   · the doctor EDITED the box this session (`allergiesDirty`, set by the
      //     textarea's onChange only) — so an untouched resave of an older entry
      //     can never NARROW a longer stored record (Business condition 1);
      //   · the trimmed value is NON-BLANK — an empty box sends no key at all,
      //     so the pad can never wipe the patient's recorded allergy
      //     (Business condition 2; clearing is a Patient Details action).
      // Failure surfaces like a vitals failure and never blocks the Rx save.
      // Idempotent: on success the baseline moves and `dirty` clears, so a
      // second save in the same session sends nothing (≤1 PATCH per save).
      const allergiesToWrite = allergies.trim();
      if (allergiesDirty && allergiesToWrite && patientId && !practiceMode) {
        promises.push(
          updatePatientDemographics(patientId, { allergies: allergiesToWrite })
            .then(() => {
              setPatientAllergies(allergiesToWrite); setAllergiesDirty(false);
              // QA D1 — a stale module-level cache after a write-back re-seeds
              // the PRE-edit value as "From patient record" on the next mount
              // in the same session, and the next edit silently narrows the
              // record back down. Re-key `_referenceCache` here too — the
              // same move `applyMemoryChange` (:1302) and the `memory_delta`
              // merge (below) already make for the memory groups.
              // …and move `refData` with it. The `memory_delta` merge below
              // runs LATER on this same save path and re-keys the cache from
              // its own `prev` — without this line that `prev` still carries
              // the PRE-edit allergies and the merge clobbers the re-key
              // above (D1 again, whenever the server learned anything).
              // Functional updates apply in queue order, so the merge's
              // `prev` already carries the value written here.
              setRefData((prev) => (prev ? { ...prev, patient_allergies: allergiesToWrite } : prev));
              const cacheKey = practiceMode ? PRACTICE_REFERENCE_KEY : (visitId || PRACTICE_REFERENCE_KEY);
              const cached = _referenceCache.get(cacheKey);
              if (cached) {
                const patched = cached.then((d) => ({ ...d, patient_allergies: allergiesToWrite }));
                _referenceCache.set(cacheKey, patched);
                // Never cache a rejection — same rule as the mount read (:1263).
                patched.catch(() => {
                  if (_referenceCache.get(cacheKey) === patched) _referenceCache.delete(cacheKey);
                });
              }
            })
            .catch(e => { errors.push('Allergies: ' + (e.message || 'failed')); })
        );
      }
      let pdfFetchPromise = null; // started early, in parallel with vitals/notes saves
      let uploadFailed = false; // image upload failed after the Rx saved -> tell the doctor
      let pdfFetchCancelled = false; // set true on error path to suppress viewer open
      if (hasRxContent) {
        // FE-14 — `medicine_source`/`code` ride only when the row came from the
        // org's medicine list (`buildMedicineRow` only ever sets them together,
        // both null on a typed/parser/memory row) — BE-13's identity-by-code fold.
        const payload = medicines.map(m => ({ medicine: m.medicine || m.medicine_input || 'Unknown', medicine_input: m.medicine_input, strength: m.strength || null, form: m.form || null, dosage_schedule: m.dosage_schedule || null, frequency: m.frequency || null, timing: m.timing || null, duration: m.duration || null, route: m.route || null, notes: m.notes || null, is_resolved: m.is_resolved ?? true, medicine_source: m.medicine_source || null, code: m.code || null }));
        // Build vitals snapshot to store alongside the prescription for edit restoration
        const vitalsSnapshot = [];
        for (const key of activeVitalKeys) { const val = vitalValues[key]; if (val !== undefined && val !== '' && val != null) { const def = VITAL_DEFINITIONS[key]; vitalsSnapshot.push({ key, label: def?.label || key, value: String(val), unit: def?.unit || '', is_custom: false }); } }
        for (const cv of customVitals) { if (cv.value) vitalsSnapshot.push({ key: cv.key, label: cv.label, value: String(cv.value), unit: cv.unit || '', is_custom: true }); }
        // G2 — `advised_surgeries` is a FLAT top-level key on the request, the
        // doctor's typed lines and nothing else (default []). The body is
        // `extra = "forbid"`, so no read-only/derived field is echoed back into it.
        const cd = { report_required: allInvestigations.length > 0, investigations: allInvestigations.length > 0 ? allInvestigations : [], advised_surgeries: [...advisedSurgeries], follow_up_required: followUpRequired, follow_up_in: followUpRequired && followUpDate ? followUpDate : null, vitals: vitalsSnapshot, chief_complaint: chiefComplaint.trim() || null, diagnosis: diagnosis.trim() || null, allergies: allergies.trim() || null };
        // Always route an edit-and-resave through replaces_meta_id so the
        // backend owns the old entry's lifecycle: COMPLETED visits get a
        // soft-supersede (audit trail preserved); not-yet-completed visits
        // get a hard-delete-and-relink (add_prescription re-points any
        // attached PRESCRIPTION_IMAGE rows onto the new entry before
        // removing the old one). This used to call deleteMetadata directly
        // for the not-completed case instead -- that hits the GENERIC
        // delete route, which cascade-deletes attached images outright
        // (correct for an actual delete, wrong for a version replace) and
        // never told addDigitalPrescription a replace was even happening,
        // so every ordinary edit-and-resave permanently destroyed that
        // prescription's attached images.
        if (editMetaId) {
          cd.replaces_meta_id = editMetaId;
        }
        // Practice mode: delete ALL existing prescription entries before saving so the
        // PDF always reflects only the current prescription, not accumulated entries.
        // Fetch-and-delete-all is robust — no stale ref can cause duplicates.
        if (practiceMode) {
          try {
            const existing = await fetchVisitMetadata(visitId, 'PRESCRIPTION');
            const toDelete = (existing?.entries || []).map(e => e.meta_id).filter(Boolean);
            if (toDelete.length > 0) {
              await Promise.all(toDelete.map(id => deleteMetadata(visitId, 'PRESCRIPTION', id).catch(() => {})));
            }
          } catch { /* */ }
        }
        // Staged image removals must be CONFIRMED before the save: the save's
        // replaces_meta_id relink counts the parent's image slots, and a delete
        // still in flight can make it 400 "max 4" (qa-report-r2 D3r: 2 of 3
        // interleavings failed when the two ran in parallel).
        if (imageDeletesDone) await imageDeletesDone;
        // Start raw promise so we can chain PDF fetch immediately when it resolves,
        // overlapping with vitals/symptoms/notes saves still in flight
        const rawRxPromise = addDigitalPrescription(visitId, payload, doctorNotes || null, cd);
        // Open the PDF for medicine OR lab-only OR surgeries-only prescriptions
        // (the backend composer requires none of the three in particular).
        if (hasRxContent) {
          pdfFetchPromise = rawRxPromise.then(async (rxRes) => {
            if (attachedImageFiles.length > 0 && rxRes?.meta_id) {
              try {
                if (imageDeletesDone) await imageDeletesDone;
                await uploadPrescriptionImages(visitId, rxRes.meta_id, attachedImageFiles);
                // Must clear on success: attachedImageFiles otherwise stays
                // populated forever (edit-in-place never unmounts), so every
                // later Generate Prescription click -- even one unrelated to
                // images -- re-uploads this same file again as a brand-new
                // row. Found live: one attached photo turned into 3 separate
                // PRESCRIPTION_IMAGE rows / S3 objects across 3 consecutive
                // saves in the same session.
                setAttachedImageFiles([]);
              } catch (upErr) {
                console.error('Failed to upload prescription image attachments:', upErr);
                uploadFailed = true;
              }
            }
            return !pdfFetchCancelled && openPrescriptionPdf(visitId);
          }).catch(() => null);
        }
        const rxPromise = rawRxPromise.catch(e => { errors.push('Prescription: ' + (e.message || 'failed')); return null; });
        promises.push(rxPromise);
        // Follow-up is written to the VISIT by the backend prescription-save
        // (single source, resolved to an absolute date) — no fragile client
        // mirror here. The Rx payload (cd) already carries follow_up_required/in.
      }
      const results = await Promise.all(promises);
      if (errors.length > 0) { pdfFetchCancelled = true; setError('Partial save — ' + errors.join('; ')); }
      else {
        skipDraftRef.current = true; // prevent draft save of empty state
        clearDraft(); setSuccessMsg('Generating PDF...');

        // Extract new meta_id from the prescription creation response
        // The rx promise is the last item pushed when hasRxContent
        let newMetaId = null;
        let rxResponse = null;
        if (hasRxContent) {
          rxResponse = results[results.length - 1];
          newMetaId = rxResponse?.meta_id || null;
        }
        // Remember this meta_id so the useEffect skips re-populating
        if (newMetaId) lastSavedMetaIdRef.current = newMetaId;

        // FE-2 / S2 AC8 — optimistic memory merge, NO refetch. `memory_delta`
        // is null when nothing was learned (practice mode, non-prescriber,
        // an unchanged re-save — G2), so this is a no-op in those cases.
        // Re-key the promise cache to the merged snapshot too, so a REMOUNT
        // later in the same session (no sign-out in between) sees it —
        // never a second network read for a value saved this visit.
        if (rxResponse?.memory_delta) {
          const cacheKey = practiceMode ? PRACTICE_REFERENCE_KEY : (visitId || PRACTICE_REFERENCE_KEY);
          setRefData((prev) => {
            if (!prev) return prev;
            const merged = { ...prev, memory: mergeMemoryDelta(prev.memory, rxResponse.memory_delta) };
            if (_referenceCache.has(cacheKey)) _referenceCache.set(cacheKey, Promise.resolve(merged));
            return merged;
          });
        }

        // Clear ephemeral state but KEEP form content (always-edit-in-place)
        setRawInput(''); setError(null); setUnrecognizedItems([]); setInvestigationPicks([]); setInvestigationLikely([]);
        // Keep draft suppressed — the prescription is saved, no stale
        // draft should be re-created from the still-visible form data.
        // skipDraftRef stays true; it resets when the component remounts.

        // FE-4 (G3) — the client no longer calls `/prescription/log-edit`.
        // The route stays server-side for the parser's own quality file log
        // (BE-10); `learned` is now always 0 on the wire, so there is nothing
        // left for a client-side "Learned N preferences" toast to report.
        // Field learning (CO/DX/Tests/Surgeries/Symptoms/RX) now comes back
        // synchronously as `memory_delta` on THIS save response, merged above.

        // Build the data snapshot to pass back to parent
        const savedData = {
          medicines: [...medicines],
          doctor_notes: doctorNotes || '',
          chief_complaint: chiefComplaint || '',
          allergies: allergies || '',
          diagnosis: diagnosis || '',
          investigations: [...investigations],
          // FE-6 — the parent hands this snapshot straight back as `initialData`
          // for edit-in-place, so it must carry the surgeries or the next render
          // silently empties the section.
          advised_surgeries: [...advisedSurgeries],
          follow_up_required: followUpRequired,
          follow_up_in: followUpRequired && followUpDate ? followUpDate : null,
          vitals: [],
        };
        // Build vitals for the callback
        for (const key of activeVitalKeys) { const val = vitalValues[key]; if (val !== undefined && val !== '' && val != null) { const def = VITAL_DEFINITIONS[key]; savedData.vitals.push({ key, label: def?.label || key, value: String(val), unit: def?.unit || '', is_custom: false }); } }
        for (const cv of customVitals) { if (cv.value) savedData.vitals.push({ key: cv.key, label: cv.label, value: String(cv.value), unit: cv.unit || '', is_custom: true }); }

        // PDF was already being fetched in parallel — just await it now (likely done).
        // pdfFetchPromise is set for medicine OR lab-only prescriptions.
        if (pdfFetchPromise) {
          try { await pdfFetchPromise; } catch { /* silent — viewer open failed */ }
        }
        setSuccessMsg(null);
        if (uploadFailed) setError(IMAGES_SECTION_COPY.uploadFailed);
        onSuccess?.({ metaId: newMetaId, data: savedData, imageUploadFailed: uploadFailed });
        if (practiceMode) {
          setMedicines([]);
          setInvestigations([]);
          setAdvisedSurgeries([]);
          setDiagnosis('');
          setChiefComplaint('');
          setAllergies('');
          setDoctorNotes('');
          setFollowUpRequired(false);
          setFollowUpDate(null);
          setRawInput('');
          setVitalValues({});
          setCustomVitals([]);
          setSelectedSymptoms(new Set());
        }
      }
    } catch (err) { setError(typeof err.message === 'string' ? err.message : 'Save failed'); }
    finally { setSaving(false); }
    // `attachedImageFiles` MUST stay in this list. handleSave reads it when it
    // sequences the image upload before opening the PDF; without the dep the
    // callback closes over a stale empty array and the doctor's attachments are
    // silently dropped on save. (Fixed once already — do not drop it in a merge.)
  }, [medicines, doctorNotes, diagnosis, clinicalNotes, chiefComplaint, allergies, allergiesDirty, patientId, followUpRequired, followUpDate, investigations, advisedSurgeries, visitId, onSuccess, onSaveStart, editMetaId, vitalValues, activeVitalKeys, customVitals, selectedSymptoms, knownSymptoms, clearDraft, practiceMode, printableInvestigations, attachedImageFiles, hasRxContent]);

  // ── Printing is ONE click ──────────────────────────────────────────
  // There is deliberately no print-time confirmation. It was removed by the
  // owner: the amber row notes already say what is missing, and a popup before
  // every print would be clicked through unread. Saving always opens the
  // printed PDF (see openPrescriptionPdf above), so Generate → print, directly.
  //
  // CONSEQUENCE, stated and accepted: the row notes are now the ONLY thing
  // between a blank field or an unlabelled strength and a signed prescription.
  // Never weaken them without revisiting this decision.
  handleSaveRef.current = handleSave;


  // ═════════════════════════════════════════════════════════════════
  // PAPER-PAD THEME
  // ═════════════════════════════════════════════════════════════════

  const pad = isDark ? 'rgba(255,255,255,0.025)' : '#fefdfb';
  const padBorder = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(180,160,120,0.2)';
  const lineColor = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(180,160,120,0.12)';
  const ink = isDark ? '#e8e9ed' : '#1a1a2e';
  const inkLight = isDark ? '#9ca3af' : '#6b7280';
  const inkFaint = isDark ? '#6b7280' : '#9ca3af';
  const teal = '#0d9488';
  // T1a — day-mode contrast rule: a 4% wash disappears against the already-light
  // pad. Dark keeps its alpha ramp (rule exempts dark:), day gets the solid tint.
  const tealBg = isDark ? 'rgba(13,148,136,0.16)' : '#f0fdfa';
  // ONE amber for the row's advisory lines — the no-unit warning and the
  // blank-field note. Named here because two things use it, so the row can
  // never drift to a second orange.
  //
  // Both values are contrast-checked at the 8px these lines render at, and the
  // bar is AA (4.5:1) rather than "looks fine": with the print-time
  // confirmation removed, these two lines are the ONLY warning between a blank
  // field or an unlabelled strength and a printed script.
  //   light #b45309 on the cream pad  = 4.94:1  (the old #d97706 was 3.13:1)
  //   dark  #fbbf24 on the dark pad   = 10.12:1 (the old #d97706 was 5.31:1)
  const warnAmber = isDark ? '#fbbf24' : '#b45309';
  const inpBg = isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.015)';
  const inpBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(180,160,120,0.18)';

  // T-F2 — app-standard field sizing for the medicine editor (mirrors
  // RegisterVisitForm.jsx inputBase()/labelStyle). SIZE only — same
  // paper-pad palette (ink/inpBg/inpBorder), no theme/layout change.
  const fieldInput = (hasError) => ({
    width: '100%', padding: '0.75rem 0.875rem', borderRadius: '0.75rem',
    border: `1.5px solid ${hasError ? (isDark ? 'rgba(217,119,6,0.4)' : 'rgba(217,119,6,0.35)') : inpBorder}`,
    background: inpBg, fontSize: '1rem', color: ink, outline: 'none', boxSizing: 'border-box',
  });
  const fieldLabel = {
    fontSize: '0.8125rem', fontWeight: 600, color: inkFaint,
    marginBottom: '0.375rem', textTransform: 'uppercase', letterSpacing: '0.06em',
  };
  const manBox = (hasError) => ({
    width: '2.75rem', textAlign: 'center', padding: '0.4rem 0.375rem', margin: '0.15rem 0', borderRadius: '0.75rem',
    border: `1.5px solid ${hasError ? (isDark ? 'rgba(217,119,6,0.4)' : 'rgba(217,119,6,0.35)') : inpBorder}`,
    background: hasError ? (isDark ? 'rgba(217,119,6,0.12)' : '#fffbeb') : inpBg,
    fontSize: '1.0625rem', fontWeight: 700,
    color: ink, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  });
  // T1a/D22 interactive floor (0.8125rem, qaChromeCensus) — sized up from the
  // mockup's 0.65rem accordingly.
  const manStepBtn = {
    width: '1.75rem', height: '1.2rem', borderRadius: '0.35rem', border: `1.5px solid ${inpBorder}`,
    background: inpBg, color: teal, fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  };

  // T1a — type-scale ladder for the CHROME around the form (the editor fields
  // above — fieldInput/fieldLabel/manBox — are already app-standard from
  // rx-r3 and stay untouched). Floor: nothing below 0.6875rem, no
  // INTERACTIVE text below 0.75rem. One block, every chrome literal below the
  // floor reads from here — no per-spot re-guessing.
  // ── D22 (a) — the whole ladder moved ONE step up: the owner could not read
  // the pad ("the whole form, template fonts are too tiny"). Every token grew
  // by exactly one rung of the same scale and NOTHING shrank; the floors moved
  // with it (chrome 0.6875 → 0.75rem, interactive 0.75 → 0.8125rem). The rungs
  // are 0.6875 · 0.75 · 0.8125 · 0.875 · 0.9375 · 1 · 1.0625rem.
  const type = {
    row: '0.9375rem', rowSub: '0.8125rem',    // picker dropdown rows / sub-labels
    chip: '0.8125rem',                        // quick-add pills, template/test/follow-up chips
    badge: '0.75rem',                         // summary-row badges (⚠ verify · ★ Your pick · …)
    rowAction: '2.25rem',                     // row Edit/Remove icon-button box (not a font size)
    hint: '0.8125rem',                        // hint line under the entry box, "Loading…"
    note: '0.875rem',                         // amber "Not filled in:" — the ONLY pre-print guard
    invText: '0.875rem', invChip: '0.8125rem',// investigation blocks
    section: '0.8125rem',                     // section + vitals inline labels (uppercase 600)
    input: '1rem',                            // vitals values, Diagnosis, C/o textarea, custom-symptom
    medName: '1.0625rem', medMeta: '0.9375rem', // list row name / meta
    btn: '0.9375rem', save: '1rem',           // footer/dialog buttons, Save
    banner: '0.8125rem',                      // nurse-vitals banner, draft-restored, toasts
  };

  // ── P5 / owner fix — EVERY section's primary control is a real input box:
  // solid fill in both modes, a visible 1px mid-tone border, a teal focus ring
  // (CSS, below) and a plain placeholder in readable ink. It used to be a bare
  // underline on cream, which doctors read as decoration and missed. Sizes come
  // from the `type` ladder untouched (PR #96 floors hold).
  const fieldBox = {
    width: '100%', boxSizing: 'border-box',
    padding: '0.45rem 0.6rem',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.22)' : '#cbd5e1'}`,
    borderRadius: '0.375rem',
    background: isDark ? '#0f172a' : '#ffffff',
    color: ink, fontSize: type.input, outline: 'none',
    fontFamily: "'Georgia', serif", lineHeight: 1.5,
  };
  // The medicine box is THE primary control of the pad — same box, heavier.
  const medicineBox = {
    ...fieldBox,
    border: `1.5px solid ${isDark ? 'rgba(45,212,191,0.55)' : 'rgba(13,148,136,0.55)'}`,
    fontWeight: 700,
    padding: '0.55rem 0.7rem',
  };
  // P5 — a shortcut reads as a shortcut: muted ink, one indent, never a
  // different size (the type ladder is untouched).
  const shortcutRow = {
    display: 'flex', gap: '0.25rem', flexWrap: 'wrap', alignItems: 'center',
    marginTop: space.item, paddingLeft: '0.15rem',
  };

  const hasAny = hasMeds || hasInv || hasSurg || selectedSymptoms.size > 0 ||
    Object.values(vitalValues).some(v => v !== '' && v != null) || clinicalNotes.trim() || chiefComplaint.trim() || diagnosis.trim() || customVitals.some(v => v.value);
  // Repeat Last Rx ONLY — "is there anything on this pad that Load would
  // overwrite?". Allergies is deliberately excluded from `hasAny` (it is not
  // prescription content and must never gate Generate), but a doctor who has
  // typed ONLY allergies is about to have them replaced, so this dialog must
  // not tell them the pad is empty. Narrow predicate, this one dialog.
  const repeatRxWouldOverwrite = Boolean(hasAny) || allergies.trim().length > 0;

  // save button label
  const saveLabel = useMemo(() => {
    if (saving) return 'Generating...';
    const parts = [];
    const vc = Object.values(vitalValues).filter(v => v !== '' && v != null).length + customVitals.filter(v => v.value).length;
    if (vc) parts.push(`${vc} vital${vc !== 1 ? 's' : ''}`);
    if (selectedSymptoms.size) parts.push(`${selectedSymptoms.size} symptom${selectedSymptoms.size !== 1 ? 's' : ''}`);
    if (medicines.length) parts.push(`${medicines.length} med${medicines.length !== 1 ? 's' : ''}`);
    if (printableInvestigations.length) parts.push(`${printableInvestigations.length} test${printableInvestigations.length !== 1 ? 's' : ''}`);
    if (clinicalNotes.trim()) parts.push('notes');
    return parts.length ? `Generate Prescription (${parts.join(' + ')})` : 'Generate Prescription';
  }, [saving, vitalValues, customVitals, selectedSymptoms, medicines, printableInvestigations, clinicalNotes]);

  // ═════════════════════════════════════════════════════════════════
  // RENDER — Paper Pad
  // ═════════════════════════════════════════════════════════════════

  // R4/R4b (bk-997) — two bugs in the same strip:
  //  (a) the entered value used to sit on a bare underline on `transparent`
  //      with a raw `#eab308` warn literal (fails day-mode contrast) — now a
  //      solid field shell, full-contrast `ink`/the already-shared `warnAmber`
  //      (single source, no second amber). Placeholder keeps its own lighter
  //      + italic treatment (CSS below) so empty vs filled never read alike.
  //  (b) the unit used to be a plain sibling <span> with no floor on its own
  //      width, and the input had a hard `width:3rem` — in the ≥1024px right
  //      column (flex `0 1 24rem`, `minWidth:0`, so it DOES get squeezed
  //      narrower than 24rem) the flex row would shrink the input/unit rather
  //      than wrap, cropping the value or the unit. The unit now lives INSIDE
  //      a shell with the input (same idea as AddVitals), the shell and its
  //      per-vital wrapper are `flexShrink:0` (a vital never shrinks, it
  //      WRAPS to the next row instead), and the shell's `min-width` scales
  //      with the unit's own length so "breaths/min" is never truncated.
  // RV-7 (owner, real-browser click-through) — R4b's fix held in jsdom but
  // not in a real layout: `width:2.25rem` + `flex:'1 1 auto', minWidth:0`
  // let the box shrink to ~2 characters ("98.6" rendered as "3."), and the
  // native number-spinner arrows (never hidden) ate into what little width
  // there was. Fixed deterministically, not by eyeballing a screenshot:
  // each vital gets an EXPLICIT width sized off its own longest expected
  // value (VITAL_DEFINITIONS, incl. the decimal point), with a hard floor —
  // `flex:'0 0 auto'` so the shell can shrink around it, but the input
  // itself never can. See `mockups/nurse-vitals/pad-vitals-check.html` for
  // a static render at 1024/1440px with these exact classes/CSS.
  const VITAL_INPUT_CH = {
    body_temperature: 5,           // "104.5"
    blood_pressure_systolic: 3,    // "120"
    blood_pressure_diastolic: 3,   // "80"
    heart_rate: 3,                 // "72"
    respiratory_rate: 2,           // "16"
    oxygen_saturation: 3,          // "98"
    weight: 5,                     // "300.0"
    height: 5,                     // headroom to match temperature/weight
  };
  const DEFAULT_VITAL_INPUT_CH = 5; // custom vitals (no VITAL_DEFINITIONS entry)
  const vitalInputWidth = (key) => `calc(${VITAL_INPUT_CH[key] || DEFAULT_VITAL_INPUT_CH}ch + 1.25rem)`;
  const vitalShellStyle = (warn) => ({
    display: 'flex', alignItems: 'stretch', flexShrink: 0,
    borderRadius: '0.25rem', overflow: 'hidden',
    border: `1px solid ${warn ? warnAmber : inpBorder}`, background: inpBg,
  });
  const vitalInputStyle = (key, warn) => ({
    flex: '0 0 auto', width: vitalInputWidth(key), minWidth: vitalInputWidth(key),
    padding: '0.2rem 0 0.2rem 0.35rem', border: 'none', background: 'transparent',
    fontSize: type.input, fontWeight: 600, color: warn ? warnAmber : ink,
    outline: 'none', textAlign: 'center', fontFamily: "'Georgia', serif",
    MozAppearance: 'textfield',
  });
  const vitalUnitStyle = {
    display: 'flex', alignItems: 'center', flexShrink: 0, whiteSpace: 'nowrap',
    padding: '0 0.35rem', fontSize: type.section, fontWeight: 600, color: inkLight,
    borderLeft: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
  };

  const blockVitals = (
    <PadField label="Vitals" tooltip={SECTION_INFO.vitals} isDark={isDark} dataTour="vitals">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          {activeVitalKeys.map((key) => {
            const def = VITAL_DEFINITIONS[key];
            if (!def) return null;
            const val = vitalValues[key] || '';
            const num = parseFloat(val);
            const warn = val && !isNaN(num) && (num < def.min || num > def.max);
            return (
              <div key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                <span style={{ fontSize: type.section, fontWeight: 600, color: inkLight, fontFamily: 'system-ui, sans-serif', whiteSpace: 'nowrap', textTransform: 'uppercase' }}>
                  {def.label}:
                </span>
                <div style={vitalShellStyle(warn)}>
                  <input
                    type="number" step={def.step} min={def.min} max={def.max}
                    inputMode="decimal"
                    value={val}
                    onChange={(e) => { doctorModifiedVitalsRef.current = true; setVitalValues(p => ({ ...p, [key]: e.target.value })); }}
                    placeholder={def.placeholder}
                    className="rx-vital-input"
                    style={vitalInputStyle(key, warn)}
                  />
                  <span style={vitalUnitStyle}>{def.unit}</span>
                </div>
              </div>
            );
          })}

          {/* Custom vitals inline */}
          {customVitals.map(cv => (
            <div key={cv.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
              <span style={{ fontSize: type.section, fontWeight: 600, color: teal, fontFamily: 'system-ui, sans-serif', whiteSpace: 'nowrap', textTransform: 'uppercase' }}>{cv.label}:</span>
              <div style={vitalShellStyle(false)}>
                <input type="text" value={cv.value}
                  onChange={(e) => { doctorModifiedVitalsRef.current = true; setCustomVitals(p => p.map(v => v.key === cv.key ? { ...v, value: e.target.value } : v)); }}
                  placeholder={cv.unit || '—'}
                  className="rx-vital-input"
                  style={vitalInputStyle(cv.key, false)}
                />
                {cv.unit && <span style={vitalUnitStyle}>{cv.unit}</span>}
              </div>
              <button onClick={() => setCustomVitals(p => p.filter(v => v.key !== cv.key))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: type.hint, padding: 0, lineHeight: 1, flexShrink: 0 }}>✕</button>
            </div>
          ))}

          {/* + More vitals — flexShrink:0 so the affordance stays reachable
              even when every vital above has wrapped onto its own row. */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button onClick={() => setShowOptionalVitals(!showOptionalVitals)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: type.hint, color: inkFaint, fontFamily: 'system-ui, sans-serif', padding: '0.1rem 0.25rem' }}>
              {showCustomVitalForm ? '' : '+ more'}
            </button>
            {showOptionalVitals && availableOptionalVitals.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, zIndex: 30,
                background: isDark ? '#1f2937' : '#fff', border: `1px solid ${padBorder}`, borderRadius: '0.375rem',
                padding: '0.2rem', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '7rem',
              }}>
                {availableOptionalVitals.map(k => (
                  <button key={k} onClick={() => { setActiveVitalKeys(p => [...p, k]); setShowOptionalVitals(false); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.2rem 0.4rem', border: 'none', background: 'transparent', fontSize: type.hint, color: ink, cursor: 'pointer', fontFamily: 'system-ui, sans-serif' }}
                    onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#f3f4f6'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >{VITAL_DEFINITIONS[k]?.label}</button>
                ))}
                <hr style={{ border: 'none', borderTop: `1px solid ${lineColor}`, margin: '0.15rem 0' }} />
                <button onClick={() => { setShowOptionalVitals(false); setShowCustomVitalForm(true); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.2rem 0.4rem', border: 'none', background: 'transparent', fontSize: type.hint, color: teal, cursor: 'pointer', fontFamily: 'system-ui, sans-serif', fontWeight: 600 }}>
                  + Custom
                </button>
              </div>
            )}
          </div>
          {showCustomVitalForm && (
            <div style={{ display: 'inline-flex', gap: '0.2rem', alignItems: 'center' }}>
              <input autoFocus type="text" value={customVitalLabel} onChange={e => setCustomVitalLabel(e.target.value)} placeholder="Label" style={{ width: '4rem', padding: '0.15rem 0.25rem', border: 'none', borderBottom: `1.5px solid ${inpBorder}`, background: 'transparent', fontSize: type.hint, color: ink, outline: 'none', fontFamily: 'system-ui, sans-serif' }} />
              <input type="text" value={customVitalUnit} onChange={e => setCustomVitalUnit(e.target.value)} placeholder="Unit" style={{ width: '2.5rem', padding: '0.15rem 0.25rem', border: 'none', borderBottom: `1.5px solid ${inpBorder}`, background: 'transparent', fontSize: type.hint, color: ink, outline: 'none', fontFamily: 'system-ui, sans-serif' }} />
              <button onClick={addCustomVital} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8125rem', color: teal }}>✓</button>
              <button onClick={() => { setShowCustomVitalForm(false); setCustomVitalLabel(''); setCustomVitalUnit(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8125rem', color: '#ef4444' }}>✕</button>
            </div>
          )}
        </div>
    </PadField>
  );

  // RX-FORM-BUGS item 3 — "recent" C/O pills, ranked by RECENCY (`t` desc,
  // `c` tiebreak) via `recentCoValues` (rxMemory.js), the ONE shared
  // selector every C/O-pill reader consumes. Same D12 gate the dropdown
  // uses; updates in-session off `refData.memory` with no refetch (the
  // `memory_delta` merge already keeps that object current on save).
  const recentCoPills = useMemo(() => (
    canUseTemplates ? recentCoValues(refData?.memory, 5) : []
  ), [canUseTemplates, refData]);

  const blockChiefComplaint = (
    <PadField label="C/o" isDark={isDark} dataTour="chief-complaint">
          <div style={{ position: 'relative' }}>
            <textarea
              ref={ccInputRef}
              value={chiefComplaint}
              onChange={(e) => { setChiefComplaint(e.target.value); updateCcSuggestions(e.target.value); }}
              // FE-3B — same open-on-focus as Diagnosis (FE-3), no length gate.
              onFocus={(e) => { if (e.target.rows < 2) e.target.rows = 2; updateCcSuggestions(chiefComplaint); }}
              // FIX-6 — an Enter-picked suggestion closes this dropdown
              // without blurring; a click on the still-focused box is a
              // real re-focus.
              onMouseDown={reopenIfAlreadyFocused(ccInputRef, () => updateCcSuggestions(chiefComplaint))}
              onBlur={() => setTimeout(() => { setShowCcSuggestions(false); setCcHighlight(-1); }, 200)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { setShowCcSuggestions(false); setCcHighlight(-1); e.target.blur(); return; }
                if (!showCcSuggestions || ccSuggestions.length === 0) return;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setCcHighlight((h) => Math.min(h + 1, ccSuggestions.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setCcHighlight((h) => Math.max(h - 1, -1));
                } else if (e.key === 'Enter' && ccHighlight >= 0) {
                  // Enter with NOTHING highlighted is left alone — a plain
                  // newline in this multi-line field, same as before.
                  e.preventDefault();
                  applyCcSuggestion(ccSuggestions[ccHighlight]);
                }
              }}
              placeholder="e.g. Cough with sputum since 4 days, mild fever"
              rows={1}
              className="rx-field"
              style={{ ...fieldBox, resize: 'none' }}
            />
            {/* FE-3B — rect-anchored like Diagnosis (bk-973's fix, generalised
                here): the memory list can now be up to 300 rows tall, so it
                needs to escape the pad's `overflow: hidden` the same way. */}
            {showCcSuggestions && ccSuggestions.length > 0 && ccPopoverRect && (
              <div ref={ccSuggestionsRef} data-testid="co-suggestions" style={{
                position: 'fixed',
                top: ccPopoverRect.top, left: ccPopoverRect.left, width: ccPopoverRect.width,
                zIndex: 1000,
                background: isDark ? '#1f2937' : '#fff', border: `1px solid ${padBorder}`,
                borderRadius: '0.375rem',
                maxHeight: narrowHost ? 'min(14rem, 38vh)' : 'min(20rem, 50vh)',
                overflowY: 'auto',
                boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
              }}>
                {/* FE-3B — C/O has no template category, so its dropdown is
                    the memory group alone, via the SAME row component
                    Diagnosis and Medicines reuse (never copy-pasted). */}
                {ccSuggestions.map((s, i) => (
                  <MemorySuggestionRow
                    key={`co-mem-${i}`}
                    label={s.name}
                    chip={s.chip}
                    groupHeading={s.groupHeading}
                    countLine={s.countLine}
                    startsGroup={s.startsGroup}
                    highlighted={i === ccHighlight}
                    isDark={isDark}
                    type={type}
                    ink={ink}
                    inkFaint={inkFaint}
                    onClick={() => applyCcSuggestion(s)}
                    finePointer={finePointer}
                    // co-memory-backfill — a comma-bearing store row emits a
                    // `shape:'typed'` row (chip + ×). A comma-FREE store line
                    // (owner ruling 2026-09-15) is a single value: it never
                    // gets a typed twin, so it is the row `shape:'individual'`
                    // + `src:'store'` that must carry the × — it is the ONLY
                    // row backing that line, and it is still forgettable.
                    // r5 — a `src:'derived'` individual (no store row of its
                    // own) now ALSO carries an ×, but it opens the
                    // informational "where does this come from" box instead
                    // of the forget confirm — same control, different action.
                    forgetAriaLabel={s.row.src === 'store'
                      ? MEMORY_FORGET_ARIA.replace('{name}', s.name)
                      : CO_DERIVED_INFO_ARIA.replace('{name}', s.name)}
                    onForget={s.row.src === 'store'
                      ? () => requestForgetMemoryRow(MEMORY_TYPES.CO, s.row)
                      : () => showCoDerivedInfo(s.row)}
                  />
                ))}
              </div>
            )}
            {/* P4 — the doctor writes FIRST; the shortcut sits under the box,
                capped at 3 with the rest one tap away. */}
            {recentCoPills.length > 0 && (
              <div style={shortcutRow}>
                <span style={{ fontSize: type.hint, color: inkFaint, fontFamily: 'system-ui, sans-serif' }}>
                  {C_O_MORE_PILLS.rowLabel}
                </span>
                {recentCoPills.slice(0, ccPillsExpanded ? 5 : 3).map((tc, ti) => {
                  const alreadyUsed = chiefComplaint.toLowerCase().includes(tc.v.toLowerCase());
                  const label = tc.v;
                  return (
                    <button key={`ccp-${ti}`} onClick={() => {
                      if (alreadyUsed || !label) return;
                      ccUsedRef.current.add(label.toLowerCase());
                      setChiefComplaint(prev => {
                        const t = prev.trim();
                        return t ? `${t}, ${label}` : label;
                      });
                    }}
                      style={{
                        padding: '0.35rem 0.75rem', borderRadius: '2rem', fontSize: type.chip, fontWeight: 600,
                        border: `1px solid ${alreadyUsed ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)') : (isDark ? 'rgba(96,165,250,0.3)' : 'rgba(37,99,235,0.15)')}`,
                        background: alreadyUsed ? (isDark ? 'rgba(255,255,255,0.03)' : '#f9fafb') : (isDark ? 'rgba(96,165,250,0.12)' : '#eff6ff'),
                        color: alreadyUsed ? inkFaint : (isDark ? '#60a5fa' : '#2563eb'),
                        cursor: alreadyUsed ? 'default' : 'pointer', fontFamily: 'system-ui, sans-serif',
                        opacity: alreadyUsed ? 0.5 : 1,
                      }}
                    title={label}
                    >{alreadyUsed ? '✓ ' : '+ '}{label}</button>
                  );
                })}
                {recentCoPills.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setCcPillsExpanded((v) => !v)}
                    aria-label={ccPillsExpanded ? C_O_MORE_PILLS.lessAria : C_O_MORE_PILLS.moreAria(recentCoPills.length - 3)}
                    title={ccPillsExpanded ? C_O_MORE_PILLS.lessAria : C_O_MORE_PILLS.moreAria(recentCoPills.length - 3)}
                    style={{
                      padding: '0.35rem 0.6rem', borderRadius: '2rem', cursor: 'pointer',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.22)' : '#cbd5e1'}`,
                      background: isDark ? '#1e293b' : '#f8fafc',
                      color: inkFaint, fontSize: type.chip, fontWeight: 600,
                      fontFamily: 'system-ui, sans-serif',
                    }}
                  >{ccPillsExpanded ? C_O_MORE_PILLS.less : C_O_MORE_PILLS.more(recentCoPills.length - 3)}</button>
                )}
              </div>
            )}
          </div>
    </PadField>
  );

  // Allergies (Variant A) — clone of the C/o field shell minus the memory
  // machinery (no suggestions, no shortcut pills, no `onForget`); learning
  // for this field is deferred. The amber border is the ONLY visual
  // differentiation — no icon, no banner, no red.
  //
  // P-FE-1 — the ratified `From patient record` hint shows only while the box
  // still holds the unedited patient-record value; the moment the doctor edits
  // it (or a draft / saved entry supplied something else) it stops being true
  // and disappears. Derived, never a second flag to keep in step.
  const showAllergiesFromRecordHint = (
    !allergiesDirty && !!patientAllergies && allergies === patientAllergies
  );
  const blockAllergies = (
    <PadField
      label="Allergies" isDark={isDark} dataTour="allergies"
      hint={showAllergiesFromRecordHint ? ALLERGIES_FROM_RECORD_HINT : null}
    >
      <textarea
        value={allergies}
        // The ONLY thing that sets `allergiesDirty` — a prefill, a draft
        // restore or an edit-in-place hydration must never license the patient
        // write-back (never-narrow guard).
        onChange={(e) => { setAllergies(e.target.value); setAllergiesDirty(true); }}
        onFocus={(e) => { if (e.target.rows < 2) e.target.rows = 2; }}
        placeholder="e.g. Penicillin, Sulpha drugs"
        rows={1}
        maxLength={500}
        className="rx-field"
        style={{
          ...fieldBox, resize: 'none',
          border: `1px solid ${isDark ? 'rgba(217,119,6,0.4)' : 'rgba(217,119,6,0.35)'}`,
        }}
      />
    </PadField>
  );

  // One heading treatment for every group in the picker, and one option chip —
  // the groups differ by CONTENT, never by chrome.
  const symptomGroupHeading = {
    fontSize: type.section, fontWeight: 700, color: inkFaint, textTransform: 'uppercase',
    letterSpacing: '0.04em', marginBottom: '0.2rem', fontFamily: 'system-ui, sans-serif',
  };
  const symptomOption = (sym, groupKey) => {
    const sel = selectedSymptoms.has(sym);
    const highlighted = symptomGroups.flat[symptomHighlight]?.name === sym;
    // FE-12 — only the "yours" (memory) group gets a forget ×; specialisation
    // and common are static lists, nothing to forget.
    if (groupKey === 'yours') {
      return (
        <SymptomYoursChip
          key={`yours-${sym}`}
          sym={sym} selected={sel} highlighted={highlighted} finePointer={finePointer}
          onToggle={() => toggleSymptom(sym)}
          onForget={() => requestForgetMemoryRow(
            MEMORY_TYPES.SYMPTOM,
            symptomGroups.memGroup.rows.find((r) => r.v === sym) || { v: sym, c: 1, t: 0 },
          )}
          forgetAriaLabel={MEMORY_FORGET_ARIA.replace('{name}', sym)}
          isDark={isDark} type={type} inkLight={inkLight}
        />
      );
    }
    return (
      <button
        key={`${groupKey}-${sym}`}
        onClick={() => toggleSymptom(sym)}
        style={{
          padding: '0.3rem 0.6rem', borderRadius: '2rem', fontSize: type.chip,
          border: sel || highlighted
            ? `1.5px solid ${isDark ? '#fbbf24' : '#d97706'}`
            : `1px solid ${isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.12)'}`,
          background: sel || highlighted
            ? (isDark ? 'rgba(234,179,8,0.18)' : '#fef3c7')
            : (isDark ? '#1e293b' : '#f9fafb'),
          color: sel || highlighted ? (isDark ? '#fbbf24' : '#92400e') : inkLight,
          cursor: 'pointer', fontWeight: sel ? 600 : 400, fontFamily: 'system-ui, sans-serif',
        }}
      >{sel ? '✓ ' : ''}{sym}</button>
    );
  };

  const blockSymptoms = (
    <PadField label="Symptoms" tooltip={sectionTip(SECTION_INFO.symptoms)} isDark={isDark}>
        <div style={{ display: 'flex', alignItems: 'center', gap: space.item, flexWrap: 'wrap', minHeight: '1.5rem' }}>

          {/* Selected chips */}
          {[...selectedSymptoms].map(s => (
            <span key={s} className="rx-chip-hover" style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              padding: '0.35rem 0.75rem', borderRadius: '2rem',
              fontSize: type.chip, fontWeight: 600,
              background: isDark ? 'rgba(234,179,8,0.18)' : '#fef3c7',
              color: isDark ? '#fbbf24' : '#78350f',
              border: `1.5px solid ${isDark ? 'rgba(234,179,8,0.35)' : 'rgba(217,119,6,0.25)'}`,
              fontFamily: 'system-ui, sans-serif',
            }}>
              {s}
              <button onClick={() => toggleSymptom(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: type.chip, padding: 0, lineHeight: 1, opacity: 0.7 }}>✕</button>
            </span>
          ))}

          {/* + add button -> opens picker */}
          <div style={{ position: 'relative' }} ref={symptomPickerRef}>
            <button onClick={() => setShowSymptomPicker(!showSymptomPicker)}
              ref={symptomPickerAnchorRef}
              style={{
                padding: '0.35rem 0.75rem', borderRadius: '2rem',
                border: `1.5px dashed ${isDark ? 'rgba(234,179,8,0.4)' : 'rgba(217,119,6,0.35)'}`,
                background: isDark ? 'rgba(234,179,8,0.1)' : '#fffbeb',
                fontSize: type.chip, fontWeight: 600, color: isDark ? '#fbbf24' : '#92400e',
                cursor: 'pointer', fontFamily: 'system-ui, sans-serif',
              }}>
              + add
            </button>

            {showSymptomPicker && (
              <div data-testid="symptom-picker" style={{
                // D25/F5 — rect-anchored fixed (was absolute): the pad is
                // `overflow: hidden`, so the accordion could clip the template
                // group inside this panel.
                position: 'fixed',
                top: symptomPickerRect?.top ?? 0, left: symptomPickerRect?.left ?? 0,
                zIndex: 1000,
                background: isDark ? '#1f2937' : '#fff', border: `1px solid ${padBorder}`,
                borderRadius: '0.5rem', padding: '0.5rem', marginTop: '0.25rem',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: '16rem', maxWidth: '22rem',
                maxHeight: narrowHost ? 'min(14rem, 38vh)' : 'min(20rem, 50vh)', overflowY: 'auto',
              }}>
                {/* D23 — TYPING FIRST. One entry point, at the top: Enter adds
                    the highlighted suggestion or exactly what was typed, ↓/↑
                    walk the offered names, Esc clears the box. */}
                <input
                  type="text"
                  autoFocus
                  value={customSymptomInput}
                  aria-label={SYMPTOM_PICKER_COPY.inputPlaceholder}
                  placeholder={SYMPTOM_PICKER_COPY.inputPlaceholder}
                  className="rx-field"
                  data-testid="symptom-input"
                  onChange={(e) => { setCustomSymptomInput(e.target.value); setSymptomHighlight(-1); }}
                  onDoubleClick={openListOnDoubleClick(
                    TEMPLATE_CATEGORIES.SYMPTOMS,
                    () => { setShowSymptomPicker(false); setSymptomHighlight(-1); },
                  )}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setSymptomHighlight((h) => Math.min(h + 1, symptomGroups.flat.length - 1));
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setSymptomHighlight((h) => Math.max(h - 1, -1));
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      // Nothing highlighted → Enter still means "add exactly
                      // what I typed". A highlighted TEMPLATE row adds the
                      // whole set by the merge rules (never a replace).
                      const entry = symptomHighlight >= 0 ? symptomGroups.flat[symptomHighlight] : null;
                      if (entry?.seeAll) {
                        handleOpenTemplateList(
                          TEMPLATE_CATEGORIES.SYMPTOMS, symptomSeeAllRef.current, customSymptomInput.trim(),
                        );
                        setSymptomHighlight(-1);
                      } else if (entry?.template) {
                        applyTemplates([entry.template]);
                        setCustomSymptomInput('');
                        setSymptomHighlight(-1);
                      } else {
                        addTypedSymptom(entry?.name);
                      }
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setCustomSymptomInput(''); setSymptomHighlight(-1);
                    }
                  }}
                  style={{ ...fieldBox, padding: '0.35rem 0.55rem', fontSize: type.chip, fontFamily: 'system-ui, sans-serif', marginBottom: '0.4rem' }}
                />

                {/* D25 — the doctor's own SYMPTOMS templates: the picker's
                    FIRST group, in the same card the other three fields render,
                    narrowed by what is typed and reachable with ↓. Picking one
                    adds every symptom in it by the merge rules (already-selected
                    ones are left alone). No templates in this category → no
                    group and no heading. */}
                {symptomGroups.templates.rows.length > 0 && (
                  <div style={{ marginBottom: '0.375rem' }} data-testid="symptom-group-templates">
                    <TemplateGroupHeading label={symptomGroups.templates.heading} isDark={isDark} />
                    {symptomGroups.templates.rows.map((t) => (
                      <TemplateSuggestionRow
                        key={t.template_id}
                        template={t}
                        category={TEMPLATE_CATEGORIES.SYMPTOMS}
                        narrow={narrowHost}
                        highlighted={symptomGroups.flat[symptomHighlight]?.template === t}
                        isDark={isDark}
                        onPick={() => { applyTemplates([t]); setCustomSymptomInput(''); setSymptomHighlight(-1); }}
                      />
                    ))}
                    {symptomGroups.templates.seeAll > 0 && (
                      <TemplateSeeAllRow
                        count={symptomGroups.templates.seeAll}
                        category={TEMPLATE_CATEGORIES.SYMPTOMS}
                        isDark={isDark}
                        highlighted={!!symptomGroups.flat[symptomHighlight]?.seeAll}
                        rowRef={symptomSeeAllRef}
                        onClick={(e) => handleOpenTemplateList(
                          TEMPLATE_CATEGORIES.SYMPTOMS, e.currentTarget, customSymptomInput.trim(),
                        )}
                      />
                    )}
                  </div>
                )}

                {/* Your symptoms — the doctor's own MEMORY (FE-4), most used
                    first. Hidden (never disabled) for a role with no memory
                    chrome (D12), same gate every other field's memory group
                    uses. */}
                {canUseTemplates && (
                  <div style={{ marginBottom: '0.375rem' }} data-testid="symptom-group-yours">
                    <div style={symptomGroupHeading}>
                      {memoryGroupHeading(MEMORY_TYPES.SYMPTOM, refData?.memory?.owner)}
                    </div>
                    {symptomGroups.memTotal === 0 ? (
                      <div style={{ fontSize: type.hint, color: inkFaint, fontFamily: 'system-ui, sans-serif' }}>
                        {SYMPTOM_PICKER_COPY.yoursEmpty}
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: type.hint, color: inkFaint, marginBottom: '0.15rem', fontFamily: 'system-ui, sans-serif' }}>
                          {memoryCountLine(symptomGroups.memTotal, symptomGroups.yours.length, customSymptomInput)}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', maxHeight: '7rem', overflowY: 'auto' }}>
                          {symptomGroups.yours.map((sym) => symptomOption(sym, 'yours'))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* The SELECTED doctor's specialisation; nothing rendered when
                    that doctor has none. */}
                {symptomGroups.specialised.length > 0 && specialisationName(doctorSpecialization) && (
                  <div style={{ marginBottom: '0.375rem' }} data-testid="symptom-group-specialisation">
                    <div style={symptomGroupHeading}>
                      {/* PO ruling (D42) — the label's NAME part: "Cardiologist
                          symptoms", "M.S ENT symptoms". No label for the stored
                          value → the group above does not render at all, so
                          this can never print "undefined symptoms" or a raw
                          "MS_ENT symptoms". */}
                      {SYMPTOM_PICKER_COPY.groupSpecialisation(specialisationName(doctorSpecialization))}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                      {symptomGroups.specialised.map((sym) => symptomOption(sym, 'spec'))}
                    </div>
                  </div>
                )}

                {/* Common — TWO entries (D23); the other six static ones are
                    gone from this surface. */}
                {symptomGroups.common.length > 0 && (
                  <div style={{ marginBottom: '0.375rem' }} data-testid="symptom-group-common">
                    <div style={symptomGroupHeading}>{SYMPTOM_PICKER_COPY.groupCommon}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                      {symptomGroups.common.map((sym) => symptomOption(sym, 'common'))}
                    </div>
                  </div>
                )}

                {/* This visit's typed symptoms — toggle like the rest, so the
                    ✕ on the main row un-selects here instead of staying stuck. */}
                {customSymptoms.filter(sym => !knownSymptoms.has(sym)).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {customSymptoms.filter(sym => !knownSymptoms.has(sym)).map(sym => {
                      const sel = selectedSymptoms.has(sym);
                      return (
                        <button key={sym} onClick={() => toggleSymptom(sym)} style={{
                          padding: '0.3rem 0.6rem', borderRadius: '2rem', fontSize: type.chip,
                          border: sel ? `1.5px solid ${teal}` : `1px solid ${isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.12)'}`,
                          background: sel ? (isDark ? 'rgba(13,148,136,0.16)' : '#f0fdfa') : (isDark ? '#1e293b' : '#f9fafb'),
                          color: sel ? teal : inkFaint, cursor: 'pointer', fontWeight: sel ? 600 : 400, fontFamily: 'system-ui, sans-serif',
                        }}>{sel ? '✓ ' : ''}{sym}</button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
    </PadField>
  );

  const blockMedicines = (
    <>
        {/* P1 — the ONE hairline the pad keeps: the real content boundary. */}
        <SectionDivider isDark={isDark} />
        <PadField
          label="Medicines"
          icon={<span style={{ fontSize: '1.0625rem', fontWeight: 700, color: teal, fontFamily: "'Georgia', serif", lineHeight: 1 }}>℞</span>}
          tooltip={sectionTip(SECTION_INFO.medicines)}
          isDark={isDark}
          style={{ marginTop: space.item }}
        >

        {/* P3 — the 8 learned-medicine quick-pills are deliberately NOT
            rendered: the same medicines are already "★ Your pick" rows inside
            the medicine picker (rx-form-ux), and the band was the biggest block
            of noise above the box. The reference fetch still stores the list
            (`setTopMedicines`), so bringing the band back is one block. */}

        {/* ── Medicine Input (PRIMARY) ── */}
        <div data-tour="rx-input" style={{ position: 'relative' }}>
          <input
            ref={inputRef} type="text" value={rawInput}
            onChange={handleInputChange} onKeyDown={handleKeyDown}
            onFocus={() => { setMedBoxFocused(true); return rawInput.trim() ? updateSuggestions(rawInput) : showBrowseList(); }}
            onBlur={() => { suppressAutoOpenRef.current = false; setMedBoxFocused(false); }}
            onMouseDown={reopenIfAlreadyFocused(inputRef, () => {
              // FIX-6 — a click on the already-focused box is treated as a
              // real re-focus: D38's suppress clears (a NEW gesture, not the
              // one the warning replied to), then the SAME opener a focus runs.
              suppressAutoOpenRef.current = false;
              return rawInput.trim() ? updateSuggestions(rawInput) : showBrowseList();
            })}
            onDoubleClick={openListOnDoubleClick(
              TEMPLATE_CATEGORIES.MEDICINES, () => setShowSuggestions(false),
            )}
            placeholder='Type prescription... e.g. "tab pcm 650 1-0-1 5d af"'
            disabled={parsing}
            className="rx-field"
            data-testid="rx-medicine-input"
            style={{ ...medicineBox, letterSpacing: '0.01em' }}
          />
          {parsing && (
            <span style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', fontSize: type.hint, color: inkFaint }}>adding…</span>
          )}

          {/* Autocomplete with variant grouping — T2a: fixed-position popover
              anchored off the input's own rect (escapes the pad's
              overflow:hidden without a portal), real tall scroll region,
              keyboard-tracked, mouse-move (not mouseenter) hover. */}
          {showSuggestions && (() => {
            const isBrowseMode = !rawInput.trim();
            // D25 — the template group is NOT a medicine row: an empty medicine
            // list still says so (bk-887 / "no match") with the group above it.
            const hasMedicineRows = suggestions.some(
              (s) => s.source !== 'template_set' && s.source !== 'template_see_all');
            const query = rawInput.trim();
            return (
            <div ref={suggestionsRef} style={{
              position: 'fixed',
              top: suggPopoverRect?.top ?? 0, left: suggPopoverRect?.left ?? 0, width: suggPopoverRect?.width ?? undefined,
              zIndex: 1000,
              background: isDark ? '#1f2937' : '#fff', border: `1px solid ${padBorder}`,
              borderRadius: '0.5rem', maxHeight: 'min(26rem, 60vh)', overflowY: 'auto',
              boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
            }}>
              {browseError && isBrowseMode ? (
                // bk-927 — the browse fetch failed. Named honestly, never silent.
                <div style={{ padding: '0.75rem 0.875rem', fontFamily: 'system-ui, sans-serif' }}>
                  <div style={{ fontSize: type.section, fontWeight: 700, color: ink, marginBottom: '0.25rem' }}>
                    Medicine list did not load
                  </div>
                  <div style={{ fontSize: type.hint, color: inkFaint, marginBottom: '0.5rem' }}>
                    Type the medicine name — it prints exactly as typed. Or try again to load the list.
                  </div>
                  <button onClick={retryBrowseList}
                    style={{
                      padding: '0.35rem 0.75rem', borderRadius: '0.25rem', border: 'none', cursor: 'pointer',
                      background: teal, color: '#fff', fontSize: type.chip, fontWeight: 600,
                    }}
                  >Try again</button>
                </div>
              ) : (
                <>
                  {/* T2b — count line; additive, only once the server sends
                      `master_total` (absent → falls back to today's
                      search-only behaviour, no line at all). `shown` counts
                      MEDICINES only — Y is "medicines", not tests. */}
                  {(() => {
                    // FE-3B — the memory group carries its OWN count line
                    // (rendered by `MemorySuggestionRow` above its first row,
                    // "n saved" against `memory.caps`, not the master total),
                    // so it is excluded here exactly like the template group.
                    const shownMedicineCount = suggestions.filter(
                      (s) => s.source !== 'lab_catalog' && s.source !== 'template_set'
                        && s.source !== 'template_see_all' && s.source !== 'doctor_memory').length;
                    const line = countLineText(shownMedicineCount, masterTotal, isBrowseMode ? null : query);
                    return line ? (
                      <div style={{ padding: '0.4rem 0.875rem', fontSize: type.hint, color: inkFaint, borderBottom: `1px solid ${padBorder}`, fontFamily: 'system-ui, sans-serif' }}>
                        {line}
                      </div>
                    ) : null;
                  })()}
                  {suggestions.map((s, i) => {
                    // D10 — a WHOLE-SET row. Deliberately not a variant of the
                    // plain row: filled card, its own icon tile, the template's
                    // name where a drug name would be and a solid count pill, so
                    // a doctor can never add 3 medicines thinking he added 1.
                    if (s.source === 'template_set') {
                      // D25 — ONE row component for all four fields; the
                      // heading is `TEMPLATE_ROW_LABELS` (Medicine templates /
                      // Lab test templates), never a free-handed string.
                      return (
                        <div key={`set-${s.template.template_id}`}>
                          {s.startsGroup && (
                            <TemplateGroupHeading label={s.groupHeading} isDark={isDark} />
                          )}
                          <TemplateSuggestionRow
                            template={s.template}
                            category={s.category}
                            narrow={narrowHost}
                            highlighted={i === selectedSuggIdx}
                            isDark={isDark}
                            onPick={() => applySuggestion(s)}
                            rowRef={(el) => { suggItemRefs.current[i] = el; }}
                            onMouseMove={handleSuggMouseMove(i)}
                          />
                        </div>
                      );
                    }
                    // bk-989 — the opener is a real entry in this list, so ↓
                    // reaches it and Enter opens the list from the keyboard.
                    if (s.source === 'template_see_all') {
                      return (
                        <TemplateSeeAllRow
                          key={`see-all-${s.category}`}
                          count={s.count}
                          category={s.category}
                          isDark={isDark}
                          highlighted={i === selectedSuggIdx}
                          rowRef={(el) => { suggItemRefs.current[i] = el; }}
                          onMouseMove={handleSuggMouseMove(i)}
                          onClick={(e) => applySuggestion(s, e.currentTarget)}
                        />
                      );
                    }
                    // FE-3B/FE-10 — the doctor's own remembered medicines,
                    // reusing the SAME row component Diagnosis/C-O use (never
                    // copy-pasted per field). This is the ONLY "★ Your pick"
                    // render left — `badge`/`subtitle`/`variants` are the
                    // rich context the old, now-retired `doctor_history` row
                    // used to render from a SECOND derivation; here they all
                    // come from this same memory row (FE-1's `variants[]`).
                    if (s.source === 'doctor_memory') {
                      return (
                        <MemorySuggestionRow
                          key={`rx-mem-${i}`}
                          label={s.label}
                          groupHeading={s.groupHeading}
                          countLine={s.countLine}
                          startsGroup={s.startsGroup}
                          highlighted={i === selectedSuggIdx}
                          isDark={isDark}
                          type={type}
                          ink={ink}
                          inkFaint={inkFaint}
                          rowRef={(el) => { suggItemRefs.current[i] = el; }}
                          onMouseMove={handleSuggMouseMove(i)}
                          onClick={() => applySuggestion(s)}
                          icon={MEDICINE_ROW_ICON}
                          badge="★ Your pick"
                          subtitle={s.subtitle}
                          variants={s.variants}
                          finePointer={finePointer}
                          forgetAriaLabel={MEMORY_FORGET_ARIA.replace('{name}', s.label)}
                          onForget={() => requestForgetMemoryRow(MEMORY_TYPES.RX, s.row)}
                          onPickVariant={(v) => {
                            directAddMedicine({
                              name: s.label, raw_input: s.shorthand || s.label,
                              form: v.form, strength: v.strength,
                              dosage: v.dosage, duration: v.duration, timing: v.timing,
                            });
                            setRawInput(''); setShowSuggestions(false); focusInputSoon();
                          }}
                        />
                      );
                    }
                    const isLabRow = s.source === 'lab_catalog';
                    // LC2 — the heading + safety line render above the FIRST
                    // lab row whatever its index (R1 — a tests-only list, the
                    // commonest path to a test row, must still carry the
                    // "no order is placed" line). The divider border only
                    // shows when a medicine row sits above it to divide from.
                    const startsLabGroup = isLabRow && (i === 0 || suggestions[i - 1].source !== 'lab_catalog');
                    return (
                    <div key={`${s.label}-${i}`} data-source={s.source || 'unspecified'}>
                      {startsLabGroup && (
                        <div style={{ padding: '0.5rem 0.625rem 0.25rem', borderTop: i > 0 ? `1px solid ${padBorder}` : 'none', fontFamily: 'system-ui, sans-serif' }}>
                          <div style={{ fontSize: type.section, fontWeight: 700, color: inkFaint, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            Tests — printed, not ordered
                          </div>
                          <div style={{ fontSize: type.hint, color: inkFaint, marginTop: '0.1rem' }}>
                            Adds the test name to the prescription. No lab order is placed.
                          </div>
                        </div>
                      )}
                      <button onClick={() => applySuggestion(s)}
                        ref={(el) => { suggItemRefs.current[i] = el; }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%',
                          minHeight: '2.6rem', padding: '0.5rem 0.75rem', border: 'none',
                          background: i === selectedSuggIdx ? (isDark ? 'rgba(13,148,136,0.16)' : '#f0fdfa') : 'transparent',
                          cursor: 'pointer', fontSize: type.row, color: ink, textAlign: 'left', fontFamily: 'system-ui, sans-serif',
                        }}
                        onMouseMove={handleSuggMouseMove(i)}
                      >
                        <span style={{ fontSize: type.row }}>{isLabRow ? '🧪' : MEDICINE_ROW_ICON}</span>
                        <span style={{ fontWeight: 500, flex: 1 }}>
                          {s.label}
                        </span>
                        {/* Muted "Test" tag ONLY — never a price and never an order
                            affordance: picking this writes text, so nothing on the
                            row may read as an order. `inkFaint` is already
                            isDark-paired, so it stays legible in both modes. */}
                        {isLabRow && <span style={{ fontSize: type.badge, color: inkFaint, fontWeight: 600 }}>Test</span>}
                      </button>
                    </div>
                    );
                  })}
                  {/* The medicine list's own empty states — they now render
                      BELOW the template group (which is not a medicine row), so
                      a doctor with templates and an org with no medicine list
                      still gets both truths. */}
                  {!hasMedicineRows && isBrowseMode && (
                    // bk-887 — the organisation has no medicine list yet.
                    <div style={{ padding: '0.75rem 0.875rem', fontFamily: 'system-ui, sans-serif' }}>
                      <div style={{ fontSize: type.section, fontWeight: 700, color: ink, marginBottom: '0.25rem' }}>
                        No medicine list yet
                      </div>
                      <div style={{ fontSize: type.hint, color: inkFaint }}>
                        Type the medicine name — it prints exactly as typed. Your organisation admin can set up the medicine list.
                      </div>
                    </div>
                  )}
                  {!hasMedicineRows && !isBrowseMode && (
                    // 1b — a search that matches nothing.
                    <div style={{ padding: '0.75rem 0.875rem', fontFamily: 'system-ui, sans-serif' }}>
                      <div style={{ fontSize: type.hint, color: inkFaint }}>
                        No match for "{query}" — it prints exactly as typed.
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            );
          })()}
        </div>

        {/* P6 — the hint sits directly under the box while the doctor is in it
            (or before anything is typed), then gets out of the way. */}
        {(medBoxFocused || !rawInput) && (
          <div data-testid="rx-medicine-hint" style={{ ...shortcutRow, fontSize: type.hint, color: inkFaint, fontFamily: 'system-ui, sans-serif', gap: '0.75rem' }}>
            <span><b>Enter</b> to add</span>
            {showDoubleClickHint(rawInput) && (
              <span data-testid="double-click-hint">{DOUBLE_CLICK_HINT}</span>
            )}
            <span>type <b>“cbc”</b> for a lab test</span>
            <span><b>Ctrl+Enter</b> to save</span>
          </div>
        )}

        {/* ── Investigation Candidates — parser knows it's an investigation, doctor picks which one ── */}
        {investigationPicks.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {investigationPicks.map((item, pi) => (
              <div key={`inv-pick-${pi}`} style={{
                padding: '0.5rem 0.625rem', borderRadius: '0.375rem',
                background: isDark ? 'rgba(13,148,136,0.1)' : '#f0fdfa',
                border: `1px solid ${isDark ? 'rgba(13,148,136,0.25)' : 'rgba(13,148,136,0.2)'}`,
                fontFamily: 'system-ui, sans-serif',
              }}>
                <div style={{ fontSize: type.invText, fontWeight: 600, color: teal, marginBottom: '0.375rem' }}>
                  🧪 "{item.input}" — select investigation:
                </div>
                <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                  {item.candidates.map((cand, ci) => (
                    <button key={ci} onClick={() => {
                      setInvestigations(prev => prev.includes(cand) ? prev : [...prev, cand]);
                      setInvestigationPicks(prev => prev.filter((_, j) => j !== pi));
                    }}
                      style={{
                        padding: '0.3rem 0.6rem', borderRadius: '0.25rem',
                        border: `1px solid ${teal}`, background: tealBg,
                        fontSize: type.invChip, fontWeight: 500, color: teal, cursor: 'pointer',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => e.target.style.background = isDark ? 'rgba(13,148,136,0.24)' : '#ccfbf1'}
                      onMouseLeave={e => e.target.style.background = tealBg}
                    >{cand}</button>
                  ))}
                  <button onClick={() => {
                    const nameTitle = item.input.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                    setInvestigations(prev => prev.includes(nameTitle) ? prev : [...prev, nameTitle]);
                    setInvestigationPicks(prev => prev.filter((_, j) => j !== pi));
                  }}
                    style={{
                      padding: '0.3rem 0.6rem', borderRadius: '0.25rem',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)'}`,
                      background: isDark ? 'rgba(255,255,255,0.04)' : '#f9fafb',
                      fontSize: type.invChip, fontWeight: 500, color: inkFaint, cursor: 'pointer',
                      fontStyle: 'italic',
                    }}
                  >None — add as "{item.input}"</button>
                  <button onClick={() => setInvestigationPicks(prev => prev.filter((_, j) => j !== pi))}
                    style={{
                      padding: '0.3rem 0.45rem', borderRadius: '0.25rem',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)'}`,
                      background: isDark ? 'rgba(255,255,255,0.04)' : '#f9fafb',
                      fontSize: type.invChip, fontWeight: 500, color: inkFaint, cursor: 'pointer',
                    }}
                  >✕</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Investigation Likely — parser thinks it's an investigation, confirm or override ── */}
        {investigationLikely.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {investigationLikely.map((item, li) => (
              <div key={`inv-likely-${li}`} style={{
                padding: '0.5rem 0.625rem', borderRadius: '0.375rem',
                background: isDark ? 'rgba(13,148,136,0.08)' : '#f0fdfa',
                border: `1px solid ${isDark ? 'rgba(13,148,136,0.2)' : 'rgba(13,148,136,0.15)'}`,
                fontFamily: 'system-ui, sans-serif',
              }}>
                <div style={{ fontSize: type.invText, fontWeight: 600, color: teal, marginBottom: '0.375rem' }}>
                  🧪 "{item.input}" looks like an investigation
                </div>
                <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                  <button onClick={() => {
                    const nameTitle = item.input.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                    setInvestigations(prev => prev.includes(nameTitle) ? prev : [...prev, nameTitle]);
                    setInvestigationLikely(prev => prev.filter((_, j) => j !== li));
                  }}
                    style={{
                      padding: '0.3rem 0.75rem', borderRadius: '0.25rem',
                      border: `1px solid ${teal}`, background: tealBg,
                      fontSize: type.invChip, fontWeight: 600, color: teal, cursor: 'pointer',
                    }}
                  >✅ Add as Investigation</button>
                  <button onClick={() => {
                    const nameTitle = item.input.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                    setMedicines(prev => [...prev, {
                      _id: Date.now() + Math.random(),
                      medicine: nameTitle, medicine_input: item.input,
                      strength: null, form: null, dosage_schedule: null,
                      frequency: null, timing: null, duration: null,
                      route: null, notes: null, is_resolved: false,
                      _needsReview: true, _warnings: ['Manually added — verify name'],
                    }]);
                    setEditingIdx(medicines.length);
                    setInvestigationLikely(prev => prev.filter((_, j) => j !== li));
                  }}
                    style={{
                      padding: '0.3rem 0.75rem', borderRadius: '0.25rem',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)'}`,
                      background: isDark ? 'rgba(255,255,255,0.04)' : '#f9fafb',
                      fontSize: type.invChip, fontWeight: 500, color: inkFaint, cursor: 'pointer',
                    }}
                  >💊 No, it's a Medicine</button>
                  <button onClick={() => setInvestigationLikely(prev => prev.filter((_, j) => j !== li))}
                    style={{
                      padding: '0.3rem 0.45rem', borderRadius: '0.25rem',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)'}`,
                      background: isDark ? 'rgba(255,255,255,0.04)' : '#f9fafb',
                      fontSize: type.invChip, fontWeight: 500, color: inkFaint, cursor: 'pointer',
                    }}
                  >✕</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Unrecognized Items — truly ambiguous, equal-weight classification ── */}
        {unrecognizedItems.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {unrecognizedItems.map((item, ui) => (
              <div key={`unrecognized-${ui}`} style={{
                padding: '0.5rem 0.625rem', borderRadius: '0.375rem',
                background: isDark ? 'rgba(217,119,6,0.1)' : '#fffbeb',
                border: `1px solid ${isDark ? 'rgba(217,119,6,0.25)' : 'rgba(217,119,6,0.2)'}`,
                fontFamily: 'system-ui, sans-serif',
              }}>
                <div style={{ fontSize: type.invText, fontWeight: 600, color: isDark ? '#fbbf24' : '#92400e', marginBottom: '0.375rem' }}>
                  ⚠ "{item.input}" — not recognized
                </div>
                {item.warning && (
                  <div style={{ fontSize: type.invChip, color: isDark ? '#fbbf24' : '#92400e', opacity: 0.85, marginBottom: '0.375rem' }}>
                    {item.warning}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                  <button onClick={() => {
                    const nameTitle = item.input.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                    setMedicines(prev => [...prev, {
                      _id: Date.now() + Math.random(),
                      medicine: nameTitle, medicine_input: item.input,
                      strength: null, form: null, dosage_schedule: null,
                      frequency: null, timing: null, duration: null,
                      route: null, notes: null, is_resolved: false,
                      _needsReview: true, _warnings: ['Manually added — verify name'],
                    }]);
                    setEditingIdx(medicines.length);
                    setUnrecognizedItems(prev => prev.filter((_, j) => j !== ui));
                  }}
                    style={{
                      padding: '0.3rem 0.75rem', borderRadius: '0.25rem',
                      border: `1px solid ${teal}`, background: tealBg,
                      fontSize: type.invChip, fontWeight: 600, color: teal, cursor: 'pointer',
                    }}
                  >💊 Add as Medicine</button>
                  <button onClick={() => {
                    const nameTitle = item.input.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                    setInvestigations(prev => prev.includes(nameTitle) ? prev : [...prev, nameTitle]);
                    setUnrecognizedItems(prev => prev.filter((_, j) => j !== ui));
                  }}
                    style={{
                      padding: '0.3rem 0.75rem', borderRadius: '0.25rem',
                      border: `1px solid ${isDark ? '#fbbf24' : '#d97706'}`,
                      background: isDark ? 'rgba(245,158,11,0.1)' : '#fef3c7',
                      fontSize: type.invChip, fontWeight: 600,
                      color: isDark ? '#fbbf24' : '#92400e', cursor: 'pointer',
                    }}
                  >🧪 Add as Investigation</button>
                  <button onClick={() => setUnrecognizedItems(prev => prev.filter((_, j) => j !== ui))}
                    style={{
                      padding: '0.3rem 0.75rem', borderRadius: '0.25rem',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)'}`,
                      background: isDark ? 'rgba(255,255,255,0.04)' : '#f9fafb',
                      fontSize: type.invChip, fontWeight: 500,
                      color: inkFaint, cursor: 'pointer',
                    }}
                  >✕ Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Medicine List (numbered like a real prescription) ── */}
        {hasMeds && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
            {medicines.map((med, idx) => (
              <div key={med._id || idx}>
                {/* Summary line */}
                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: '0.375rem',
                  // P5 — the medicine rows ARE the prescription: more vertical
                  // room and full ink, while the shortcuts around them are muted.
                  padding: '0.5rem 0',
                  borderBottom: `1px solid ${med._needsReview ? (isDark ? 'rgba(217,119,6,0.3)' : 'rgba(217,119,6,0.25)') : lineColor}`,
                  background: med._needsReview ? (isDark ? 'rgba(217,119,6,0.04)' : 'rgba(217,119,6,0.02)') : undefined,
                  borderRadius: med._needsReview ? '0.25rem' : undefined,
                  cursor: 'pointer',
                }} onClick={() => openEditor(idx)}>
                  {/* Remove — owner addition: START of the row, before the
                      medicine name (was a trailing ✕). No confirmation dialog —
                      doctors add/remove rows constantly. */}
                  <button onClick={(e) => { e.stopPropagation(); removeMedicine(idx); }}
                    aria-label={`Remove ${med.medicine || med.medicine_input || 'medicine'}`}
                    title="Remove"
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: type.rowAction, height: type.rowAction, flexShrink: 0,
                      borderRadius: '0.375rem', border: 'none', cursor: 'pointer',
                      background: isDark ? 'rgba(239,68,68,0.2)' : '#fee2e2',
                      color: '#ef4444', fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1,
                    }}
                  >−</button>

                  {/* Number */}
                  <span style={{ fontSize: type.medMeta, fontWeight: 600, color: teal, minWidth: '1.125rem', fontFamily: 'system-ui, sans-serif' }}>{idx + 1}.</span>

                  {/* Medicine details — natural flow */}
                  <div style={{ flex: 1, display: 'flex', alignItems: 'baseline', gap: '0.375rem', flexWrap: 'wrap', fontSize: type.medName, color: ink, fontFamily: "'Georgia', serif" }}>
                    <span style={{ fontWeight: 700, color: med._needsReview ? '#d97706' : undefined }}>{med.medicine || med.medicine_input || 'Unknown'}</span>
                    {med._needsReview && <span style={{ fontSize: type.badge, fontWeight: 700, color: '#d97706', background: isDark ? 'rgba(217,119,6,0.18)' : '#fef3c7', padding: '0.1rem 0.4rem', borderRadius: '2rem', fontFamily: 'system-ui, sans-serif', border: `1px solid ${isDark ? 'rgba(217,119,6,0.25)' : 'rgba(217,119,6,0.2)'}` }}>⚠ verify</span>}
                    {/* Auto-suggested badge */}
                    {med._autoSuggested && !med._needsReview && <span style={{ fontSize: type.badge, fontWeight: 600, color: isDark ? '#34d399' : '#059669', background: isDark ? 'rgba(52,211,153,0.18)' : '#ecfdf5', padding: '0.1rem 0.4rem', borderRadius: '2rem', fontFamily: 'system-ui, sans-serif', border: `1px solid ${isDark ? 'rgba(52,211,153,0.2)' : 'rgba(5,150,105,0.15)'}` }}>✦ Auto-suggested</span>}
                    {/* Typed name resolved against the org's medicine list —
                        same chip pattern as the badges above, ratified copy. */}
                    {med.medicine_source === 'master' && <span title={MASTER_SOURCE_LABEL} style={{ fontSize: type.badge, fontWeight: 600, color: teal, background: tealBg, padding: '0.1rem 0.4rem', borderRadius: '2rem', fontFamily: 'system-ui, sans-serif', border: `1px solid ${isDark ? 'rgba(13,148,136,0.3)' : 'rgba(13,148,136,0.2)'}` }}>{MASTER_SOURCE_LABEL}</span>}
                    {/* Source indicator from top suggestion */}
                    {med._suggestions?.length > 0 && (() => {
                      const topSrc = med._suggestions[0]?.source;
                      const srcLabel = topSrc === 'doctor_history' ? 'Your preference' : topSrc === 'ai_corrected' ? 'AI corrected' : null;
                      const srcColor = topSrc === 'doctor_history' ? (isDark ? '#818cf8' : '#6366f1') : topSrc === 'ai_corrected' ? (isDark ? '#c084fc' : '#9333ea') : null;
                      return srcLabel ? <span style={{ fontSize: type.badge, fontWeight: 600, color: srcColor, fontFamily: 'system-ui, sans-serif', opacity: 0.85 }}>{srcLabel}</span> : null;
                    })()}
                    {med.strength && <span style={{ fontWeight: 600, color: teal }}>{med.strength}</span>}
                    {med.form && <span style={{ fontStyle: 'italic', color: inkLight, fontSize: type.medMeta }}>{med.form}</span>}
                    {med.dosage_schedule && <span style={{ fontWeight: 600, letterSpacing: '0.05em' }}>{formatDoseSchedule(med.dosage_schedule)}</span>}
                    {med.timing && <span style={{ color: inkLight }}>{med.timing}</span>}
                    {med.duration && <span style={{ color: inkLight }}>{med.duration}</span>}
                    {med.frequency && <span style={{ fontSize: type.medMeta, color: inkFaint }}>({med.frequency})</span>}
                    {med.route && <span style={{ fontSize: type.medMeta, color: '#8b5cf6', fontFamily: 'system-ui, sans-serif' }}>{med.route}</span>}
                    {med.notes && <span style={{ fontSize: type.medMeta, fontStyle: 'italic', color: isDark ? '#93c5fd' : '#2563eb', fontFamily: 'system-ui, sans-serif' }}>{med.notes}</span>}
                  </div>

                  {/* Edit — labelled control (T3-LITE), replaces the bare ✎ glyph. */}
                  <button onClick={(e) => { e.stopPropagation(); openEditor(idx); }}
                    ref={(el) => { editButtonRefs.current[idx] = el; }}
                    aria-label={`Edit ${med.medicine || med.medicine_input || 'medicine'}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0,
                      height: type.rowAction, padding: '0 0.625rem',
                      borderRadius: '0.375rem', border: 'none', cursor: 'pointer',
                      background: isDark ? 'rgba(13,148,136,0.22)' : tealBg,
                      color: teal, fontSize: type.chip, fontWeight: 600, fontFamily: 'system-ui, sans-serif',
                    }}
                    title="Edit"
                  >✎ Edit</button>
                </div>

                {/* Per-medicine warnings (missing duration, unresolved name, no-unit
                    strength, etc.) and the blank-field note — both derived fresh
                    from `med` every render by rowNotices (single source shared
                    with the print confirmation, never stale). */}
                {(() => {
                  const { warnings: rowWarnings, blankNote } = rowNotices(med);
                  if (editingIdx === idx) return null;
                  return (
                    <>
                      {rowWarnings.length > 0 && (
                        // FE-28 — assertive: a screen reader announces the
                        // warning the moment the row lands, and the ref is what
                        // the pad scrolls to (last one wins = the newest row).
                        <div
                          ref={(el) => { if (el) warningRowRef.current = el; }}
                          role="alert"
                          data-testid="row-warning"
                          style={{ padding: '0.15rem 0 0.15rem 1.25rem', fontSize: type.note, color: warnAmber, fontFamily: 'system-ui, sans-serif', lineHeight: 1.6 }}>
                          {rowWarnings.map((w, wi) => <div key={wi}>⚠ {w}</div>)}
                        </div>
                      )}
                      {/* Blank-field note — owner asked for orange, so it shares
                          `warnAmber` with the warning line right above rather than
                          introducing a second one. Still a NOTE, not an error: no
                          icon, no exclamation, no bold, never red — a doctor may
                          legitimately print with blanks. This is the ONLY guard
                          before print — never made quieter than `type.note`. */}
                      {blankNote && (
                        <div
                          ref={(el) => { if (el) warningRowRef.current = el; }}
                          role="alert"
                          data-testid="row-warning"
                          style={{ padding: '0.15rem 0 0.15rem 1.25rem', fontSize: type.note, color: warnAmber, fontFamily: 'system-ui, sans-serif', lineHeight: 1.6 }}>
                          {blankNote}
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Duplicate name in the org's medicine list — the backend fills
                    nothing and returns the rival rows. The warning right above
                    already says to pick one, so these carry no label of their
                    own: each chip is just the row's own data (strength / form /
                    code) so the doctor can tell them apart. Picking one runs
                    the shared master fill. */}
                {med.medicine_candidates?.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', padding: '0.1rem 0 0.35rem 1.25rem' }}>
                    {med.medicine_candidates.map((c, ci) => (
                      <button key={c.code || ci} onClick={() => fillFromMasterCandidate(idx, c)}
                        style={{
                          padding: '0.3rem 0.6rem', borderRadius: '2rem',
                          fontSize: type.chip, fontWeight: 500,
                          border: `1.5px solid ${isDark ? 'rgba(13,148,136,0.35)' : 'rgba(13,148,136,0.25)'}`,
                          background: isDark ? 'rgba(13,148,136,0.12)' : '#f0fdfa',
                          color: ink, cursor: 'pointer', fontFamily: 'system-ui, sans-serif',
                        }}>
                        {[c.name, [c.strength_value, c.strength_unit].filter(isPresent).join(' '), c.type, c.code]
                          .filter(Boolean).join(' · ')}
                      </button>
                    ))}
                  </div>
                )}

                {/* Inline edit panel */}
                {editingIdx === idx && (
                  <div ref={(el) => { editorRefs.current[idx] = el; }} style={{
                    padding: '0 0.625rem 0.5rem 1.25rem',
                    display: 'flex', flexDirection: 'column', gap: '0.5rem',
                    fontFamily: 'system-ui, sans-serif',
                    background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.008)',
                    borderRadius: '0 0 0.375rem 0.375rem',
                  }}>
                    {/* T3-LITE — sticky "Editing: …" bar so the doctor always
                        knows which row a long editor belongs to; Done closes
                        and returns focus to that row's Edit button. */}
                    <div style={{
                      position: 'sticky', top: 0, zIndex: 5,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: '0.5rem', padding: '0.5rem 0', marginBottom: '-0.125rem',
                      background: isDark ? '#1e293b' : '#fefdfb',
                      borderBottom: `1px solid ${padBorder}`,
                    }}>
                      <span style={{ fontSize: type.section, fontWeight: 700, color: ink, textTransform: 'none' }}>
                        Editing: {med.medicine || med.medicine_input || 'medicine'}
                      </span>
                      <button
                        onClick={() => { setEditingIdx(null); editButtonRefs.current[idx]?.focus(); }}
                        aria-label={`Done editing ${med.medicine || med.medicine_input || 'medicine'}`}
                        style={{
                          padding: '0.25rem 0.75rem', borderRadius: '0.25rem', border: 'none', cursor: 'pointer',
                          background: tealBg, color: teal, fontSize: type.chip, fontWeight: 600,
                        }}
                      >Done</button>
                    </div>
                    {/* Suggestion picker — shown when parser returns multiple alternatives */}
                    {med._suggestions && med._suggestions.length > 1 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <div style={{ fontSize: type.section, fontWeight: 600, color: inkFaint, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Suggestions — pick one</div>
                        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                          {med._suggestions.map((sg, si) => {
                            const isActive = med.medicine === sg.name;
                            const srcLabel = sg.source === 'doctor_history' ? 'Your preference' : sg.source === 'ai_corrected' ? 'AI corrected' : sg.source === 'system' ? 'System' : '';
                            const srcColor = sg.source === 'doctor_history' ? (isDark ? '#818cf8' : '#6366f1') : sg.source === 'ai_corrected' ? (isDark ? '#c084fc' : '#9333ea') : (isDark ? '#94a3b8' : '#64748b');
                            return (
                              <button key={si} onClick={() => updateMedicine(idx, 'medicine', sg.name)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '0.25rem',
                                  padding: '0.3rem 0.6rem', borderRadius: '2rem',
                                  fontSize: type.chip, fontWeight: isActive ? 700 : 500,
                                  border: `1.5px solid ${isActive ? teal : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)')}`,
                                  background: isActive ? tealBg : (isDark ? 'transparent' : '#f9fafb'),
                                  color: isActive ? teal : ink,
                                  cursor: 'pointer', fontFamily: 'system-ui, sans-serif', transition: 'all 0.15s',
                                }}>
                                <span>{sg.name}</span>
                                {srcLabel && <span style={{ fontSize: type.badge, color: srcColor, opacity: 0.8 }}>{srcLabel}</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {/* Row 1: Medicine name + Strength + Form */}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: 2, minWidth: '9rem' }}>
                        <div style={fieldLabel}>Medicine</div>
                        <input type="text" value={med.medicine || ''} onChange={(e) => updateMedicine(idx, 'medicine', e.target.value)} placeholder="Name"
                          ref={(el) => { editorFirstFieldRefs.current[idx] = el; }}
                          style={{ ...fieldInput(med._needsReview), fontWeight: 600 }} />
                      </div>
                      <div style={{ flex: 1, minWidth: '6rem' }}>
                        <div style={fieldLabel}>Strength</div>
                        <input type="text" value={med.strength || ''} onChange={(e) => updateMedicine(idx, 'strength', e.target.value)} placeholder="Strength"
                          style={fieldInput()} />
                      </div>
                      <div style={{ flex: 1, minWidth: '6rem' }}>
                        <div style={fieldLabel}>Form</div>
                        <select value={med.form || ''} onChange={(e) => updateMedicine(idx, 'form', e.target.value)}
                          style={fieldInput()}>
                          <option value="">—</option>
                          {['Tablet','Capsule','Syrup','Suspension','Injection','Drops','Cream','Ointment','Gel','Lotion','Inhaler','Nebulization','Spray','Powder','Sachet','Patch','Paste','Vaccine'].map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                      </div>
                    </div>

                    {/* Row 2: Dosage schedule — labeled M / A / N boxes */}
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div>
                        <div style={fieldLabel}>Dosage (M-A-N)</div>
                        <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'flex-start' }}>
                          {[{ key: 'morning', label: 'M', color: '#f59e0b' }, { key: 'afternoon', label: 'A', color: '#f97316' }, { key: 'night', label: 'N', color: '#6366f1' }].map(slot => {
                            const slotHasError = !!med._doseErrors?.[slot.key];
                            return (
                              <div key={slot.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.15rem' }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: slot.color }}>{slot.label}</span>
                                <DoseSlotInput
                                  value={med.dosage_schedule?.[slot.key] ?? 0}
                                  onChange={(parsed) => updateDosageSlot(idx, slot.key, parsed)}
                                  hasError={slotHasError}
                                  testId={`dose-${slot.key}`}
                                  ariaLabel={`${slot.label === 'M' ? 'Morning' : slot.label === 'A' ? 'Afternoon' : 'Night'} dose`}
                                  boxStyle={manBox(slotHasError)}
                                  stepBtnStyle={manStepBtn}
                                />
                                {slotHasError && (
                                  <span style={{ fontSize: '0.75rem', color: warnAmber, maxWidth: '7rem', textAlign: 'center', lineHeight: 1.2 }}>
                                    {DOSE_INVALID_COPY}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div style={{ flex: 1, minWidth: '7rem' }}>
                        <div style={fieldLabel}>Timing</div>
                        <select value={med.timing || ''} onChange={(e) => updateMedicine(idx, 'timing', e.target.value)}
                          style={fieldInput()}>
                          <option value="">—</option>
                          {['Before Food','After Food','With Food','Before Breakfast','Before Lunch','Before Dinner','At Bedtime','Empty Stomach','With Milk','SOS'].map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div style={{ flex: 1, minWidth: '6rem' }}>
                        <div style={fieldLabel}>Frequency</div>
                        <input type="text" value={med.frequency || ''} onChange={(e) => updateMedicine(idx, 'frequency', e.target.value)} placeholder="e.g. Twice Daily"
                          style={fieldInput()} />
                      </div>
                    </div>

                    {/* Row 3: Duration + Route + Instructions */}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '6rem' }}>
                        <div style={fieldLabel}>Duration</div>
                        <input type="text" value={med.duration || ''} onChange={(e) => updateMedicine(idx, 'duration', e.target.value)} placeholder="e.g. 5 Days"
                          style={fieldInput()} />
                      </div>
                      <div style={{ flex: 1, minWidth: '6rem' }}>
                        <div style={fieldLabel}>Route</div>
                        <select value={med.route || ''} onChange={(e) => updateMedicine(idx, 'route', e.target.value)}
                          style={fieldInput()}>
                          <option value="">—</option>
                          {['Oral','Topical','IV','IM','SC','Sublingual','Nasal','Rectal','Inhalation'].map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div style={{ flex: 2, minWidth: '8rem', position: 'relative' }}>
                        <div style={fieldLabel}>Instructions</div>
                        <input type="text" value={med.notes || ''}
                          onChange={(e) => { updateMedicine(idx, 'notes', e.target.value); updateMedNoteSuggestions(e.target.value, idx); }}
                          onFocus={(e) => { if (e.target.value.trim().length >= 2) updateMedNoteSuggestions(e.target.value, idx); }}
                          onBlur={() => setTimeout(() => { setShowMedNoteSugg(false); setEditingMedNoteIdx(null); }, 200)}
                          onKeyDown={(e) => { if (e.key === 'Escape') { setShowMedNoteSugg(false); } }}
                          placeholder="e.g. Apply locally"
                          style={fieldInput()} />
                        {showMedNoteSugg && editingMedNoteIdx === idx && medNoteSugg.length > 0 && (
                          <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 60,
                            background: isDark ? '#1f2937' : '#fff', border: `1px solid ${padBorder}`,
                            borderRadius: '0 0 0.375rem 0.375rem', maxHeight: '6rem', overflowY: 'auto',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          }}>
                            {medNoteSugg.map((ins, ii) => (
                              <button key={ii} onClick={() => { updateMedicine(idx, 'notes', ins); setShowMedNoteSugg(false); }}
                                style={{
                                  display: 'block', width: '100%', padding: '0.3rem 0.5rem', border: 'none',
                                  background: 'transparent', cursor: 'pointer', fontSize: type.hint,
                                  color: ink, textAlign: 'left', fontFamily: 'system-ui, sans-serif',
                                }}
                                onMouseEnter={e => e.target.style.background = isDark ? 'rgba(13,148,136,0.1)' : 'rgba(13,148,136,0.04)'}
                                onMouseLeave={e => e.target.style.background = 'transparent'}
                              >{ins}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Confirm / Done button */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button onClick={() => { updateMedicine(idx, '_needsReview', false); updateMedicine(idx, '_warnings', []); setEditingIdx(null); }}
                        style={{
                          padding: '0.4rem 0.875rem', borderRadius: '0.25rem', border: 'none',
                          background: med._needsReview ? teal : tealBg,
                          color: med._needsReview ? '#fff' : teal,
                          fontSize: type.chip, fontWeight: 700, cursor: 'pointer',
                          boxShadow: med._needsReview ? '0 1px 4px rgba(13,148,136,0.3)' : 'none',
                        }}>
                        {med._needsReview ? '✓ Confirm & Close' : 'Done'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Clear all */}
            {medicines.length > 1 && (
              <div style={{ textAlign: 'right', marginTop: '0.125rem' }}>
                <button onClick={() => {
                  setDialog({
                    title: 'Clear All Medicines',
                    message: `Remove all ${medicines.length} medicines from the prescription?`,
                    confirmLabel: 'Clear All',
                    confirmDanger: true,
                    cancelLabel: 'Keep',
                    onConfirm: () => { pushUndo(); setMedicines([]); setEditingIdx(null); setDialog(null); },
                    onCancel: () => setDialog(null),
                  });
                }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: type.hint, color: '#ef4444', opacity: 0.75, fontFamily: 'system-ui, sans-serif' }}>
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}
        </PadField>
    </>
  );

  // FE-4 — the lab-OFF Tests strip's own memory group (`memory.types.INV`),
  // filtered by whatever is typed. `alreadySelectedText` keeps an
  // already-added test from being offered again — the same option Diagnosis
  // and C/O use for their own current value, repurposed here for the
  // investigations ARRAY (joined, since the option does a substring check).
  const testMemGroup = useMemo(() => (
    canUseTemplates
      ? memoryFieldRows(refData?.memory, MEMORY_TYPES.INV, testInput, { alreadySelectedText: investigations.join(', ') })
      : { rows: [], total: 0, matched: 0 }
  ), [canUseTemplates, refData, testInput, investigations]);

  const blockTests = (
    <PadField label="Tests" tooltip={sectionTip(SECTION_INFO.tests)} isDark={isDark}>
        {/* ── Tests / Investigations ── */}
        {/* Lab module ON: the Tests row becomes the lab-advise surface (in-house
            orders + outside advice, with a manage panel). Off: the plain free-text strip. */}
        {labActive ? (
          <LabTestsField
            visitId={visitId}
            patientId={patientId}
            visit={visit}
            investigations={investigations}
            setInvestigations={setInvestigations}
            isDark={isDark}
            // FE-4 — the doctor's own remembered tests (`memory.types.INV`),
            // gated the SAME way `templates` is: absent (null) for a role/
            // visit-state that may not see memory chrome (D12), so
            // `LabListPicker` never even computes it for reception.
            memory={canUseTemplates ? refData?.memory : null}
            // FE-12 — forget a remembered test (G5); gated the SAME way as
            // `memory` above.
            onForgetMemory={canUseTemplates
              ? (row) => requestForgetMemoryRow(MEMORY_TYPES.INV, row) : null}
            finePointer={finePointer}
            // D25/F6 — the lab-test template group inside the picker. Hidden
            // (never disabled) for a role that may not use templates and on a
            // completed visit, exactly like every other template affordance.
            // The object is rebuilt each render but is never a hook dependency
            // downstream — `LabListPicker` memoises on `selection`, which is
            // this pad's own `useMemo`.
            templates={canUseTemplates ? {
              selection: templateSelection,
              narrow: narrowHost,
              onAdd: (t) => applyTemplates([t]),
              onSeeAll: (el, query) => handleOpenTemplateList(TEMPLATE_CATEGORIES.LAB_TESTS, el, query),
              // D41/S17 — the same shared rule, handed to the field that owns
              // the input: empty only, fine pointer only.
              onDoubleClickEmpty: openListOnDoubleClick(TEMPLATE_CATEGORIES.LAB_TESTS, null),
              finePointer,
              doubleClickHint: DOUBLE_CLICK_HINT,
              // FE-27 — so the field can take the doctor back when the list it
              // opened is closed (the list's own opener is gone by then).
              listOpen: templateListRequest?.category === TEMPLATE_CATEGORIES.LAB_TESTS,
              // FE-28 — a warning appeared on the pad: this panel closes too,
              // so it cannot cover it. Same rule for both fields.
              closeSignal: warningSeq,
            } : null}
          />
        ) : (
          <div data-tour="investigations" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
            {hasInv && (
              <>
                <SectionHeader icon="🧪" label="Tests:" tooltip={sectionTip(SECTION_INFO.tests)} isDark={isDark} />
                {investigations.map((inv, i) => (
                  editingInvIdx === i ? (
                    <input key={`edit-inv-${i}`} autoFocus type="text" defaultValue={inv}
                      onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== inv) setInvestigations(p => p.map((x, j) => j === i ? v : x)); else if (!v) setInvestigations(p => p.filter((_, j) => j !== i)); setEditingInvIdx(null); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } if (e.key === 'Escape') setEditingInvIdx(null); }}
                      style={{ padding: '0.3rem 0.6rem', borderRadius: '2rem', fontSize: type.invChip, fontWeight: 500, background: isDark ? 'rgba(245,158,11,0.1)' : '#fef3c7', color: isDark ? '#fbbf24' : '#92400e', border: `1.5px solid ${isDark ? '#fbbf24' : '#d97706'}`, fontFamily: 'system-ui, sans-serif', outline: 'none', minWidth: '4rem' }}
                    />
                  ) : (
                  <span key={inv} className="rx-chip-hover" style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0.3rem 0.6rem', borderRadius: '2rem',
                    fontSize: type.invChip, fontWeight: 500,
                    background: isDark ? 'rgba(245,158,11,0.1)' : '#fef3c7',
                    color: isDark ? '#fbbf24' : '#92400e',
                    border: `1px solid ${isDark ? 'rgba(245,158,11,0.18)' : 'rgba(245,158,11,0.12)'}`,
                    fontFamily: 'system-ui, sans-serif', cursor: 'pointer',
                  }} onClick={() => setEditingInvIdx(i)} title="Click to edit"
                  >
                    {inv}
                    <button onClick={(e) => { e.stopPropagation(); setInvestigations(p => p.filter((_, j) => j !== i)); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: type.invChip, padding: 0, lineHeight: 1, opacity: 0.7 }}>✕</button>
                  </span>
                  )
                ))}
              </>
            )}
            {/* FE-4 — lab OFF has no catalogue, but the doctor's own
                remembered tests still show as they type: zero `/suggest`
                calls, the ONE on-mount memory payload filtered client-side. */}
            <div style={{ position: 'relative' }}>
              <input
                ref={testInputRef}
                type="text"
                value={testInput}
                onChange={(e) => { setTestInput(e.target.value); setTestHighlight(-1); }}
                onFocus={() => setShowTestSuggestions(true)}
                // FIX-6 — `addTypedTest` closes this dropdown without
                // blurring; a click on the still-focused box is a real
                // re-focus.
                onMouseDown={reopenIfAlreadyFocused(testInputRef, () => setShowTestSuggestions(true))}
                onBlur={() => setTimeout(() => { setShowTestSuggestions(false); setTestHighlight(-1); }, 200)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { setTestInput(''); setShowTestSuggestions(false); setTestHighlight(-1); return; }
                  if (e.key === 'ArrowDown' && showTestSuggestions && testMemGroup.rows.length > 0) {
                    e.preventDefault();
                    setTestHighlight((h) => Math.min(h + 1, testMemGroup.rows.length - 1));
                    return;
                  }
                  if (e.key === 'ArrowUp' && showTestSuggestions && testMemGroup.rows.length > 0) {
                    e.preventDefault();
                    setTestHighlight((h) => Math.max(h - 1, -1));
                    return;
                  }
                  if (e.key === 'Enter') {
                    // Nothing highlighted still means "what I typed stands"
                    // (D25/F4's rule, mirrored here) — a highlighted memory
                    // row picks IT instead.
                    e.preventDefault();
                    const entry = testHighlight >= 0 ? testMemGroup.rows[testHighlight] : null;
                    addTypedTest(entry ? entry.v : undefined);
                    setTestHighlight(-1);
                  }
                }}
                placeholder={TEST_TYPE_PLACEHOLDER}
                className="rx-field"
                data-testid="test-input"
                style={{
                  padding: '0.3rem 0.6rem', borderRadius: '2rem', fontSize: type.invChip, minWidth: '8rem',
                  border: `1.5px dashed ${isDark ? 'rgba(245,158,11,0.4)' : 'rgba(217,119,6,0.35)'}`,
                  background: isDark ? 'rgba(245,158,11,0.08)' : '#fffbeb',
                  color: isDark ? '#fbbf24' : '#92400e', fontFamily: 'system-ui, sans-serif',
                }}
              />
              {showTestSuggestions && testMemGroup.rows.length > 0 && testPopoverRect && (
                <div data-testid="test-suggestions" style={{
                  position: 'fixed', ...dropdownRectWithMinWidth(testPopoverRect),
                  zIndex: 1000, background: isDark ? '#1f2937' : '#fff', border: `1px solid ${padBorder}`,
                  borderRadius: '0.375rem', maxHeight: narrowHost ? 'min(14rem, 38vh)' : 'min(20rem, 50vh)', overflowY: 'auto',
                  boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
                }}>
                  {testMemGroup.rows.map((r, i) => (
                    <MemorySuggestionRow
                      key={`test-mem-${i}`}
                      label={r.v}
                      groupHeading={i === 0 ? memoryGroupHeading(MEMORY_TYPES.INV, refData?.memory?.owner) : undefined}
                      countLine={i === 0 ? memoryCountLine(testMemGroup.total, testMemGroup.matched, testInput) : undefined}
                      startsGroup={i === 0}
                      highlighted={i === testHighlight}
                      isDark={isDark}
                      type={type}
                      ink={ink}
                      inkFaint={inkFaint}
                      onClick={() => addTypedTest(r.v)}
                      finePointer={finePointer}
                      forgetAriaLabel={MEMORY_FORGET_ARIA.replace('{name}', r.v)}
                      onForget={() => requestForgetMemoryRow(MEMORY_TYPES.INV, r)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
    </PadField>
  );

  // FE-6 — the Advised surgeries strip's own memory group (`memory.types.SURG`),
  // filtered by whatever is typed. `alreadySelectedText` keeps a surgery already
  // on the pad from being offered again — the same option Tests uses for its own
  // array (joined, since the option does a substring check).
  const surgeryMemGroup = useMemo(() => (
    canUseTemplates
      ? memoryFieldRows(refData?.memory, MEMORY_TYPES.SURG, surgeryInput, { alreadySelectedText: advisedSurgeries.join(', ') })
      : { rows: [], total: 0, matched: 0 }
  ), [canUseTemplates, refData, surgeryInput, advisedSurgeries]);

  // FE-8 — the `Surgery templates` group ABOVE the memory group (mockup
  // variant A, the same order Symptoms already ships), narrowed by the same
  // typed text. Picking one merges the whole set (D7 — never a replace); an
  // empty category → no group, no heading, exactly like every sibling field.
  const surgeryTemplateGroup = useMemo(() => (
    canUseTemplates
      ? fieldTemplateRows(templateSelection, TEMPLATE_CATEGORIES.SURGERIES, surgeryInput, { narrow: narrowHost })
      : { heading: '', rows: [], total: 0, seeAll: 0 }
  ), [canUseTemplates, templateSelection, surgeryInput, narrowHost]);

  // ONE flat sequence for the keyboard — templates first (bk-989: `See all n`
  // is the group's LAST entry), then every memory row, exactly the shape
  // `symptomGroups.flat` already established for the other array field.
  const surgeryFlat = useMemo(() => [
    ...surgeryTemplateGroup.rows.map((template) => ({ template })),
    ...(surgeryTemplateGroup.seeAll > 0 ? [{ seeAll: surgeryTemplateGroup.seeAll }] : []),
    ...surgeryMemGroup.rows.map((r) => ({ name: r.v })),
  ], [surgeryTemplateGroup, surgeryMemGroup]);

  const blockDiagnosis = (
    <PadField label="Diagnosis" tooltip={sectionTip(SECTION_INFO.diagnosis)} isDark={isDark}>
          <div style={{ position: 'relative' }}>
            {/* Quick-add DX pills */}
            {topDiagnoses.length > 0 && (
              <div style={{ display: 'flex', gap: '0.2rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.15rem' }}>
                {topDiagnoses.slice(0, 5).map((td, ti) => {
                  const label = td.name || td.raw_input || '';
                  const alreadyUsed = diagnosis.toLowerCase().includes(label.toLowerCase());
                  return (
                    <button key={`dxp-${ti}`} onClick={() => {
                      if (alreadyUsed || !label) return;
                      dxUsedRef.current.add(label.toLowerCase());
                      setDiagnosis(prev => {
                        const t = prev.trim();
                        return t ? `${t}, ${label}` : label;
                      });
                    }}
                      style={{
                        padding: '0.35rem 0.75rem', borderRadius: '2rem', fontSize: type.chip, fontWeight: 600,
                        border: `1px solid ${alreadyUsed ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)') : (isDark ? 'rgba(13,148,136,0.3)' : 'rgba(13,148,136,0.15)')}`,
                        background: alreadyUsed ? (isDark ? 'rgba(255,255,255,0.03)' : '#f9fafb') : tealBg,
                        color: alreadyUsed ? inkFaint : teal,
                        cursor: alreadyUsed ? 'default' : 'pointer', fontFamily: 'system-ui, sans-serif',
                        opacity: alreadyUsed ? 0.5 : 1,
                      }}
                      className={alreadyUsed ? '' : 'rx-chip-hover'}
                    title={label}
                    >{alreadyUsed ? '✓ ' : '+ '}{label}</button>
                  );
                })}
              </div>
            )}
            <input type="text" value={diagnosis}
              ref={dxInputRef}
              className="rx-field"
              onChange={(e) => { setDiagnosis(e.target.value); updateDxSuggestions(e.target.value); }}
              // FE-3 — D25's `onFocus` open-on-focus is RESTORED (never by
              // reverting the c54f197 hotfix): that hotfix's genuine root
              // cause was the outside-click handler's missing `dxInputRef`
              // exemption (fixed permanently, above) — dropping `onFocus` was
              // only a SAFE-FALLBACK caution, not the fix itself. The
              // measured-rect gate that same hotfix added (`dxPopoverRect &&`
              // below) stays exactly as-is: it is what makes restoring
              // `onFocus` safe (no paint at an unmeasured {top:0,left:0}).
              // D41's `onDoubleClick` list-opener stays pulled — out of
              // scope here. Every other field is unchanged.
              onFocus={() => updateDxSuggestions(diagnosis)}
              // FIX-6 — an Enter-picked suggestion closes this dropdown
              // without blurring; a click on the still-focused box is a
              // real re-focus.
              onMouseDown={reopenIfAlreadyFocused(dxInputRef, () => updateDxSuggestions(diagnosis))}
              onBlur={() => setTimeout(() => { setShowDxSuggestions(false); setDxHighlight(-1); }, 200)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { setShowDxSuggestions(false); setDxHighlight(-1); e.target.blur(); return; }
                if (!showDxSuggestions || dxSuggestions.length === 0) return;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setDxHighlight((h) => Math.min(h + 1, dxSuggestions.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setDxHighlight((h) => Math.max(h - 1, -1));
                } else if (e.key === 'Enter' && dxHighlight >= 0) {
                  // Enter with NOTHING highlighted is left alone — it keeps
                  // meaning "what I typed stands".
                  e.preventDefault();
                  applyDxSuggestion(dxSuggestions[dxHighlight]);
                }
              }}
              placeholder="e.g. Viral Fever, URTI..."
              style={fieldBox}
            />
            {/* HOTFIX (P1) — never paint at an unmeasured {top:0,left:0}: that
                is a real box, at the viewport's top-left, with real (auto)
                width the moment `dxPopoverRect` is still null — exactly the
                shape of "a stray box stuck at top-left" the owner reported.
                Wait for a real measurement instead of defaulting to 0. */}
            {showDxSuggestions && dxSuggestions.length > 0 && dxPopoverRect && (
              <div ref={dxSuggestionsRef} data-testid="dx-suggestions" style={{
                // Rect-anchored fixed, like the medicine popover (bk-973).
                position: 'fixed',
                ...dropdownRectWithMinWidth(dxPopoverRect),
                zIndex: 1000,
                background: isDark ? '#1f2937' : '#fff', border: `1px solid ${padBorder}`,
                borderRadius: '0.375rem',
                maxHeight: narrowHost ? 'min(14rem, 38vh)' : 'min(20rem, 50vh)',
                overflowY: 'auto',
                boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
              }}>
                {dxSuggestions.map((s, i) => (
                  s.source === 'template_see_all' ? (
                    <TemplateSeeAllRow
                      key={`dx-see-all-${s.category}`}
                      count={s.count}
                      category={s.category}
                      isDark={isDark}
                      highlighted={i === dxHighlight}
                      onClick={(e) => applyDxSuggestion(s, e.currentTarget)}
                    />
                  ) : s.source === 'template_set' ? (
                    <div key={`dx-set-${s.template.template_id}`}>
                      {s.startsGroup && <TemplateGroupHeading label={s.groupHeading} isDark={isDark} />}
                      <TemplateSuggestionRow
                        template={s.template}
                        category={TEMPLATE_CATEGORIES.DIAGNOSIS}
                        narrow={narrowHost}
                        highlighted={i === dxHighlight}
                        isDark={isDark}
                        onPick={() => applyDxSuggestion(s)}
                      />
                    </div>
                  ) : s.source === 'doctor_memory' ? (
                    // FE-3/FE-3B — the doctor's own remembered diagnoses,
                    // UNDER the template group (mockup variant A), via the
                    // ONE shared row component every field's memory group
                    // reuses. The dropdown IS the full list (owner ruling) —
                    // every row FE-1 ranked, no cap, no `See all`; the outer
                    // popover's own maxHeight/overflow (above) is the ONE
                    // scroll container, never taller than `min(20rem, 50vh)`.
                    <MemorySuggestionRow
                      key={`dx-mem-${i}`}
                      label={s.name}
                      groupHeading={s.groupHeading}
                      countLine={s.countLine}
                      startsGroup={s.startsGroup}
                      highlighted={i === dxHighlight}
                      isDark={isDark}
                      type={type}
                      ink={ink}
                      inkFaint={inkFaint}
                      onClick={() => applyDxSuggestion(s)}
                      finePointer={finePointer}
                      forgetAriaLabel={MEMORY_FORGET_ARIA.replace('{name}', s.name)}
                      onForget={() => requestForgetMemoryRow(MEMORY_TYPES.DX, s.row)}
                    />
                  ) : (
                  <button key={i} data-source={s.source || 'unspecified'} onClick={() => applyDxSuggestion(s)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.375rem', width: '100%',
                      padding: '0.4rem 0.625rem', border: 'none',
                      background: i === dxHighlight
                        ? (isDark ? 'rgba(13,148,136,0.16)' : '#f0fdfa')
                        : 'transparent',
                      cursor: 'pointer', fontSize: type.row,
                      color: ink, textAlign: 'left', fontFamily: 'system-ui, sans-serif',
                    }}
                    onMouseEnter={e => e.target.style.background = isDark ? 'rgba(13,148,136,0.1)' : 'rgba(13,148,136,0.04)'}
                    onMouseLeave={e => e.target.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: type.rowSub, color: inkFaint }}>{s.shorthand}</span>
                    <span style={{ flex: 1, fontWeight: 500 }}>{s.name}</span>
                  </button>
                  )
                ))}
              </div>
            )}
          </div>
    </PadField>
  );

  const blockSurgeries = (
    <PadField label={SURGERY_SECTION_LABEL} tooltip={sectionTip(SURGERY_SECTION_TOOLTIP)} isDark={isDark}>
        <div data-testid="advised-surgeries">
          {hasSurg && (
            <div
              data-testid="advised-surgeries-chips"
              style={{
                display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap',
                marginBottom: space.item,
                // ≥6 lines: the section scrolls INSIDE its own box instead of
                // growing until Follow-up is pushed off the screen. Below that
                // it is a plain wrapping chip row with no scrollbar.
                ...(advisedSurgeries.length >= SURGERY_SCROLL_AFTER
                  ? { maxHeight: SURGERY_SCROLL_MAX_HEIGHT, overflowY: 'auto', alignItems: 'flex-start' }
                  : null),
              }}
            >
              {advisedSurgeries.map((s, i) => (
                // FIX-1 (owner) — the mismatched lavender tint was bespoke;
                // S4 AC1 calls for the Tests chip/line pattern, so this is
                // the SAME amber tokens as the lab-OFF investigations chip
                // (no separate colour meaning invented for this section).
                <span key={s} className="rx-chip-hover" style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                  padding: '0.3rem 0.6rem', borderRadius: '2rem',
                  fontSize: type.invChip, fontWeight: 500,
                  background: isDark ? 'rgba(245,158,11,0.1)' : '#fef3c7',
                  color: isDark ? '#fbbf24' : '#92400e',
                  border: `1px solid ${isDark ? 'rgba(245,158,11,0.18)' : 'rgba(245,158,11,0.12)'}`,
                  fontFamily: 'system-ui, sans-serif',
                }}
                >
                  {s}
                  {/* Same affordance as a Tests chip's ✕ — a real button, so
                      Tab reaches it and Enter/Space activates it. */}
                  <button
                    onClick={() => removeSurgery(i)}
                    data-testid={`surgery-remove-${i}`}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: type.invChip, padding: 0, lineHeight: 1, opacity: 0.7 }}
                  >✕</button>
                </span>
              ))}
            </div>
          )}
          {/* Typing shows the doctor's OWN remembered surgeries — the ONE
              on-mount memory payload filtered client-side, zero requests per
              keystroke. FE-8 adds the `Surgery templates` group above it. */}
          <div style={{ position: 'relative' }}>
            <input
              ref={surgeryInputRef}
              type="text"
              value={surgeryInput}
              onChange={(e) => { setSurgeryInput(e.target.value); setSurgeryHighlight(-1); }}
              onFocus={() => setShowSurgerySuggestions(true)}
              // FIX-6 — `addSurgery` closes this dropdown without blurring;
              // a click on the still-focused box is a real re-focus.
              onMouseDown={reopenIfAlreadyFocused(surgeryInputRef, () => setShowSurgerySuggestions(true))}
              onBlur={() => setTimeout(() => { setShowSurgerySuggestions(false); setSurgeryHighlight(-1); }, 200)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { setShowSurgerySuggestions(false); setSurgeryHighlight(-1); return; }
                if (e.key === 'ArrowDown' && showSurgerySuggestions && surgeryFlat.length > 0) {
                  e.preventDefault();
                  setSurgeryHighlight((h) => Math.min(h + 1, surgeryFlat.length - 1));
                  return;
                }
                if (e.key === 'ArrowUp' && showSurgerySuggestions && surgeryFlat.length > 0) {
                  e.preventDefault();
                  setSurgeryHighlight((h) => Math.max(h - 1, -1));
                  return;
                }
                if (e.key === 'Enter') {
                  // Nothing highlighted still means "what I typed stands". A
                  // highlighted TEMPLATE row merges the whole set (D7 — never
                  // a replace); `See all n` opens the All-templates list.
                  e.preventDefault();
                  const entry = surgeryHighlight >= 0 ? surgeryFlat[surgeryHighlight] : null;
                  if (entry?.seeAll) {
                    handleOpenTemplateList(
                      TEMPLATE_CATEGORIES.SURGERIES, surgerySeeAllRef.current, surgeryInput.trim(),
                    );
                    setSurgeryHighlight(-1);
                  } else if (entry?.template) {
                    applyTemplates([entry.template]);
                    setSurgeryInput('');
                    setSurgeryHighlight(-1);
                  } else {
                    addSurgery(entry?.name);
                  }
                }
              }}
              // FIX-2 (owner) — D41's ONE double-click handler, reused exactly
              // as Symptoms/Medicines/Tests(lab-ON) wire it: empty input, fine
              // pointer only, opens the All-templates list pre-filtered to
              // this field's category and closes the on-focus dropdown. Esc
              // returning focus to this input and the keyboard `See all`
              // fallback are already the shared `TemplatePickerList`/`See all`
              // machinery — nothing new needed for those.
              onDoubleClick={openListOnDoubleClick(
                TEMPLATE_CATEGORIES.SURGERIES,
                () => { setShowSurgerySuggestions(false); setSurgeryHighlight(-1); },
              )}
              placeholder={SURGERY_PLACEHOLDER}
              className="rx-field"
              data-testid="surgery-input"
              // FIX-7 (owner) — the dashed chip-strip look is gone: this is
              // now `fieldBox`, the SAME solid rectangular input every other
              // primary control on the pad (Diagnosis, C/O, Medicines via
              // `medicineBox`, Tests's own LabListPicker search box) reads
              // as — same border token, radius, height and placeholder
              // colour; the teal focus ring comes free from the shared
              // `.rx-field:focus` rule every one of those already carries.
              // The chips beneath the input are UNCHANGED (still the Tests
              // chip tokens, FIX-1).
              style={fieldBox}
            />
            {/* FIX-2 (owner) — the ratified shortcut hint, shown only while
                empty + focused + fine pointer, the SAME gate the medicine
                box's own hint line uses. */}
            {showSurgerySuggestions && showDoubleClickHint(surgeryInput) && (
              <div style={{ fontSize: type.hint, color: inkFaint, fontFamily: 'system-ui, sans-serif', marginTop: '0.2rem' }}>
                <span data-testid="double-click-hint">{DOUBLE_CLICK_HINT}</span>
              </div>
            )}
            {showSurgerySuggestions && surgeryFlat.length > 0 && surgeryPopoverRect && (
              <div data-testid="surgery-suggestions" style={{
                position: 'fixed', ...dropdownRectWithMinWidth(surgeryPopoverRect),
                zIndex: 1000, background: isDark ? '#1f2937' : '#fff', border: `1px solid ${padBorder}`,
                borderRadius: '0.375rem', maxHeight: narrowHost ? 'min(14rem, 38vh)' : 'min(20rem, 50vh)', overflowY: 'auto',
                boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
              }}>
                {/* FE-8 — the doctor's own SURGERIES templates, the group's
                    FIRST entries (mockup variant A, same order Symptoms
                    ships). Picking one merges the whole set (D7 — never a
                    replace); an empty category renders no group at all. */}
                {surgeryTemplateGroup.rows.length > 0 && (
                  <div data-testid="surgery-group-templates">
                    <TemplateGroupHeading label={surgeryTemplateGroup.heading} isDark={isDark} />
                    {surgeryTemplateGroup.rows.map((t, i) => (
                      <TemplateSuggestionRow
                        key={t.template_id}
                        template={t}
                        category={TEMPLATE_CATEGORIES.SURGERIES}
                        narrow={narrowHost}
                        highlighted={i === surgeryHighlight}
                        isDark={isDark}
                        onPick={() => { applyTemplates([t]); setSurgeryInput(''); setSurgeryHighlight(-1); }}
                      />
                    ))}
                    {surgeryTemplateGroup.seeAll > 0 && (
                      <TemplateSeeAllRow
                        count={surgeryTemplateGroup.seeAll}
                        category={TEMPLATE_CATEGORIES.SURGERIES}
                        isDark={isDark}
                        highlighted={surgeryHighlight === surgeryTemplateGroup.rows.length}
                        rowRef={surgerySeeAllRef}
                        onClick={(e) => handleOpenTemplateList(
                          TEMPLATE_CATEGORIES.SURGERIES, e.currentTarget, surgeryInput.trim(),
                        )}
                      />
                    )}
                  </div>
                )}
                {surgeryMemGroup.rows.map((r, i) => {
                  // The keyboard position offset by however many template-group
                  // entries sit above this group in `surgeryFlat`.
                  const flatIdx = surgeryTemplateGroup.rows.length
                    + (surgeryTemplateGroup.seeAll > 0 ? 1 : 0) + i;
                  return (
                    <MemorySuggestionRow
                      key={`surg-mem-${i}`}
                      label={r.v}
                      groupHeading={i === 0 ? memoryGroupHeading(MEMORY_TYPES.SURG, refData?.memory?.owner) : undefined}
                      countLine={i === 0 ? memoryCountLine(surgeryMemGroup.total, surgeryMemGroup.matched, surgeryInput) : undefined}
                      startsGroup={i === 0}
                      highlighted={flatIdx === surgeryHighlight}
                      isDark={isDark}
                      type={type}
                      ink={ink}
                      inkFaint={inkFaint}
                      onClick={() => addSurgery(r.v)}
                      finePointer={finePointer}
                      forgetAriaLabel={MEMORY_FORGET_ARIA.replace('{name}', r.v)}
                      onForget={() => requestForgetMemoryRow(MEMORY_TYPES.SURG, r)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
    </PadField>
  );

  // RX-FORM-BUGS item 1 — quick-add Instructions pills, ranked count-desc →
  // time-desc via `memoryFieldRows` (rxMemory.js, the SAME selector the
  // dropdown above reads), no `top_instructions` legacy slice. Same D12
  // gate; updates in-session off `refData.memory`, no refetch.
  const instructionPills = useMemo(() => (
    canUseTemplates ? memoryFieldRows(refData?.memory, MEMORY_TYPES.INSTR, '', {}).rows.slice(0, 6) : []
  ), [canUseTemplates, refData]);

  const blockInstructions = (
    <PadField label="Instructions" tooltip={SECTION_INFO.instructions} isDark={isDark}>
          <div style={{ position: 'relative' }}>
            {/* Quick-add instruction pills */}
            {instructionPills.length > 0 && (
              <div style={{ display: 'flex', gap: '0.2rem', flexWrap: 'wrap', marginBottom: '0.15rem' }}>
                {instructionPills.map((ti, idx) => {
                  const fullText = ti.v;
                  const alreadyUsed = doctorNotes.toLowerCase().includes(fullText.toLowerCase());
                  return (
                    <button key={`ip-${idx}`} title={fullText} onClick={() => {
                      if (alreadyUsed || !fullText) return;
                      setDoctorNotes(prev => {
                        const t = prev.trim();
                        return t ? `${t}\n${fullText}` : fullText;
                      });
                    }}
                      style={{
                        padding: '0.35rem 0.75rem', borderRadius: '2rem', fontSize: type.chip, fontWeight: 600,
                        border: `1px solid ${alreadyUsed ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)') : (isDark ? 'rgba(245,158,11,0.3)' : 'rgba(245,158,11,0.15)')}`,
                        background: alreadyUsed ? (isDark ? 'rgba(255,255,255,0.03)' : '#f9fafb') : (isDark ? 'rgba(245,158,11,0.1)' : '#fffbeb'),
                        color: alreadyUsed ? inkFaint : (isDark ? '#fbbf24' : '#92400e'),
                        cursor: alreadyUsed ? 'default' : 'pointer', fontFamily: 'system-ui, sans-serif',
                        opacity: alreadyUsed ? 0.5 : 1,
                      }}
                      className={alreadyUsed ? '' : 'rx-chip-hover'}
                    >{alreadyUsed ? '✓ ' : '+ '}{fullText}</button>
                  );
                })}
              </div>
            )}
            <textarea value={doctorNotes}
              className="rx-field"
              onChange={(e) => { setDoctorNotes(e.target.value); updateInstructionSuggestions(e.target.value); }}
              onFocus={(e) => { if (e.target.rows < 3) e.target.rows = 3; if (doctorNotes.trim().length >= 2) updateInstructionSuggestions(doctorNotes); }}
              onBlur={(e) => { if (!e.target.value.trim()) e.target.rows = 1; setTimeout(() => setShowInstructionSugg(false), 200); }}
              onKeyDown={(e) => { if (e.key === 'Escape') setShowInstructionSugg(false); }}
              placeholder="One instruction per line..."
              rows={1}
              style={{ ...fieldBox, resize: 'vertical' }}
            />
            {/* Instruction autocomplete dropdown */}
            {showInstructionSugg && instructionSuggestions.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                background: isDark ? '#1f2937' : '#fff', border: `1px solid ${padBorder}`,
                borderRadius: '0 0 0.375rem 0.375rem', maxHeight: '8rem', overflowY: 'auto',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              }}>
                {instructionSuggestions.map((ins, i) => (
                  <button key={i} onClick={() => {
                    // Replace the current (last) line with the suggestion
                    setDoctorNotes(prev => {
                      const lines = prev.split('\n');
                      lines[lines.length - 1] = ins;
                      return lines.join('\n');
                    });
                    setShowInstructionSugg(false);
                  }}
                    style={{
                      display: 'block', width: '100%', padding: '0.4rem 0.625rem', border: 'none',
                      background: 'transparent', cursor: 'pointer', fontSize: type.row,
                      color: ink, textAlign: 'left', fontFamily: 'system-ui, sans-serif',
                    }}
                    onMouseEnter={e => e.target.style.background = isDark ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.04)'}
                    onMouseLeave={e => e.target.style.background = 'transparent'}
                  >
                    <span style={{ fontWeight: 500 }}>{ins}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
    </PadField>
  );

  const blockFollowUp = (
    <>
        {/* ── Follow-up presets ── */}
        {hasAny && (
          <PadField label="Follow-up" tooltip={SECTION_INFO.followUp} isDark={isDark} dataTour="follow-up">
            <FollowUpPicker
              required={followUpRequired}
              date={followUpDate}
              onChange={(req, d) => { setFollowUpRequired(req); setFollowUpDate(d); }}
              isDark={isDark}
              size="comfortable"
            />
          </PadField>
        )}
    </>
  );

  return (
    <div ref={padRef} style={{
      background: pad,
      borderRadius: '0.625rem',
      border: `1px solid ${padBorder}`,
      fontFamily: "'Georgia', 'Times New Roman', serif",
      position: 'relative',
      overflow: 'hidden',
    }}>
      <PrescriptionHoverStyles />
      {/* Placeholder color fix for vitals inputs + the pad's field boxes */}
      <style>{`
        .rx-vital-input::placeholder { color: ${isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.25)'} !important; font-weight: 400; font-style: italic; }
        /* RV-7 (owner, real-browser click-through) — the native number-input
           spinner arrows were eating into an already-too-narrow box, so
           "98.6" rendered as "3.". Hidden in both engines; the box itself
           now gets an explicit ch-based width per vital (vitalInputStyle
           below) instead of relying on flex to size it. */
        .rx-vital-input::-webkit-outer-spin-button,
        .rx-vital-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        @keyframes rx-spin { to { transform: rotate(360deg); } }
        /* Every section's PRIMARY control reads as an input box: solid fill,
           visible border, a teal focus ring and a plain (never italic)
           placeholder in readable ink — doctors were missing where to type. */
        .rx-field::placeholder {
          color: ${isDark ? '#94a3b8' : '#64748b'} !important;
          font-style: normal !important; opacity: 1 !important;
        }
        .rx-field:focus {
          border-color: #0d9488 !important;
          box-shadow: 0 0 0 2px rgba(13,148,136,0.28) !important;
        }
        /* P8 — the printed prescription never reflows. */
        @media print { .rx-pad-columns { display: block !important; } }
      `}</style>

      {/* ── In-app confirmation dialog ── */}
      {dialog && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 50,
          background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(2px)', borderRadius: '0.625rem',
        }}>
          <div style={{
            background: isDark ? '#1e293b' : '#fff', borderRadius: '0.75rem',
            padding: '1.25rem 1.5rem', maxWidth: '20rem', width: '85%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}>
            <div style={{ fontWeight: 700, fontSize: '0.875rem', color: isDark ? '#f1f5f9' : '#1e293b', marginBottom: '0.375rem' }}>
              {dialog.title}
            </div>
            <div style={{ fontSize: '0.8125rem', color: isDark ? '#94a3b8' : '#64748b', lineHeight: 1.5, marginBottom: '1rem' }}>
              {dialog.message}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={dialog.onCancel}
                style={{
                  padding: '0.375rem 0.875rem', borderRadius: '0.375rem', fontSize: '0.8125rem', fontWeight: 600,
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  color: isDark ? '#94a3b8' : '#64748b', cursor: 'pointer',
                }}>{dialog.cancelLabel || 'Cancel'}</button>
              <button onClick={dialog.onConfirm}
                style={{
                  padding: '0.375rem 0.875rem', borderRadius: '0.375rem', fontSize: '0.8125rem', fontWeight: 600,
                  border: 'none', cursor: 'pointer',
                  background: dialog.confirmDanger ? '#ef4444' : (isDark ? '#0d9488' : '#0d9488'),
                  color: '#fff',
                }}>{dialog.confirmLabel || 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}

      {/* FE-12 — forget a remembered entry: the SHARED `ConfirmationModal`
          (never a native `window.confirm`), initial focus on "Keep" (D16's
          own destructive-confirm pattern, `autoFocusCancel`). */}
      <ConfirmationModal
        isOpen={!!memoryForgetConfirm}
        title={MEMORY_FORGET_TITLE}
        message={memoryForgetConfirm ? MEMORY_FORGET_BODY.replace('{name}', memoryForgetConfirm.row.v) : ''}
        ctaLabel={MEMORY_FORGET_CONFIRM}
        cancelLabel={MEMORY_FORGET_KEEP}
        variant="danger"
        icon="🗑"
        autoFocusCancel
        onConfirm={confirmForgetMemoryRow}
        onCancel={cancelForgetMemoryRow}
      />

      {/* r5 — the C/O derived-value "×": informational variant of the SAME
          `ConfirmationModal`, no `onCancel` (single "Got it" dismiss, no
          destructive action, no DELETE call). `{lines}` — one quoted saved
          line per own line, `coDerivedInfo.lines` already in rank order. */}
      <ConfirmationModal
        isOpen={!!coDerivedInfo}
        title={CO_DERIVED_INFO_TITLE}
        message={coDerivedInfo
          ? CO_DERIVED_INFO_BODY
            .replace('{lines}', coDerivedInfo.lines.map((line) => `"${line}"`).join('\n'))
            .replace(/{name}/g, coDerivedInfo.name)
          : ''}
        ctaLabel={CO_DERIVED_INFO_DISMISS}
        icon="ℹ️"
        onConfirm={dismissCoDerivedInfo}
      />

      {/* FE-12 — ONE toast for the forget action, carrying Undo while it is
          up (10s, matching the pad's existing template-add toast). */}
      {memoryForgetToast && (
        <div style={{
          margin: '0 0.75rem', padding: '0.375rem 0.625rem', borderRadius: '0.375rem',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          background: isDark ? 'rgba(239,68,68,0.14)' : '#fef2f2',
          border: `1px solid ${isDark ? 'rgba(248,113,113,0.4)' : 'rgba(239,68,68,0.3)'}`,
          fontSize: type.banner, color: isDark ? '#fca5a5' : '#b91c1c',
          fontFamily: 'system-ui, sans-serif',
        }} role="status">
          <span style={{ flex: 1 }}>{memoryForgetToast.message}</span>
          <button type="button" onClick={memoryForgetToast.onUndo}
            style={{
              padding: '0.2rem 0.6rem', borderRadius: '0.25rem', cursor: 'pointer',
              border: `1px solid ${isDark ? 'rgba(248,113,113,0.5)' : 'rgba(239,68,68,0.4)'}`,
              background: isDark ? '#1e293b' : '#ffffff',
              color: isDark ? '#fca5a5' : '#b91c1c',
              fontSize: type.banner, fontWeight: 700, fontFamily: 'system-ui, sans-serif',
            }}>Undo</button>
        </div>
      )}

      {/* Draft restored toast */}
      {draftRestored && (
        <div style={{ padding: '0.25rem 0.75rem', background: isDark ? 'rgba(59,130,246,0.12)' : '#eff6ff', fontSize: type.banner, color: '#3b82f6', textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
          📋 Unsaved work restored
        </div>
      )}

      {/* ─── Nurse vitals banner ─── */}
      {nurseVitalsSource && (
        <div style={{
          padding: '0.25rem 0.75rem',
          background: isDark ? 'rgba(16,185,129,0.12)' : '#ecfdf5',
          fontSize: type.banner,
          color: isDark ? '#6ee7b7' : '#059669',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontFamily: 'system-ui, sans-serif',
          borderBottom: `1px solid ${isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.1)'}`,
        }}>
          <span>🌡️ Vitals pre-filled by {nurseVitalsSource.name}{nurseVitalsSource.timestamp ? ` · ${new Date(nurseVitalsSource.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''} — you can override any value</span>
          <button
            onClick={() => {
              setVitalValues({});
              setCustomVitals([]);
              setActiveVitalKeys([...DEFAULT_VITAL_KEYS]);
              setNurseVitalsSource(null);
              doctorModifiedVitalsRef.current = true;
            }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: type.hint, color: isDark ? '#6ee7b7' : '#059669', textDecoration: 'underline', padding: 0 }}
          >
            Clear
          </button>
        </div>
      )}

      {/* ─── TOP BAR: the Templates strip + Repeat Last Rx ─── */}
      <div style={{
        padding: '0.5rem 0.75rem',
        borderBottom: `1px solid ${lineColor}`,
        display: 'flex', flexDirection: 'column', gap: space.item,
      }}>

        {/* Smart actions bar */}
        <div data-tour="templates" style={{ display: 'flex', gap: '0.375rem', alignItems: 'center', flexWrap: 'wrap', fontFamily: 'system-ui, sans-serif' }}>
          {/* THE templates home (D20, variant C) — one labelled strip under the
              vitals line, tabbed by category. A chip click ADDS (merge + Undo),
              never replaces: Replace lives inside the list. This REPLACES the
              five per-section chip groups (D14 reversed); the per-field path is
              the D10 typing suggestions, untouched. */}
          {canUseTemplates && !templatesLoading && (
            <TemplateStrip
              selection={templateSelection}
              // D2 (QA r1) — a FAILED fetch has its own state on the strip
              // itself: the ratified line + `Try again`, never the first-use
              // sentence. The list is unreachable in that state (its opener
              // needs a count), so passing the error only to the list left the
              // doctor with no way back.
              error={templatesError}
              onRetry={loadTemplates}
              openCategory={templateListRequest?.category ?? null}
              onAdd={(t) => applyTemplates([t])}
              onOpenAll={handleOpenTemplateList}
              onSave={openTemplateSaveForm}
              saveAffordanceFor={(category) => templateSaveAffordance(category, padSnapshot)}
              isDark={isDark}
            />
          )}

          {/* Repeat Last Rx — load previous digital prescription. R1-FE:
              honest summary (never "0 meds" / a bare "()") and the confirm
              dialog ALWAYS fires — loading another visit's prescription is a
              clinical act, not a convenience, empty pad or not. */}
          {prevRx && prevRxSummary && prevRxSummary.parts.length > 0 && (
            <button
              onClick={() => {
                const summary = prevRxSummary.fullSummary;
                const date = prevRxSummary.date;
                setDialog({
                  title: REPEAT_RX_TITLE,
                  message: repeatRxWouldOverwrite
                    ? REPEAT_RX_CONFIRM_FILLED({ date, summary })
                    : REPEAT_RX_CONFIRM_EMPTY({ date, summary }),
                  confirmLabel: 'Load',
                  confirmDanger: true,
                  cancelLabel: 'Cancel',
                  onConfirm: () => { handleApplyPrevRx(); setDialog(null); },
                  onCancel: () => setDialog(null),
                });
              }}
              title={REPEAT_RX_TOOLTIP({ date: prevRxSummary.date, summary: prevRxSummary.fullSummary })}
              style={{
                padding: '0.35rem 0.75rem', borderRadius: '2rem',
                border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : 'rgba(99,102,241,0.22)'}`,
                background: isDark ? 'rgba(99,102,241,0.14)' : '#eef2ff',
                fontSize: type.chip, color: isDark ? '#a5b4fc' : '#6366f1',
                cursor: 'pointer', fontWeight: 600, fontFamily: 'system-ui, sans-serif',
              }}
            >{REPEAT_RX_CHIP({ date: prevRxSummary.date, summary: prevRxSummary.chipSummary })}</button>
          )}
          {prevRxLoading && (
            <span style={{ fontSize: type.hint, color: inkFaint }}>Loading previous Rx…</span>
          )}
        </div>
      </div>

      {/* All-templates list — one component, opened pre-filtered from any row's
          "See all n". Portalled, so the pad's overflow:hidden and the narrow
          Visit Actions accordion never clip it. */}
      {canUseTemplates && templateListRequest && (
        <TemplatePickerList
          templates={savedTemplates}
          loading={templatesLoading}
          error={templatesError}
          onRetry={loadTemplates}
          initialCategory={templateListRequest.category}
          initialQuery={templateListRequest.query}
          anchorRect={templateListRequest.rect}
          anchorEl={templateListRequest.el}
          sort={templateSort}
          onSortChange={changeTemplateSort}
          onAdd={applyTemplates}
          onReplace={replaceWithTemplates}
          onNewTemplate={() => openTemplateSaveForm(templateListRequest.category)}
          onEdit={(t) => { setTemplateListRequest(null); setEditingTemplate(t); setShowTemplateSaveForm(true); }}
          onDelete={deleteTemplate}
          onClose={() => setTemplateListRequest(null)}
          isDark={isDark}
        />
      )}

      {/* "+ New template" — the same save form the Rx Templates card uses,
          prefilled from this pad. The saved row is merged into list state, so
          the chips and the list update at once with NO refetch. */}
      {canUseTemplates && showTemplateSaveForm && (
        <TemplateSaveForm
          template={editingTemplate}
          pad={padSnapshot}
          initialCategory={saveFormCategory}
          categoryHint={saveFormCategory ? TEMPLATE_ROW_COPY.categoryPreselectedHint(saveFormCategory) : null}
          existingTemplates={savedTemplates}
          onSaved={(saved, mode) => {
            // The edited/created row lands in list state directly — chips, the
            // list and the field suggestions all re-derive from it, no refetch.
            setSavedTemplates((prev) => [saved, ...prev.filter((t) => t.template_id !== saved.template_id)]);
            showTemplateToast(
              mode === 'new' ? `Saved as "${saved.name}".`
                : mode === 'edit' ? `Updated "${saved.name}".`
                  : `Replaced "${saved.name}".`,
              false,
            );
          }}
          onClose={() => { setShowTemplateSaveForm(false); setEditingTemplate(null); }}
          isDark={isDark}
        />
      )}

      {/* ONE toast per template Add, carrying Undo while it is up (D7). */}
      {templateToast && (
        <div style={{
          margin: '0 0.75rem', padding: '0.375rem 0.625rem', borderRadius: '0.375rem',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          background: isDark ? 'rgba(13,148,136,0.18)' : '#f0fdfa',
          border: `1px solid ${isDark ? 'rgba(45,212,191,0.45)' : 'rgba(13,148,136,0.35)'}`,
          fontSize: type.banner, color: isDark ? '#5eead4' : '#0f766e',
          fontFamily: 'system-ui, sans-serif',
        }} role="status">
          <span style={{ flex: 1 }}>{templateToast.message}</span>
          {templateToast.canUndo && (
            <button type="button" onClick={undoTemplateAdd}
              style={{
                padding: '0.2rem 0.6rem', borderRadius: '0.25rem', cursor: 'pointer',
                border: `1px solid ${isDark ? 'rgba(45,212,191,0.5)' : 'rgba(13,148,136,0.45)'}`,
                background: isDark ? '#1e293b' : '#ffffff',
                color: isDark ? '#5eead4' : '#0f766e',
                fontSize: type.banner, fontWeight: 700, fontFamily: 'system-ui, sans-serif',
              }}>Undo</button>
          )}
        </div>
      )}

      {/* ─── BODY ─── */}
      <div style={{ padding: '0.5rem 0.75rem', display: 'flex', flexDirection: 'column', gap: space.section }}>

        {/* P7 — two columns on a wide host: LEFT is the clinical narrative that
            grows (C/O · Symptoms · Medicines · Diagnosis), RIGHT the short fixed
            blocks that used to hide below a long medicine list (Vitals · Tests ·
            Instructions · Follow-up). Below the threshold, and on a read-only /
            printed prescription (P8), it is one column in today's order. Each
            block is defined ONCE above and only placed differently. */}
        {twoColumn ? (
          <div data-testid="rx-pad-columns" className="rx-pad-columns"
            style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem' }}>
            <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: space.section }}>
              {blockChiefComplaint}
              {blockAllergies}
              {blockSymptoms}
              {blockMedicines}
              {blockDiagnosis}
              {/* FE-6 (Q13) — Advised surgeries sits after Tests and before
                  Instructions in BOTH layouts, and stays on the SAME side as
                  the clinical chain it follows (C/O → Symptoms → Medicines →
                  Diagnosis → Surgeries), so reflowing at the 1024px breakpoint
                  never makes the field jump columns. */}
              {blockSurgeries}
            </div>
            <div style={{ flex: '0 1 24rem', minWidth: 0, display: 'flex', flexDirection: 'column', gap: space.section }}>
              {blockVitals}
              {blockTests}
              {blockInstructions}
              {blockFollowUp}
            </div>
          </div>
        ) : (
          <>
            {blockVitals}
            {blockChiefComplaint}
            {blockAllergies}
            {blockSymptoms}
            {blockMedicines}
            {blockTests}
            {blockDiagnosis}
            {blockSurgeries}
            {blockInstructions}
            {blockFollowUp}
          </>
        )}

        {/* ── Error / Success ── */}
        {error && (
          <div style={{ padding: '0.3rem 0.5rem', borderRadius: '0.25rem', background: isDark ? 'rgba(239,68,68,0.12)' : '#fee2e2', fontSize: type.banner, color: '#ef4444', fontFamily: 'system-ui, sans-serif' }}>
            {error}
          </div>
        )}
        {successMsg && (
          <div style={{ padding: '0.3rem 0.5rem', borderRadius: '0.25rem', background: isDark ? 'rgba(16,185,129,0.12)' : '#ecfdf5', fontSize: type.banner, color: '#10b981', fontFamily: 'system-ui, sans-serif' }}>
            ✓ {successMsg}
          </div>
        )}

        {/* ── Image Attachments (Custom Theme) ── */}
        {themeSupportsImages && hasRxContent && (
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDraggingImages(true); }}
            onDragLeave={() => setIsDraggingImages(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDraggingImages(false);
              const droppedFiles = takeValidImages(Array.from(e.dataTransfer.files || []));
              if (droppedFiles.length === 0) return;
              if (droppedFiles.length + existingImages.length + attachedImageFiles.length > 4) {
                setError('Maximum 4 images allowed per prescription');
                return;
              }
              setAttachedImageFiles(prev => [...prev, ...droppedFiles].slice(0, 4 - existingImages.length));
            }}
            style={{
              // D22 — the pad's language: a quiet SOLID surface, not a dashed
              // box. Dashed is the DRAG-OVER state only.
              padding: '0.5rem 0.6rem',
              borderRadius: '0.375rem',
              border: isDraggingImages
                ? `1.5px dashed ${isDark ? '#5eead4' : '#0d9488'}`
                : `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.10)'}`,
              backgroundColor: isDraggingImages
                ? (isDark ? 'rgba(13,148,136,0.18)' : '#f0fdfa')
                : (isDark ? '#1a2333' : '#f8fafc'),
              transition: 'all 0.15s ease',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              {/* Same label treatment as every PadField section — uppercase
                  muted token in the 7rem gutter, no emoji, no serif — with the
                  count as a muted secondary. Reads `Images (0 of 4)`. */}
              {/* FE-33 — this section used to roll its own label; it goes
                  through the shared SectionHeader like every other one, so the
                  bold treatment cannot miss it. The count stays the muted
                  secondary, and the concatenated text is still the ratified
                  `Images (0 of 4)`. */}
              <span style={{ minWidth: SECTION_LABEL_GUTTER, flexShrink: 0 }}>
                <SectionHeader
                  isDark={isDark}
                  style={{ fontSize: type.section }}
                  label={(
                    <>
                      {IMAGES_SECTION_COPY.labelWord}{' '}
                      <span style={{ marginLeft: '0.3rem', fontWeight: 500, textTransform: 'none', color: isDark ? '#94a3b8' : '#64748b' }}>
                        {IMAGES_SECTION_COPY.labelCount(existingImages.length + attachedImageFiles.length, 4)}
                      </span>
                    </>
                  )}
                />
              </span>
              {existingImages.length + attachedImageFiles.length >= 4 ? (
                <span style={{ fontSize: type.hint, color: isDark ? '#94a3b8' : '#64748b', fontFamily: 'system-ui, sans-serif' }}>
                  {IMAGES_SECTION_COPY.maxReason(4)}
                </span>
              ) : (
                <label
                  data-testid="rx-add-image"
                  title={IMAGES_SECTION_COPY.addButtonTitle}
                  style={{
                    // A real outlined button (button-visibility rule): solid in
                    // BOTH modes, mid-tone border, teal text, `Save…`'s height.
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
                    padding: '0.2rem 0.55rem', borderRadius: '0.375rem',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.22)' : '#cbd5e1'}`,
                    background: isDark ? '#0f172a' : '#ffffff',
                    color: isDark ? '#5eead4' : '#0d9488',
                    fontSize: type.chip, fontWeight: 600, fontFamily: 'system-ui, sans-serif',
                  }}
                >
                  {IMAGES_SECTION_COPY.addButton}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const files = takeValidImages(Array.from(e.target.files || []));
                      if (files.length + existingImages.length + attachedImageFiles.length > 4) {
                        setError('Maximum 4 images allowed per prescription');
                        return;
                      }
                      setAttachedImageFiles(prev => [...prev, ...files].slice(0, 4 - existingImages.length));
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
            </div>
            {existingImages.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', marginTop: '0.375rem' }}>
                <span style={{ fontSize: type.hint, color: isDark ? '#94a3b8' : '#64748b', fontFamily: 'system-ui, sans-serif' }}>
                  Already saved — carried forward automatically:
                </span>
                <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                  {existingImages.map((img) => {
                    const url = existingImageUrls[img.meta_id];
                    return (
                      <div key={img.meta_id} style={{ display: 'flex', flexDirection: 'column', minWidth: '48px' }}>
                       <div style={{ position: 'relative', width: '48px' }}>
                        <a
                          href={url || undefined}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: 'block', boxSizing: 'border-box', width: '48px', height: '36px', borderRadius: '4px',
                            overflow: 'hidden',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.22)' : '#cbd5e1'}`,
                            background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                            cursor: url ? 'pointer' : 'default',
                          }}
                        >
                          {url && (
                            <img src={url} alt={img.file_name || 'Attached image'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          )}
                        </a>
                        <button
                          type="button"
                          title="Remove image (applies when you save)"
                          onClick={() => handleRemoveExistingImage(img.meta_id)}
                          style={{ position: 'absolute', top: '1px', right: '1px', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: '18px', height: '18px', fontSize: type.badge, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          ✕
                        </button>
                       </div>
                        <label
                          data-testid="rx-replace-saved-image"
                          title={IMAGES_SECTION_COPY.replaceTitle}
                          style={{
                            cursor: 'pointer', display: 'block', marginTop: '2px', textAlign: 'center', whiteSpace: 'nowrap',
                            boxSizing: 'border-box', padding: 0, overflow: 'hidden', borderRadius: '0.25rem',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.22)' : '#cbd5e1'}`,
                            background: isDark ? '#0f172a' : '#ffffff',
                            color: isDark ? '#5eead4' : '#0d9488',
                            fontSize: type.badge, fontWeight: 600, fontFamily: 'system-ui, sans-serif',
                          }}
                        >
                          {IMAGES_SECTION_COPY.replaceButton}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            data-testid="rx-replace-saved-image-input"
                            style={{ display: 'none' }}
                            onChange={(e) => { handleReplaceExistingImage(img.meta_id, e.target.files?.[0]); e.target.value = ''; }}
                          />
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {attachedImageFiles.length > 0 && (
              <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.375rem', flexWrap: 'wrap' }}>
                {attachedImageFiles.map((file, idx) => (
                  <div key={idx} style={{ display: 'flex', flexDirection: 'column', minWidth: '48px' }}>
                   <div style={{ position: 'relative', width: '48px' }}>
                    <div style={{ boxSizing: 'border-box', width: '48px', height: '36px', borderRadius: '4px', overflow: 'hidden', border: `1px solid ${isDark ? 'rgba(255,255,255,0.22)' : '#cbd5e1'}` }}>
                      <img src={URL.createObjectURL(file)} alt="Thumb" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                    <button
                      type="button"
                      onClick={() => setAttachedImageFiles(prev => prev.filter((_, i) => i !== idx))}
                      style={{ position: 'absolute', top: '1px', right: '1px', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: '18px', height: '18px', fontSize: type.badge, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      ✕
                    </button>
                   </div>
                        <label
                          data-testid="rx-replace-attached-image"
                          title={IMAGES_SECTION_COPY.replaceTitle}
                          style={{
                            cursor: 'pointer', display: 'block', marginTop: '2px', textAlign: 'center', whiteSpace: 'nowrap',
                            boxSizing: 'border-box', padding: 0, overflow: 'hidden', borderRadius: '0.25rem',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.22)' : '#cbd5e1'}`,
                            background: isDark ? '#0f172a' : '#ffffff',
                            color: isDark ? '#5eead4' : '#0d9488',
                            fontSize: type.badge, fontWeight: 600, fontFamily: 'system-ui, sans-serif',
                          }}
                        >
                          {IMAGES_SECTION_COPY.replaceButton}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            data-testid="rx-replace-attached-image-input"
                            style={{ display: 'none' }}
                            onChange={(e) => { handleReplaceAttachedImage(idx, e.target.files?.[0]); e.target.value = ''; }}
                          />
                        </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Save Button ── */}
        {hasAny && (
          <button data-tour="save-btn" onClick={handleSave} disabled={saving}
            title="Ctrl+Enter"
            style={{
              width: '100%', padding: '0.5rem',
              borderRadius: '0.375rem', border: 'none',
              background: `linear-gradient(135deg, ${teal}, #115e59)`,
              color: '#fff', fontSize: type.save, fontWeight: 600,
              cursor: saving ? 'wait' : 'pointer', fontFamily: 'system-ui, sans-serif',
              letterSpacing: '0.02em', transition: 'opacity 0.15s',
              opacity: saving ? 0.85 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
            }}
          >
            {saving && !practiceMode && (
              <span style={{ display: 'inline-block', width: '0.875rem', height: '0.875rem', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'rx-spin 0.7s linear infinite', flexShrink: 0 }} />
            )}
            {saving ? saveLabel : `🖊 ${saveLabel}`}
          </button>
        )}
      </div>

      {/* ─── FOOTER: Secondary Actions ─── */}
      <div style={{
        padding: '0.375rem 0.75rem',
        borderTop: `1px solid ${lineColor}`,
        display: 'flex', justifyContent: 'center', gap: '1.5rem',
        fontFamily: 'system-ui, sans-serif',
      }}>
        {/* Doctors get the Rx pad only — no prescription-file upload (owner 2026-10-09) */}
        {String(role || '').toUpperCase() !== STAFF_ROLES.DOCTOR && (
          <button onClick={() => setShowUpload(showUpload === 'prescription' ? null : 'prescription')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: type.btn, color: inkLight, fontWeight: 500 }}>
            📤 {showUpload === 'prescription' ? 'Hide' : 'Upload file'}
          </button>
        )}
        <button onClick={() => setShowUpload(showUpload === 'report' ? null : 'report')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: type.btn, color: inkLight, fontWeight: 500 }}>
          📄 {showUpload === 'report' ? 'Hide' : 'Attach report'}
        </button>
      </div>

      {showUpload && (
        <div style={{ borderTop: `1px solid ${lineColor}` }}>
          <AddPrescription
            visitId={visitId}
            metaType={showUpload === 'report' ? 'REPORT' : 'PRESCRIPTION'}
            onSuccess={() => { setShowUpload(null); onSuccess?.(); }}
            onClose={() => setShowUpload(null)}
          />
        </div>
      )}
    </div>
  );
}
